import { useState, useMemo, useCallback, useEffect, useRef } from "react";

// ── System Settings defaults ──
const DEFAULT_SETTINGS = {
  companyName: "BG Group",
  currency: "AED",
  currencySymbol: "AED",
  maxAmountNoApproval: 50000,
  enableEngineers: true,
  enablePV: true,
  enableUrgent: true,
  stuckDaysThreshold: 7,
  language: "en",
  fiscalYearStart: "01",
};

const DEFAULT_STAGES = [
  { id:"received",   label:"Received by DC",    color:"#0b7a87", ownerRole:"dc",        requiresPV:false, order:0 },
  { id:"auditor",    label:"Internal Auditor",   color:"#e8a020", ownerRole:"auditor",   requiresPV:false, order:1 },
  { id:"accountant", label:"Accountant",         color:"#4a9eff", ownerRole:"accountant",requiresPV:false, order:2 },
  { id:"dc_return",  label:"Returned to DC",     color:"#a070f0", ownerRole:"dc",        requiresPV:true,  order:3 },
  { id:"pm",         label:"Operation Manager",  color:"#30d98a", ownerRole:"om",        requiresPV:false, order:4 },
  { id:"cfo",        label:"CFO Signature",      color:"#ff6060", ownerRole:"cfo",       requiresPV:false, order:5 },
  { id:"auditor2",   label:"Internal Auditor (2nd)", color:"#e8a020", ownerRole:"auditor",   requiresPV:false, order:6 },
  { id:"ready_paid", label:"Ready to be Paid",   color:"#f0a030", ownerRole:"treasury",  requiresPV:false, order:7 },
  { id:"paid",       label:"Paid",               color:"#0b7a87", ownerRole:"treasury",  requiresPV:false, order:8 },
  { id:"completed",  label:"Filed",              color:"#20c070", ownerRole:"dc",        requiresPV:false, order:9 },
];

const ALL_ROLES = ["admin","dc","pd","auditor","accountant","om","cfo","treasury","engineer"];
const ROLE_LABELS = { admin:"Administrator", dc:"Document Controller", auditor:"Internal Auditor", accountant:"Accountant", om:"Operations Manager", cfo:"CFO", treasury:"Treasury", engineer:"Site Engineer", pd:"PD Approver" };
const ROLE_COLORS = { admin:"#ff4444", dc:"#0b7a87", auditor:"#e8a020", accountant:"#4a9eff", om:"#30d98a", cfo:"#ff6060", treasury:"#f0a030", engineer:"#a070f0", pd:"#7c5cbf" };

const DEFAULT_USERS = []; // Users loaded from backend API

const DEFAULT_PROJECTS = []; // Projects loaded from DB via admin panel

const PAYMENT_METHODS = [{id:"cash",label:"Cash"},{id:"bank_transfer",label:"Bank Transfer"},{id:"credit_card",label:"Credit Card"},{id:"check",label:"Check"}];
const PAYMENT_TERMS   = [{id:"advance",label:"Advance"},{id:"cash",label:"Cash"},{id:"after_received",label:"Progress Payment"},{id:"pis",label:"PIS"}];
const CONFETTI_COLORS = ["#0b7a87","#20c070","#f0a030","#ff6060","#a070f0","#30d98a","#4a9eff"];

const todayStr = () => new Date().toISOString().slice(0,10);
const daysDiff = (a,b) => Math.max(0, Math.floor((new Date(b||new Date()) - new Date(a)) / 86400000));
const uid = () => Math.random().toString(36).slice(2,9);
const makeDueDate = f => f.paymentType==="cash" && f.receivedDate ? f.receivedDate : "";
const fmtTerms = inv => { if(!inv) return "—"; if(inv.paymentType==="advance") return "Advance "+(inv.paymentPercent||"")+"%"; if(inv.paymentType==="cash") return "Cash"; if(inv.paymentType==="after_received") return "Progress "+(inv.paymentPercent||"")+"%"; if(inv.paymentType==="pis") return "PIS "+(inv.paymentDays||30)+"d"; return "—"; };
const fmtMethod = inv => { if(!inv) return "—"; const m=PAYMENT_METHODS.find(x=>x.id===inv.paymentMethod); return m?m.label:"—"; };
const isLockedInPV = (inv, vouchers) => inv.voucherNo && vouchers.some(v=>v.no===inv.voucherNo);
const stageDuration = (history, idx) => { const end = idx+1<history.length ? new Date(history[idx+1].date) : new Date(); return Math.max(0, Math.floor((end - new Date(history[idx].date))/86400000)); };
const timeAgo = ts => {
  if(!ts) return "";
  const then = new Date(ts.includes(" ")?ts.replace(" ","T"):ts);
  const mins = Math.max(0, Math.floor((new Date()-then)/60000));
  if(mins<1) return "just now";
  if(mins<60) return mins+" min";
  const hrs = Math.floor(mins/60);
  if(hrs<24) return hrs+" hr"+(hrs>1?"s":"");
  const days = Math.floor(hrs/24);
  return days+" day"+(days>1?"s":"");
};
const greeting = () => { const h=new Date().getHours(); return h<12?"Good morning":h<17?"Good afternoon":"Good evening"; };

const INIT_INV = []; // Sample invoices removed — load from DB
const INIT_VOUCHERS = []; // Sample vouchers removed — load from DB
const INIT_SUBMISSIONS = []; // Sample submissions removed — load from DB

const EMPTY_INV_FORM = { id:"", lpo:"", supplier:"", amount:"", date:"", receivedDate:"", paymentMethod:"bank_transfer", paymentType:"after_received", paymentDays:30, paymentPercent:"", urgent:false, projectId:"" };

const S = {
  card:     { background:"#fff", borderRadius:16, padding:20, border:"1px solid #e3ebed", marginBottom:16, boxShadow:"0 1px 2px rgba(18,38,43,0.04)" },
  input:    { width:"100%", padding:"10px 14px", borderRadius:10, border:"1px solid #d5e1e4", background:"#fff", color:"#12262b", fontSize:14, boxSizing:"border-box", fontFamily:"'DM Sans',sans-serif", outline:"none" },
  btn:      { background:"#0b7a87", color:"#fff", border:"none", borderRadius:10, padding:"10px 22px", cursor:"pointer", fontWeight:600, fontSize:14, fontFamily:"'DM Sans',sans-serif" },
  btnSec:   { background:"#fff", color:"#3b545a", border:"1px solid #d5e1e4", borderRadius:10, padding:"10px 22px", cursor:"pointer", fontWeight:600, fontSize:14, fontFamily:"'DM Sans',sans-serif" },
  btnSm:    { background:"#0b7a87", color:"#fff", border:"none", borderRadius:8, padding:"5px 14px", cursor:"pointer", fontWeight:600, fontSize:12 },
  btnWarn:  { background:"#fdf3e3", color:"#7a4506", border:"1px solid #f3d9ad", borderRadius:8, padding:"5px 10px", cursor:"pointer", fontWeight:600, fontSize:12 },
  btnDanger:{ background:"#fde8e6", color:"#b42318", border:"1px solid #f3c6c1", borderRadius:8, padding:"5px 10px", cursor:"pointer", fontWeight:600, fontSize:12 },
  btnAdmin: { background:"#b42318", color:"#fff", border:"none", borderRadius:10, padding:"10px 22px", cursor:"pointer", fontWeight:600, fontSize:14, fontFamily:"'DM Sans',sans-serif" },
  label:    { fontSize:11, color:"#5a7278", marginBottom:6, display:"block", fontWeight:600, letterSpacing:"0.06em", textTransform:"uppercase" },
  th:       { padding:"12px 14px", textAlign:"left", fontWeight:600, fontSize:10, color:"#4f676d", letterSpacing:"0.08em", textTransform:"uppercase", background:"#f4f8f9" },
  td:       { padding:"13px 14px", fontSize:13, verticalAlign:"middle", color:"#12262b" },
  h:        { fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:600, color:"#12262b" },
};

const CSS = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;600;700&family=DM+Sans:wght@400;500;600&display=swap');*{box-sizing:border-box}body{background:#f4f8f9;font-family:'DM Sans',-apple-system,sans-serif;color:#12262b}::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-thumb{background:rgba(11,122,135,0.3);border-radius:3px}.hover-up{transition:all 0.2s ease}.hover-up:hover{transform:translateY(-1px)}.inv-row:hover{background:rgba(11,122,135,0.03)!important}input:focus,select:focus,textarea:focus{border-color:rgba(11,122,135,0.5)!important;box-shadow:0 0 0 3px rgba(11,122,135,0.1)!important;outline:none}@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}@keyframes confettiFall{0%{transform:translateY(0) rotate(0deg);opacity:1}100%{transform:translateY(420px) rotate(360deg);opacity:0}}.fade-in{animation:fadeIn 0.3s ease}.pulse{animation:pulse 2s infinite}`;

/* ── tiny components ── */
function Badge({ stage, stages }) {
  const list = stages || DEFAULT_STAGES;
  const s = list.find(x=>x.id===stage) || list[0];
  return <span style={{background:s.color+"18",color:s.color,border:"1px solid "+s.color+"40",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{s.label}</span>;
}
function RoleBadge({ role }) {
  const c = ROLE_COLORS[role]||"#4f676d";
  return <span style={{background:c+"18",color:c,border:"1px solid "+c+"40",borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>{ROLE_LABELS[role]||role}</span>;
}
const SUP_NOISE = new Set(["llc","l","c","ltd","limited","co","company","fze","fzco","fzc","est","establishment","inc","corp","corporation","the","and"]);
function normName(n) {
  return String(n||"").toLowerCase().replace(/[^a-z0-9 ]+/g," ").split(/\s+/).filter(w=>w&&!SUP_NOISE.has(w)).join(" ");
}
function nameSimilarity(a, b) {
  const x=normName(a), y=normName(b);
  if(!x||!y) return 0;
  if(x===y) return 1;
  if(x.includes(y)||y.includes(x)) return 0.9;
  const ta=new Set(x.split(" ")), tb=new Set(y.split(" "));
  let inter=0; ta.forEach(t=>{ if(tb.has(t)) inter++; });
  const jac=inter/(ta.size+tb.size-inter);
  const bg=t=>{ const m=new Map(); const z=t.replace(/ /g,""); for(let i=0;i<z.length-1;i++){ const g=z.slice(i,i+2); m.set(g,(m.get(g)||0)+1); } return m; };
  const ba=bg(x), bb=bg(y); let hit=0, na=0, nb=0;
  ba.forEach((v,k)=>{ na+=v; if(bb.has(k)) hit+=Math.min(v,bb.get(k)); }); bb.forEach(v=>{ nb+=v; });
  const dice=(na+nb)?2*hit/(na+nb):0;
  return Math.max(jac,dice);
}
function suggestSuppliers(name, suppliers, n=3, min=0.55) {
  return suppliers.map(s=>({s, score:nameSimilarity(name,s.name)})).filter(x=>x.score>=min).sort((a,b)=>b.score-a.score).slice(0,n);
}
function SupplierVerifyCard({ inv, suppliers, canVerify, onVerify }) {
  const [name, setName] = useState(inv.supplier||"");
  const [busy, setBusy] = useState(false);
  useEffect(()=>{ setName(inv.supplier||""); }, [inv.id, inv.supplier]);
  const sugg = suggestSuppliers(name, suppliers, 4);
  const exact = suppliers.find(x=>x.name.trim().toLowerCase()===name.trim().toLowerCase());
  const go = async (action, nm) => { if(busy) return; setBusy(true); try { await onVerify(inv, action, nm); } finally { setBusy(false); } };
  return (
    <div style={{background:"#fdf3e3",border:"0.5px solid #f3d9ad",borderRadius:12,padding:"14px 16px",marginBottom:14}}>
      <div style={{fontSize:14,fontWeight:700,color:"#7a4506"}}>⚠ New supplier - not on the supplier list</div>
      <div style={{fontSize:12,color:"#7a4506",marginTop:3}}>Typed by the engineer: <b>{inv.submittedSupplier||inv.supplier}</b>. {canVerify?"Check the name against the list, correct it if needed, then approve it or switch to an existing supplier. The invoice cannot move forward until this is done.":"Waiting for the Document Controller to verify it."}</div>
      {canVerify&&<>
        <div style={{marginTop:10}}>
          <span style={S.label}>Supplier name (edit if needed)</span>
          <input style={S.input} value={name} onChange={e=>setName(e.target.value)}/>
        </div>
        {sugg.length>0&&<div style={{marginTop:10}}>
          <div style={{fontSize:11,fontWeight:600,color:"#7a4506",marginBottom:6}}>SIMILAR SUPPLIERS ALREADY ON THE LIST</div>
          {sugg.map(x=><div key={x.s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,padding:"6px 10px",background:"#fff",border:"0.5px solid #f3d9ad",borderRadius:8,marginBottom:5}}>
            <span style={{fontSize:13}}>{x.s.name} <span style={{fontSize:11,color:"#5a7278"}}>({Math.round(x.score*100)}% match)</span></span>
            <button className="hover-up" style={S.btnSm} disabled={busy} onClick={()=>go("use_existing",x.s.name)}>Use this supplier</button>
          </div>)}
        </div>}
        <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
          {exact
            ? <button className="hover-up" style={S.btn} disabled={busy} onClick={()=>go("use_existing",exact.name)}>✓ Use existing "{exact.name}"</button>
            : <button className="hover-up" style={S.btn} disabled={busy||!name.trim()} onClick={()=>go("approve_new",name.trim())}>✓ Approve as new supplier</button>}
        </div>
      </>}
    </div>
  );
}
function SupplierAutocomplete({ value, onChange, suppliers, placeholder, canManage, onAddSupplier }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value||"");
  useEffect(()=>{ setQuery(value||""); }, [value]);
  const matches = query.trim()
    ? suppliers.filter(s=>s.name.toLowerCase().includes(query.toLowerCase())).slice(0,8)
    : suppliers.slice(0,8);
  const exactMatch = suppliers.some(s=>s.name.toLowerCase()===query.trim().toLowerCase());
  const showAddNew = canManage && query.trim().length>1 && !exactMatch;
  const showNote = !canManage && query.trim().length>1 && !exactMatch;
  return (
    <div style={{position:"relative"}}>
      <input
        style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid #d5e1e4",fontSize:13}}
        value={query}
        placeholder={placeholder||"Type to search supplier..."}
        onChange={e=>{ setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={()=>setOpen(true)}
        onBlur={()=>setTimeout(()=>setOpen(false),150)}
      />
      {open&&(matches.length>0||showAddNew||showNote)&&(
        <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid #d5e1e4",borderRadius:8,marginTop:4,maxHeight:220,overflowY:"auto",zIndex:50,boxShadow:"0 4px 12px rgba(0,0,0,0.1)"}}>
          {matches.map(s=>(
            <div key={s.id} onMouseDown={()=>{ setQuery(s.name); onChange(s.name); setOpen(false); }} style={{padding:"8px 12px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f5f5f5"}} className="hover-up">{s.name}</div>
          ))}
          {showNote&&<div style={{padding:"8px 12px",fontSize:12,color:"#7a4506",background:"#fdf3e3"}}>{matches.length?"Not in the list? ":"No match. "}Keep the full name as typed - the Document Controller will verify it as a new supplier.</div>}
          {showAddNew&&(
            <div onMouseDown={async()=>{
              const name=query.trim();
              const newSup=await onAddSupplier(name);
              if(newSup){ setQuery(newSup.name); onChange(newSup.name); }
              setOpen(false);
            }} style={{padding:"8px 12px",cursor:"pointer",fontSize:13,color:"#0b7a87",fontWeight:600}} className="hover-up">+ Add "{query.trim()}" as new supplier</div>
          )}
        </div>
      )}
    </div>
  );
}
function ProjBadge({ proj }) {
  if(!proj) return <span style={{color:"#4f676d",fontSize:12}}>—</span>;
  return <span style={{background:proj.color+"18",color:proj.color,border:"1px solid "+proj.color+"40",borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>{proj.code}</span>;
}

function DaysBadge({ days }) { const c=days>7?"#ff6060":days>3?"#e8a020":"#30d98a"; return <span style={{background:c+"18",color:c,border:"1px solid "+c+"40",borderRadius:20,padding:"3px 8px",fontSize:11,fontWeight:700}}>{days}d</span>; }
function Toggle({ value, onChange, label }) {
  return (
    <div onClick={onChange} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:12}}>
      <div style={{width:42,height:24,borderRadius:12,background:value?"#0b7a87":"#ccc",position:"relative",transition:"background 0.2s",flexShrink:0}}>
        <div style={{position:"absolute",top:3,left:value?20:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
      </div>
      <span style={{fontSize:13,color:"#12262b",fontWeight:500}}>{label}</span>
    </div>
  );
}

function Confetti() {
  const pieces = useMemo(()=>{ const a=[]; for(let i=0;i<28;i++) a.push({l:(i*97)%100,d:((i*37)%50)/100,dur:2.5+((i*53)%150)/100,c:CONFETTI_COLORS[i%7],r:(i*67)%360,w:6+((i*13)%6)}); return a; },[]);
  return <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none",borderRadius:16}}>{pieces.map((x,i)=><span key={i} style={{position:"absolute",top:-20,left:x.l+"%",width:x.w,height:x.w*1.6,background:x.c,borderRadius:2,transform:"rotate("+x.r+"deg)",animation:"confettiFall "+x.dur+"s ease-in "+x.d+"s forwards"}}/>)}</div>;
}

function StageStepper({ currentStage, stages }) {
  const st = stages || DEFAULT_STAGES;
  const ci = st.findIndex(s=>s.id===currentStage);
  return (
    <div style={{display:"flex",alignItems:"flex-start",overflowX:"auto",padding:"6px 2px 2px"}}>
      {st.map((s,i)=>{ const done=i<ci,cur=i===ci; return (
        <div key={s.id} style={{display:"flex",alignItems:"center",flex:i===st.length-1?"0 0 auto":"1 1 auto",minWidth:0}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",minWidth:65}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:done?s.color:cur?"#fff":"#f4f8f9",border:"3px solid "+(done||cur?s.color:"#d5e1e4"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:done?"#fff":cur?s.color:"#ccc",boxShadow:cur?"0 0 0 4px "+s.color+"18":"none"}}>{done?"✓":i+1}</div>
            <div style={{fontSize:9,marginTop:5,textAlign:"center",fontWeight:cur?700:500,color:cur?s.color:done?"#12262b":"#bbb",whiteSpace:"nowrap"}}>{s.label}</div>
          </div>
          {i<st.length-1&&<div style={{flex:1,height:3,background:i<ci?"#0b7a87":"#d5e1e4",margin:"0 2px",minWidth:10,marginBottom:16}}/>}
        </div>
      );})}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(18,38,43,0.45)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:16,padding:28,width:wide?700:480,maxWidth:"100%",border:"1px solid #e3ebed",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(18,38,43,0.18)"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Bricolage Grotesque',sans-serif",fontWeight:600,fontSize:17,color:"#12262b"}}>{title}</div>
          <button onClick={onClose} style={{background:"#f4f8f9",border:"none",color:"#4f676d",fontSize:18,cursor:"pointer",borderRadius:8,width:32,height:32}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PaymentFields({ form, setForm }) {
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  return (
    <div>
      <div style={{marginBottom:12}}>
        <span style={S.label}>Payment Method</span>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {PAYMENT_METHODS.map(pm=><div key={pm.id} onClick={()=>upd("paymentMethod",pm.id)} style={{padding:"8px",borderRadius:10,border:"2px solid "+(form.paymentMethod===pm.id?"#0b7a87":"#d5e1e4"),background:form.paymentMethod===pm.id?"rgba(11,122,135,0.06)":"#f4f8f9",cursor:"pointer",textAlign:"center",fontSize:12,fontWeight:600,color:form.paymentMethod===pm.id?"#0b7a87":"#4f676d"}}>{pm.label}</div>)}
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <span style={S.label}>Payment Terms</span>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {PAYMENT_TERMS.map(pt=><div key={pt.id} onClick={()=>upd("paymentType",pt.id)} style={{padding:"8px",borderRadius:10,border:"2px solid "+(form.paymentType===pt.id?"#0b7a87":"#d5e1e4"),background:form.paymentType===pt.id?"rgba(11,122,135,0.06)":"#f4f8f9",cursor:"pointer",textAlign:"center",fontSize:12,fontWeight:600,color:form.paymentType===pt.id?"#0b7a87":"#4f676d"}}>{pt.label}</div>)}
        </div>
      </div>
      {(form.paymentType==="advance"||form.paymentType==="after_received")&&<div style={{marginBottom:12}}><span style={S.label}>%</span><input style={S.input} type="number" value={form.paymentPercent||""} onChange={e=>upd("paymentPercent",e.target.value)}/></div>}
      {form.paymentType==="pis"&&<div style={{marginBottom:12}}><span style={S.label}>Days</span><input style={S.input} type="number" value={form.paymentDays||30} onChange={e=>upd("paymentDays",parseInt(e.target.value)||0)}/></div>}
    </div>
  );
}

/* ── Login Form — uses backend API ── */
function LoginForm({ onLogin }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const go = async () => {
    if(!u.trim()||!p.trim()){ setErr("Please enter username and password"); return; }
    setLoading(true); setErr("");
    try {
      const res = await fetch("/api/auth/login", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({username:u.trim(), password:p})
      });
      const data = await res.json();
      if(!res.ok){ setErr(data.error||"Login failed"); setLoading(false); return; }
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      onLogin(data.user);
    } catch(e) {
      setErr("Cannot connect to server"); setLoading(false);
    }
  };
  return (
    <div>
      <div style={{marginBottom:12}}><span style={S.label}>Username</span><input style={S.input} value={u} onChange={e=>setU(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="e.g. admin"/></div>
      <div style={{marginBottom:12}}><span style={S.label}>Password</span><input style={S.input} type="password" value={p} onChange={e=>setP(e.target.value)} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="••••••"/></div>
      {err&&<div style={{background:"#fde8e6",border:"1px solid #f3c6c1",color:"#b42318",borderRadius:10,padding:"8px 12px",fontSize:12,marginBottom:12}}>{err}</div>}
      <button className="hover-up" style={{...S.btn,width:"100%",opacity:loading?0.7:1}} onClick={go} disabled={loading}>{loading?"Signing in…":"Sign In"}</button>
    </div>
  );
}

/* ══════════════════════════════════
   ADMIN PANEL
══════════════════════════════════ */
function AdminPanel({ users, setUsers, stages, setStages, projects, setProjects, settings, setSettings, auditLog, setAuditLog, alertRules, setAlertRules, invoices, vouchers, onClose, showToast, logAudit }) {
  const [tab, setTab] = useState("dashboard");
  const [editUser, setEditUser] = useState(null);
  const [editStage, setEditStage] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [newUser, setNewUser] = useState({username:"",password:"",name:"",role:"engineer",roles:["engineer"],active:true,projects:[]});
  const [newStage, setNewStage] = useState({label:"",color:"#0b7a87",ownerRole:"dc",requiresPV:false});
  const [newProject, setNewProject] = useState({name:"",code:"",color:"#0b7a87",active:true});
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");

  useEffect(()=>{
    const onKey = e => {
      if(e.key!=="Escape") return;
      if(confirm) setConfirm(null);
      else if(editUser) setEditUser(null);
      else if(editStage) setEditStage(null);
      else if(editProject) setEditProject(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, editUser, editStage, editProject, onClose]);


  const engineers = users.filter(u=>u.role==="engineer");

  const generatePassword = () => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghjkmnpqrstuvwxyz";
    const digits = "23456789";
    const symbols = "@#$!";
    const all = upper + lower + digits + symbols;
    let pwd = upper[Math.floor(Math.random()*upper.length)]
      + lower[Math.floor(Math.random()*lower.length)]
      + digits[Math.floor(Math.random()*digits.length)]
      + symbols[Math.floor(Math.random()*symbols.length)];
    for(let i=0;i<6;i++) pwd += all[Math.floor(Math.random()*all.length)];
    return pwd.split("").sort(()=>Math.random()-0.5).join("");
  };

  const saveNewUser = async () => {
    if(!newUser.username.trim()||!newUser.password.trim()||!newUser.name.trim()) return showToast("Fill all fields","error");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/users", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({...newUser, username:newUser.username.trim(), name:newUser.name.trim()})
      });
      const data = await res.json();
      if(!res.ok) return showToast(data.error||"Failed to create user","error");
      setUsers(p=>[...p, data]);
      try { logAudit("User Added", "New user: "+newUser.username+" ("+ROLE_LABELS[newUser.role]+")"); } catch(_){}
      setNewUser({username:"",password:"",name:"",role:"engineer",active:true,projects:[]});
      showToast("User added!");
    } catch(e) {
      showToast("Cannot connect to server","error");
    }
  };
  const saveEditUser = async () => {
    if(!editUser.username.trim()||!editUser.name.trim()) return showToast("Fill all fields","error");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/users/"+editUser.id, {
        method:"PUT",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({username:editUser.username.trim(),name:editUser.name.trim(),role:editUser.role,roles:(editUser.roles&&editUser.roles.length?editUser.roles:[editUser.role]),active:editUser.active,projects:editUser.projects||[]})
      });
      const data = await res.json();
      if(!res.ok) return showToast(data.error||"Failed to update user","error");
      // Update password if provided
      if(editUser.password&&editUser.password.trim().length>=4){
        await fetch("/api/users/"+editUser.id+"/password",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({password:editUser.password.trim()})});
      }
      setUsers(p=>p.map(u=>u.id===editUser.id?data:u));
      try { logAudit("User Updated", editUser.username+" | roles: "+(editUser.roles&&editUser.roles.length?editUser.roles.join(", "):editUser.role)); } catch(_){}
      setEditUser(null); showToast("User updated!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };
  const deleteUser = u => {
    const hasAdminRole = x => (x.roles&&x.roles.length?x.roles:[x.role]).includes("admin");
    if(hasAdminRole(u)&&users.filter(x=>hasAdminRole(x)&&x.active).length<=1) return showToast("Cannot delete last admin","error");
    setConfirm({msg:"Delete user "+u.name+"?", onOk: async ()=>{
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("/api/users/"+u.id, {
          method:"DELETE",
          headers:{"Authorization":"Bearer "+token}
        });
        if(!res.ok) return showToast("Failed to delete","error");
        setUsers(p=>p.filter(x=>x.id!==u.id)); showToast("Deleted!");
        try { logAudit("User Deleted", u.username+" | "+ROLE_LABELS[u.role]); } catch(_){}
      } catch(e) { showToast("Cannot connect to server","error"); }
    }});
  };
  const toggleUser = async u => {
    if(u.role==="admin"&&users.filter(x=>x.role==="admin"&&x.active).length<=1&&u.active) return showToast("Cannot deactivate last admin","error");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/users/"+u.id, {
        method:"PUT",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({username:u.username,name:u.name,role:u.role,active:!u.active,projects:u.projects||[]})
      });
      const data = await res.json();
      if(!res.ok) return showToast("Failed to update","error");
      setUsers(p=>p.map(x=>x.id===u.id?data:x)); showToast(u.active?"Deactivated!":"Activated!");
      try { logAudit(u.active?"User Deactivated":"User Activated", u.username); } catch(_){}
    } catch(e) { showToast("Cannot connect to server","error"); }
  };

  const saveNewStage = async () => {
    if(!newStage.label.trim()) return showToast("Stage name required","error");
    const id = "s_"+uid();
    const insertAfterStage = stages.find(s=>s.id===(newStage.insertAfter||"cfo"));
    const insertOrder = insertAfterStage ? insertAfterStage.order + 1 : stages.length;
    // Shift all stages after insert point
    const shifted = stages.map(s=>s.order>=insertOrder?{...s,order:s.order+1}:s);
    const newS = {...newStage,id,order:insertOrder,label:newStage.label.trim(),isCustom:true};
    try {
      const token = localStorage.getItem("token");
      // Update shifted stages in DB
      await Promise.all(shifted.filter((s,i)=>s.order!==stages[i]?.order).map(s=>
        fetch("/api/stages/"+s.id,{method:"PUT",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
          body:JSON.stringify({label:s.label,color:s.color,ownerRole:s.ownerRole,requiresPV:s.requiresPV,order:s.order})})
      ));
      // Insert new stage
      await fetch("/api/stages",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body:JSON.stringify({id,label:newS.label,color:newS.color,ownerRole:newS.ownerRole,requiresPV:newS.requiresPV,order:newS.order})});
      setStages([...shifted,newS].sort((a,b)=>a.order-b.order));
      setNewStage({label:"",color:"#0b7a87",ownerRole:"dc",requiresPV:false,insertAfter:"cfo"}); showToast("Stage added!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };
  const saveEditStage = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch("/api/stages/"+editStage.id,{method:"PUT",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body:JSON.stringify({label:editStage.label,color:editStage.color,ownerRole:editStage.ownerRole,requiresPV:editStage.requiresPV,order:editStage.order})});
      setStages(p=>p.map(s=>s.id===editStage.id?{...editStage}:s)); setEditStage(null); showToast("Stage updated!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };
  const deleteStage = s => {
    if(["received","completed"].includes(s.id)) return showToast("Cannot delete this stage","error");
    if(!s.isCustom) return showToast("Cannot delete built-in stages","error");
    setConfirm({msg:"Delete stage \""+s.label+"\"?", onOk:async()=>{
      const token=localStorage.getItem("token");
      await fetch("/api/stages/"+s.id,{method:"DELETE",headers:{"Authorization":"Bearer "+token}}).catch(()=>{});
      setStages(p=>p.filter(x=>x.id!==s.id).map((x,i)=>({...x,order:i}))); showToast("Deleted!");
    }});
  };
  const handleDrop = async i => {
    if(dragIdx===null||dragIdx===i){setDragIdx(null);setDragOver(null);return;}
    const arr=[...stages]; const [moved]=arr.splice(dragIdx,1); arr.splice(i,0,moved);
    const reordered = arr.map((s,idx)=>({...s,order:idx}));
    setStages(reordered); setDragIdx(null); setDragOver(null);
    try {
      const token = localStorage.getItem("token");
      await Promise.all(reordered.map(s=>
        fetch("/api/stages/"+s.id,{method:"PUT",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
          body:JSON.stringify({label:s.label,color:s.color,ownerRole:s.ownerRole,requiresPV:s.requiresPV,order:s.order})})
      ));
      showToast("Reordered!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };

  const saveNewProject = async () => {
    if(!newProject.name.trim()||!newProject.code.trim()) return showToast("Name and code required","error");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/projects",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify(newProject)});
      const data = await res.json();
      if(!res.ok) return showToast(data.error||"Failed to save project","error");
      setProjects(p=>[...p,data]);
      setNewProject({name:"",code:"",color:"#0b7a87",active:true}); showToast("Project added!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };
  const saveEditProject = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/projects/"+editProject.id,{method:"PUT",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify(editProject)});
      const data = await res.json();
      if(!res.ok) return showToast(data.error||"Failed to update project","error");
      setProjects(p=>p.map(x=>x.id===editProject.id?data:x)); setEditProject(null); showToast("Project updated!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };
  const deleteProject = proj => {
    setConfirm({msg:"Delete project \""+proj.name+"\"?", onOk: async ()=>{
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("/api/projects/"+proj.id,{method:"DELETE",headers:{"Authorization":"Bearer "+token}});
        if(!res.ok) return showToast("Failed to delete","error");
        setProjects(p=>p.filter(x=>x.id!==proj.id));
        setUsers(p=>p.map(u=>({...u,projects:(u.projects||[]).filter(pid=>pid!==proj.id)})));
        showToast("Deleted!");
      } catch(e) { showToast("Cannot connect to server","error"); }
    }});
  };
  const toggleProjectUser = (userId, projId) => {
    setUsers(p=>p.map(u=>{
      if(u.id!==userId) return u;
      const has = (u.projects||[]).includes(projId);
      return {...u, projects: has ? u.projects.filter(x=>x!==projId) : [...(u.projects||[]), projId]};
    }));
  };

  const saveAssignments = async () => {
    try {
      const token = localStorage.getItem("token");
      const engineers = users.filter(u=>u.role==="engineer");

      await Promise.all(engineers.map(u=>
        fetch("/api/users/"+u.id, {
          method:"PUT",
          headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
          body: JSON.stringify({username:u.username, name:u.name, role:u.role, active:u.active, projects:u.projects||[]})
        })
      ));
      showToast("Assignments saved!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };

  const [newRule, setNewRule] = useState({type:"stuck",threshold:7,stage:"all",active:true});
  const addRule = async () => {
    const labels = {
      stuck:"Stuck > "+newRule.threshold+" days",
      amount:"Amount > AED "+Number(newRule.threshold).toLocaleString(),
      overdue:"Past due date",
      urgent_unactioned:"Urgent unactioned > "+newRule.threshold+" hrs",
      pending_submission:"Submission pending > "+newRule.threshold+" hrs",
      no_pv:"No PV created > "+newRule.threshold+" days"
    };
    const rule = {id:"r"+uid(),active:true,...newRule,label:labels[newRule.type]||"Custom rule"};
    try {
      const token = localStorage.getItem("token");
      await fetch("/api/alerts",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify(rule)});
      setAlertRules(p=>[...p,rule]);
      showToast("Rule added!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };

  const [settingsForm, setSettingsForm] = useState({...settings});
  const saveSettings = () => { setSettings({...settingsForm}); showToast("Settings saved!"); };

  const exportAll = () => {
    const data = { invoices, users: users.map(u=>({...u,password:"***"})), projects, stages, auditLog, exportedAt: new Date().toISOString() };
    const a = document.createElement("a");
    a.href = "data:application/json;charset=utf-8,"+encodeURIComponent(JSON.stringify(data,null,2));
    a.download = "invoice_tracker_backup_"+todayStr()+".json";
    a.click(); showToast("Exported!");
  };

  const activeInvs   = invoices.filter(i=>i.stage!=="completed");
  const filedInvs    = invoices.filter(i=>i.stage==="completed");
  const totalValue   = invoices.reduce((s,i)=>s+i.amount,0);
  const activeValue  = activeInvs.reduce((s,i)=>s+i.amount,0);
  const stageStats   = stages.map(s=>({...s, count:invoices.filter(i=>i.stage===s.id).length, avgDays: (()=>{ const matched=invoices.filter(i=>i.stage===s.id); if(!matched.length) return 0; return Math.round(matched.reduce((sum,inv)=>{ const h=inv.history.find(h=>h.stage===s.id); return sum+(h?daysDiff(h.date):0); },0)/matched.length); })() }));
  const supplierStats= [...invoices.reduce((m,i)=>{ const e=m.get(i.supplier)||{supplier:i.supplier,count:0,total:0,filed:0}; e.count++;e.total+=i.amount;if(i.stage==="completed")e.filed++;m.set(i.supplier,e);return m;},new Map()).values()].sort((a,b)=>b.total-a.total).slice(0,5);
  const stuckInvs    = activeInvs.filter(i=>daysDiff(i.history[i.history.length-1].date)>settings.stuckDaysThreshold);
  const urgentInvs   = activeInvs.filter(i=>i.urgent);

  const tabs = [["dashboard","📊 Dashboard"],["users","👥 Users"],["stages","🔄 Stages"],["projects","🏗️ Projects"],["assignments","📌 Assignments"],["alerts","🔔 Alerts"],["settings","⚙️ Settings"],["audit","📋 Audit Log"],["data","💾 Data"]];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(18,38,43,0.45)",zIndex:1000,display:"flex",alignItems:"stretch",justifyContent:"flex-end"}}>
      {confirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(18,38,43,0.45)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,padding:28,width:360,border:"1px solid #e3ebed",boxShadow:"0 20px 60px rgba(18,38,43,0.18)"}}>
            <div style={{fontSize:15,fontWeight:600,marginBottom:20,color:"#12262b"}}>{confirm.msg}</div>
            <div style={{display:"flex",gap:10}}>
              <button className="hover-up" style={{...S.btnDanger,flex:1,padding:"10px",fontSize:14,borderRadius:10}} onClick={()=>{confirm.onOk();setConfirm(null);}}>Confirm</button>
              <button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div style={{width:"min(100%,860px)",background:"#f4f8f9",display:"flex",flexDirection:"column",boxShadow:"-10px 0 40px rgba(18,38,43,0.15)"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e3ebed",padding:"18px 24px",display:"flex",alignItems:"center",gap:14,flexShrink:0}}>
          <div style={{width:40,height:40,borderRadius:12,background:"#0b7a87",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>⚙️</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Bricolage Grotesque',sans-serif",fontWeight:600,fontSize:18,color:"#12262b"}}>Admin Panel</div>
            <div style={{fontSize:12,color:"#4f676d",marginTop:1}}>System configuration & management</div>
          </div>
          <button onClick={onClose} style={{background:"#f4f8f9",border:"none",color:"#4f676d",fontSize:18,cursor:"pointer",borderRadius:10,width:38,height:38}}>✕</button>
        </div>
        <div style={{display:"flex",gap:4,padding:"10px 20px",background:"#fff",borderBottom:"1px solid #e3ebed",flexShrink:0,overflowX:"auto"}}>
          {tabs.map(([id,lbl])=>(
            <div key={id} onClick={()=>setTab(id)} style={{padding:"8px 16px",borderRadius:20,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif",color:tab===id?"#fff":"#3b545a",background:tab===id?"#0b7a87":"transparent",whiteSpace:"nowrap"}}>{lbl}</div>
          ))}
        </div>
        <div style={{flex:1,overflowY:"auto",padding:24}}>

          {/* DASHBOARD */}
          {tab==="dashboard"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:16}}>System Overview</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:20}}>
                {[["Total Invoices",invoices.length,"#0b7a87"],["Active",activeInvs.length,"#e8a020"],["Filed",filedInvs.length,"#20c070"],["Total Value","AED "+totalValue.toLocaleString(),"#4a9eff"],["Active Value","AED "+activeValue.toLocaleString(),"#a070f0"],["Stuck",stuckInvs.length,"#ff6060"],["Urgent",urgentInvs.length,"#ff4444"],["Users",users.filter(u=>u.active).length,"#30d98a"]].map(([k,v,c])=>(
                  <div key={k} style={{...S.card,marginBottom:0,textAlign:"center",padding:14,borderRadius:12}}>
                    <div style={{...S.h,fontSize:v.toString().length>6?14:22,color:c}}>{v}</div>
                    <div style={{fontSize:10,color:"#4f676d",marginTop:4}}>{k}</div>
                  </div>
                ))}
              </div>
              {stuckInvs.length>0&&<div style={{background:"#fde8e6",border:"1px solid #f3c6c1",borderRadius:12,padding:"12px 16px",marginBottom:16}}>
                <div style={{fontWeight:600,color:"#b42318",marginBottom:8}}>⚠️ {stuckInvs.length} invoice(s) stuck {">"} {settings.stuckDaysThreshold} days</div>
                {stuckInvs.map(i=><div key={i.id} style={{fontSize:12,color:"#b42318",marginBottom:3}}>• {i.id} — {i.supplier} — {daysDiff(i.history[i.history.length-1].date)}d at <b>{stages.find(s=>s.id===i.stage)?.label}</b></div>)}
              </div>}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={S.card}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Stage Load & Avg Time</div>
                  {stageStats.filter(s=>s.id!=="completed"&&s.ownerRole!=="pd").map(s=>(
                    <div key={s.id} style={{marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:12,color:s.color,fontWeight:600}}>{s.label}</span>
                        <span style={{fontSize:11,color:"#4f676d"}}>{s.count} inv · avg {s.avgDays}d</span>
                      </div>
                      <div style={{background:"#edf2f3",borderRadius:4,height:6}}>
                        <div style={{background:s.color,borderRadius:4,height:6,width:Math.min(100,s.count*20)+"%",transition:"width 0.5s"}}/>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={S.card}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Top Suppliers by Value</div>
                  {supplierStats.map((s,i)=>(
                    <div key={s.supplier} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      <div style={{width:22,height:22,borderRadius:"50%",background:["#0b7a87","#30d98a","#f0a030","#a070f0","#ff6060"][i],display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",flexShrink:0}}>{i+1}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600}}>{s.supplier}</div>
                        <div style={{fontSize:11,color:"#4f676d"}}>{s.count} invoices · {s.filed} filed</div>
                      </div>
                      <div style={{fontSize:12,fontWeight:700,color:"#0b7a87"}}>AED {s.total.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Project Progress</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
                  {projects.map(p=>{ const pInvs=invoices.filter(i=>i.projectId===p.id); const filed=pInvs.filter(i=>i.stage==="completed").length; const pct=pInvs.length?Math.round(filed/pInvs.length*100):0; const val=pInvs.reduce((s,i)=>s+i.amount,0); return(
                    <div key={p.id} style={{background:p.color+"08",border:"1px solid "+p.color+"30",borderRadius:12,padding:"12px 14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}><div style={{width:10,height:10,borderRadius:"50%",background:p.color}}/><span style={{fontWeight:700,fontSize:13,color:p.color}}>{p.name}</span></div>
                      <div style={{fontSize:12,color:"#4f676d",marginBottom:6}}>{pInvs.length} invoices · AED {val.toLocaleString()}</div>
                      <div style={{background:"#edf2f3",borderRadius:4,height:6,marginBottom:4}}><div style={{background:p.color,borderRadius:4,height:6,width:pct+"%"}}/></div>
                      <div style={{fontSize:11,color:"#4f676d"}}>{filed}/{pInvs.length} filed ({pct}%)</div>
                    </div>
                  );})}
                </div>
              </div>
            </div>
          )}

          {/* ALERTS */}
          {tab==="alerts"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:4}}>Alert Rules</div>
              <div style={{fontSize:12,color:"#4f676d",marginBottom:16}}>Triggered alerts appear in the notification bar for all DC users.</div>
              <div style={{...S.card,background:"rgba(11,122,135,0.03)",border:"1px solid rgba(11,122,135,0.2)"}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:12,color:"#0b7a87"}}>+ Add Rule</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Type</span>
                    <select style={S.input} value={newRule.type} onChange={e=>setNewRule(p=>({...p,type:e.target.value}))}>
                      <option value="stuck">Invoice Stuck (days)</option>
                      <option value="amount">Amount Exceeds (AED)</option>
                      <option value="overdue">Past Due Date</option>
                      <option value="urgent_unactioned">Urgent Unactioned (hours)</option>
                      <option value="pending_submission">Submission Pending (hours)</option>
                      <option value="no_pv">No PV Created (days)</option>
                    </select>
                  </div>
                  <div>
                    {newRule.type==="overdue"
                      ? <><span style={S.label}>Threshold</span><input style={{...S.input,background:"#f5f5f5",color:"#5a7278"}} disabled placeholder="No threshold needed"/></>
                      : <><span style={S.label}>{
                          newRule.type==="stuck"?"Days Stuck":
                          newRule.type==="amount"?"Amount (AED)":
                          newRule.type==="urgent_unactioned"?"Hours Unactioned":
                          newRule.type==="pending_submission"?"Hours Pending":
                          newRule.type==="no_pv"?"Days Without PV":"Threshold"
                        }</span>
                          <input style={S.input} type="number" min="1" value={newRule.threshold} placeholder={
                          newRule.type==="stuck"?"e.g. 7":
                          newRule.type==="amount"?"e.g. 50000":
                          newRule.type==="urgent_unactioned"?"e.g. 24":
                          newRule.type==="pending_submission"?"e.g. 12":
                          "e.g. 3"
                        } onChange={e=>setNewRule(p=>({...p,threshold:parseInt(e.target.value)||0}))}/></>
                    }
                  </div>
                  <div><span style={S.label}>Stage</span>
                    <select style={S.input} value={newRule.stage} onChange={e=>setNewRule(p=>({...p,stage:e.target.value}))}>
                      <option value="all">All Stages</option>
                      {stages.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <button className="hover-up" style={S.btn} onClick={addRule}>Add Rule</button>
              </div>
              <div style={S.card}>
                {alertRules.map(r=>(
                  <div key={r.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:"1px solid #f4f8f9"}}>
                    <div onClick={async()=>{
                      const token=localStorage.getItem("token");
                      await fetch("/api/alerts/"+r.id,{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({active:!r.active})}).catch(()=>{});
                      setAlertRules(p=>p.map(x=>x.id===r.id?{...x,active:!x.active}:x));
                    }} style={{width:42,height:24,borderRadius:12,background:r.active?"#0b7a87":"#ccc",position:"relative",transition:"background 0.2s",flexShrink:0,cursor:"pointer"}}>
                      <div style={{position:"absolute",top:3,left:r.active?20:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:r.active?"#12262b":"#5a7278"}}>{r.label}</div>
                      <div style={{fontSize:11,color:"#4f676d"}}>{r.type==="stuck"?"Stuck for "+r.threshold+" days":r.type==="amount"?"Amount > AED "+r.threshold.toLocaleString():"Past due date"} · {r.stage==="all"?"All stages":stages.find(s=>s.id===r.stage)?.label||r.stage}</div>
                    </div>
                    <button className="hover-up" style={S.btnDanger} onClick={async()=>{
                      const token=localStorage.getItem("token");
                      await fetch("/api/alerts/"+r.id,{method:"DELETE",headers:{"Authorization":"Bearer "+token}}).catch(()=>{});
                      setAlertRules(p=>p.filter(x=>x.id!==r.id));
                    }}>Remove</button>
                  </div>
                ))}
                {alertRules.length===0&&<div style={{textAlign:"center",padding:24,color:"#4f676d"}}>No rules yet.</div>}
              </div>
            </div>
          )}

          {/* SETTINGS */}
          {tab==="settings"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:16}}>System Settings</div>
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:"#0b7a87",marginBottom:14}}>🏢 Company</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Company Name</span><input style={S.input} value={settingsForm.companyName} onChange={e=>setSettingsForm(p=>({...p,companyName:e.target.value}))}/></div>
                  <div><span style={S.label}>Currency Symbol</span><input style={S.input} value={settingsForm.currencySymbol} onChange={e=>setSettingsForm(p=>({...p,currencySymbol:e.target.value}))}/></div>
                  <div><span style={S.label}>Fiscal Year Start (month)</span>
                    <select style={S.input} value={settingsForm.fiscalYearStart} onChange={e=>setSettingsForm(p=>({...p,fiscalYearStart:e.target.value}))}>
                      {["01","02","03","04","05","06","07","08","09","10","11","12"].map(m=><option key={m} value={m}>{new Date(2024,parseInt(m)-1,1).toLocaleString("en",{month:"long"})}</option>)}
                    </select>
                  </div>
                  <div><span style={S.label}>Stuck Invoice Threshold (days)</span><input style={S.input} type="number" value={settingsForm.stuckDaysThreshold} onChange={e=>setSettingsForm(p=>({...p,stuckDaysThreshold:parseInt(e.target.value)||7}))}/></div>
                </div>
              </div>
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:"#0b7a87",marginBottom:14}}>💰 Finance</div>
                <div style={{marginBottom:10}}><span style={S.label}>Max Invoice Amount Without Extra Approval (AED)</span><input style={S.input} type="number" value={settingsForm.maxAmountNoApproval} onChange={e=>setSettingsForm(p=>({...p,maxAmountNoApproval:parseFloat(e.target.value)||0}))}/></div>
              </div>
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:"#0b7a87",marginBottom:14}}>🔧 Features</div>
                <Toggle value={settingsForm.enableEngineers} onChange={()=>setSettingsForm(p=>({...p,enableEngineers:!p.enableEngineers}))} label="Enable Engineer Submissions"/>
                <Toggle value={settingsForm.enablePV} onChange={()=>setSettingsForm(p=>({...p,enablePV:!p.enablePV}))} label="Enable Payment Vouchers (PV)"/>
                <Toggle value={settingsForm.enableUrgent} onChange={()=>setSettingsForm(p=>({...p,enableUrgent:!p.enableUrgent}))} label="Enable Urgent Flag on Invoices"/>
              </div>
              <button className="hover-up" style={{...S.btn,width:"100%"}} onClick={saveSettings}>💾 Save Settings</button>
            </div>
          )}

          {/* AUDIT LOG */}
          {tab==="audit"&&(()=>{
            const filteredLog = auditLog.filter(l=>{
              const u = l.user||l.username||"";
              const matchSearch = !auditSearch||[u,l.action,l.detail].some(x=>(x||"").toLowerCase().includes(auditSearch.toLowerCase()));
              const matchFrom = !auditFrom||(l.ts||"")>=auditFrom;
              const matchTo = !auditTo||(l.ts||"")<=auditTo+"z";
              const matchAction = auditActionFilter==="all"||l.action===auditActionFilter;
              return matchSearch&&matchFrom&&matchTo&&matchAction;
            });
            const uniqueActions=[...new Set(auditLog.map(l=>l.action).filter(Boolean))];
            return(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div><div style={{fontSize:15,fontWeight:700}}>Audit Log</div><div style={{fontSize:12,color:"#4f676d",marginTop:2}}>{filteredLog.length} of {auditLog.length} entries</div></div>
                <button className="hover-up" style={S.btnSec} onClick={()=>{ const csv=[["Timestamp","User","Role","Action","Detail"],...filteredLog.map(l=>[l.ts,l.user||l.username||"",l.role||"",l.action||"",l.detail||""])].map(r=>r.map(c=>'"'+String(c).replace(/"/g,'""')+'"').join(",")).join("\n"); const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download="audit_log_"+todayStr()+".csv";a.click(); showToast("Exported!"); }}>Export CSV</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:12}}>
                <div><span style={S.label}>Search</span><input style={S.input} placeholder="User, action, detail..." value={auditSearch} onChange={e=>setAuditSearch(e.target.value)}/></div>
                <div><span style={S.label}>From Date</span><input style={S.input} type="date" value={auditFrom} onChange={e=>setAuditFrom(e.target.value)}/></div>
                <div><span style={S.label}>To Date</span><input style={S.input} type="date" value={auditTo} onChange={e=>setAuditTo(e.target.value)}/></div>
                <div><span style={S.label}>Action Type</span><select style={S.input} value={auditActionFilter} onChange={e=>setAuditActionFilter(e.target.value)}><option value="all">All Actions</option>{uniqueActions.map(a=><option key={a} value={a}>{a}</option>)}</select></div>
              </div>
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Timestamp","User","Action","Detail"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filteredLog.slice(0,200).map(l=>(
                      <tr key={l.id} className="inv-row" style={{borderTop:"1px solid #edf2f3"}}>
                        <td style={{...S.td,fontSize:11,color:"#4f676d",whiteSpace:"nowrap"}}>{l.ts}</td>
                        <td style={S.td}><div style={{fontSize:12,fontWeight:600}}>{l.user||l.username}</div><RoleBadge role={l.role}/></td>
                        <td style={{...S.td,fontWeight:600,fontSize:12}}>{l.action}</td>
                        <td style={{...S.td,fontSize:12,color:"#3b545a"}}>{l.detail}</td>
                      </tr>
                    ))}
                    {filteredLog.length===0&&<tr><td colSpan={4} style={{padding:30,textAlign:"center",color:"#4f676d"}}>No entries found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          {/* DATA */}
          {tab==="data"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:16}}>Data Management</div>
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:"#0b7a87",marginBottom:14}}>📤 Export</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:"#f4f8f9",borderRadius:10}}>
                    <div><div style={{fontWeight:600,fontSize:13}}>Full System Backup</div><div style={{fontSize:11,color:"#4f676d"}}>All invoices, users, projects, stages, audit log</div></div>
                    <button className="hover-up" style={S.btn} onClick={exportAll}>Export JSON</button>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:"#f4f8f9",borderRadius:10}}>
                    <div><div style={{fontWeight:600,fontSize:13}}>Invoices CSV</div><div style={{fontSize:11,color:"#4f676d"}}>{invoices.length} invoices total</div></div>
                    <button className="hover-up" style={S.btnSec} onClick={()=>{ const rows=[["Invoice #","Supplier","Amount","Stage","Project","PV Ref","File No","Date"],...invoices.map(i=>[i.id,i.supplier,i.amount,(stages.find(s=>s.id===i.stage)||{}).label||i.stage,i.projectId||"",i.pvRef||"",i.fileNumber||"",i.receivedDate])].map(r=>r.map(c=>'"'+c+'"').join(",")).join("\n"); const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(rows);a.download="invoices_"+todayStr()+".csv";a.click();showToast("Exported!"); }}>Export CSV</button>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:"#f4f8f9",borderRadius:10}}>
                    <div><div style={{fontWeight:600,fontSize:13}}>Users Export</div><div style={{fontSize:11,color:"#4f676d"}}>{users.length} users (passwords hidden)</div></div>
                    <button className="hover-up" style={S.btnSec} onClick={()=>{ const rows=[["Name","Username","Role","Active","Projects"],...users.map(u=>[u.name,u.username,u.role,u.active?"Yes":"No",u.projects.join("|")])].map(r=>r.map(c=>'"'+c+'"').join(",")).join("\n"); const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(rows);a.download="users_"+todayStr()+".csv";a.click();showToast("Exported!"); }}>Export CSV</button>
                  </div>
                </div>
              </div>
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:700,color:"#ff4444",marginBottom:14}}>⚠️ Danger Zone</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:"#fde8e6",border:"1px solid #f3c6c1",borderRadius:10}}>
                    <div><div style={{fontWeight:600,fontSize:13,color:"#b42318"}}>Clear Audit Log</div><div style={{fontSize:11,color:"#b42318"}}>Permanently deletes all {auditLog.length} log entries</div></div>
                    <button className="hover-up" style={S.btnDanger} onClick={()=>setConfirm({msg:"Clear all audit log entries? This cannot be undone.",onOk:async()=>{
                      const token=localStorage.getItem("token");
                      await fetch("/api/audit",{method:"DELETE",headers:{"Authorization":"Bearer "+token}}).catch(()=>{});
                      setAuditLog([]); showToast("Audit log cleared!");
                    }})}>Clear Log</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* USERS */}
          {tab==="users"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:16}}>User Management</div>
              <div style={{...S.card,background:"rgba(11,122,135,0.03)",border:"1px solid rgba(11,122,135,0.2)"}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:12,color:"#0b7a87"}}>+ Add New User</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Full Name *</span><input style={S.input} value={newUser.name} onChange={e=>setNewUser(p=>({...p,name:e.target.value}))} placeholder="John Doe"/></div>
                  <div><span style={S.label}>Username *</span><input style={S.input} value={newUser.username} onChange={e=>setNewUser(p=>({...p,username:e.target.value}))} placeholder="johndoe"/></div>
                  <div><span style={S.label}>Password *</span>
                    <div style={{display:"flex",gap:6}}>
                      <input style={{...S.input,flex:1}} value={newUser.password} onChange={e=>setNewUser(p=>({...p,password:e.target.value}))} placeholder="••••••"/>
                      <button className="hover-up" type="button" style={{...S.btnSec,padding:"0 10px",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>setNewUser(p=>({...p,password:generatePassword()}))}>🔑 Suggest</button>
                    </div>
                  </div>
                  <div style={{gridColumn:"1 / -1"}}><span style={S.label}>Roles * (select one or more)</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
                      {ALL_ROLES.map(r=>{
                        const checked = newUser.roles?.includes(r);
                        return <div key={r} onClick={()=>setNewUser(p=>{
                          const roles = checked ? p.roles.filter(x=>x!==r) : [...(p.roles||[]),r];
                          return {...p, roles, role: roles[0]||""};
                        })} style={{padding:"6px 12px",borderRadius:20,border:"2px solid "+(checked?"#0b7a87":"#d5e1e4"),background:checked?"rgba(11,122,135,0.08)":"#f4f8f9",cursor:"pointer",fontSize:12,fontWeight:600,color:checked?"#0b7a87":"#4f676d"}}>{ROLE_LABELS[r]}</div>;
                      })}
                    </div>
                  </div>
                </div>
                <button className="hover-up" style={S.btn} onClick={saveNewUser}>Add User</button>
              </div>
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Name","Username","Role","Status","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {users.map(u=>(
                      <tr key={u.id} className="inv-row" style={{borderTop:"1px solid #edf2f3",opacity:u.active?1:0.5}}>
                        <td style={{...S.td,fontWeight:600}}>{u.name}</td>
                        <td style={{...S.td,color:"#4f676d",fontFamily:"monospace"}}>{u.username}</td>
                        <td style={S.td}><div style={{display:"flex",flexWrap:"wrap",gap:4}}>{(u.roles&&u.roles.length?u.roles:[u.role]).map(r=><RoleBadge key={r} role={r}/>)}</div></td>
                        <td style={S.td}><span style={{background:u.active?"#20c07018":"#ff000018",color:u.active?"#20c070":"#b42318",border:"1px solid "+(u.active?"#20c07040":"#f3c6c1"),borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>{u.active?"Active":"Inactive"}</span></td>
                        <td style={{...S.td,whiteSpace:"nowrap"}}>
                          <div style={{display:"flex",gap:6}}>
                            <button className="hover-up" style={S.btnWarn} onClick={()=>setEditUser({...u})}>Edit</button>
                            <button className="hover-up" style={{...S.btnSec,fontSize:11,padding:"4px 10px"}} onClick={()=>toggleUser(u)}>{u.active?"Deactivate":"Activate"}</button>
                            {!(u.roles&&u.roles.length?u.roles:[u.role]).includes("admin")&&<button className="hover-up" style={S.btnDanger} onClick={()=>deleteUser(u)}>Delete</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {editUser&&(
                <Modal title={"Edit — "+editUser.name} onClose={()=>setEditUser(null)}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div><span style={S.label}>Full Name</span><input style={S.input} value={editUser.name} onChange={e=>setEditUser(p=>({...p,name:e.target.value}))}/></div>
                    <div><span style={S.label}>Username</span><input style={S.input} value={editUser.username} onChange={e=>setEditUser(p=>({...p,username:e.target.value}))}/></div>
                    <div><span style={S.label}>New Password</span>
                      <div style={{display:"flex",gap:6}}>
                        <input style={{...S.input,flex:1}} value={editUser.password||""} onChange={e=>setEditUser(p=>({...p,password:e.target.value}))}/>
                        <button className="hover-up" type="button" style={{...S.btnSec,padding:"0 10px",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>setEditUser(p=>({...p,password:generatePassword()}))}>🔑 Suggest</button>
                      </div>
                    </div>
                    <div style={{gridColumn:"1 / -1"}}><span style={S.label}>Roles (select one or more)</span>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
                        {ALL_ROLES.map(r=>{
                          const checked = (editUser.roles&&editUser.roles.length?editUser.roles:[editUser.role]).includes(r);
                          return <div key={r} onClick={()=>setEditUser(p=>{
                            const current = (p.roles&&p.roles.length?p.roles:[p.role]);
                            const roles = checked ? current.filter(x=>x!==r) : [...current,r];
                            return {...p, roles, role: roles[0]||""};
                          })} style={{padding:"6px 12px",borderRadius:20,border:"2px solid "+(checked?"#0b7a87":"#d5e1e4"),background:checked?"rgba(11,122,135,0.08)":"#f4f8f9",cursor:"pointer",fontSize:12,fontWeight:600,color:checked?"#0b7a87":"#4f676d"}}>{ROLE_LABELS[r]}</div>;
                        })}
                      </div>
                    </div>
                  </div>
                  <Toggle value={editUser.active} onChange={()=>setEditUser(p=>({...p,active:!p.active}))} label="Active"/>
                  {(editUser.roles&&editUser.roles.length?editUser.roles:[editUser.role]).includes("engineer")&&(
                    <div style={{marginBottom:12}}>
                      <span style={S.label}>Assigned Projects</span>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
                        {projects.filter(p=>p.active).map(p=>(
                          <div key={p.id} onClick={()=>setEditUser(prev=>({...prev,projects:prev.projects.includes(p.id)?prev.projects.filter(x=>x!==p.id):[...prev.projects,p.id]}))}
                            style={{padding:"6px 14px",borderRadius:20,border:"2px solid "+(editUser.projects.includes(p.id)?p.color:"#d5e1e4"),background:editUser.projects.includes(p.id)?p.color+"18":"#f4f8f9",cursor:"pointer",fontSize:12,fontWeight:600,color:editUser.projects.includes(p.id)?p.color:"#4f676d"}}>{p.name}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1}} onClick={saveEditUser}>Save</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setEditUser(null)}>Cancel</button></div>
                </Modal>
              )}
            </div>
          )}

          {/* STAGES */}
          {tab==="stages"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:4}}>Stage Management</div>
              <div style={{fontSize:12,color:"#4f676d",marginBottom:16}}>Drag to reorder. "Received" and "Filed" are protected.</div>
              <div style={{...S.card,background:"rgba(11,122,135,0.03)",border:"1px solid rgba(11,122,135,0.2)"}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:12,color:"#0b7a87"}}>+ Add Stage</div>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Stage Name *</span><input style={S.input} value={newStage.label} onChange={e=>setNewStage(p=>({...p,label:e.target.value}))} placeholder="e.g. Legal Review"/></div>
                  <div><span style={S.label}>Insert After</span>
                    <select style={S.input} value={newStage.insertAfter||"cfo"} onChange={e=>setNewStage(p=>({...p,insertAfter:e.target.value}))}>
                      {stages.filter(s=>s.id!=="completed").map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </div>
                  <div><span style={S.label}>Color</span><input style={{...S.input,height:42,padding:4,cursor:"pointer"}} type="color" value={newStage.color} onChange={e=>setNewStage(p=>({...p,color:e.target.value}))}/></div>
                  <div><span style={S.label}>Owner Role</span>
                    <select style={S.input} value={newStage.ownerRole} onChange={e=>setNewStage(p=>({...p,ownerRole:e.target.value}))}>
                      {ALL_ROLES.filter(r=>r!=="engineer"&&r!=="admin").map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  </div>
                </div>
                <Toggle value={newStage.requiresPV} onChange={()=>setNewStage(p=>({...p,requiresPV:!p.requiresPV}))} label="Requires PV before moving in"/>
                <button className="hover-up" style={S.btn} onClick={saveNewStage}>Add Stage</button>
              </div>
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                {stages.map((s,i)=>(
                  <div key={s.id} draggable onDragStart={()=>setDragIdx(i)} onDragOver={e=>{e.preventDefault();setDragOver(i);}} onDrop={()=>handleDrop(i)} onDragEnd={()=>{setDragIdx(null);setDragOver(null);}}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:"1px solid #edf2f3",cursor:"grab",background:dragOver===i?"rgba(11,122,135,0.06)":"#fff"}}>
                    <span style={{color:"#ccc",fontSize:18,userSelect:"none"}}>⠿</span>
                    <div style={{width:14,height:14,borderRadius:"50%",background:s.color,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:13}}>{s.label}</div>
                      <div style={{fontSize:11,color:"#4f676d"}}>{ROLE_LABELS[s.ownerRole]||s.ownerRole}{s.requiresPV?" · Requires PV":""}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button className="hover-up" style={S.btnWarn} onClick={()=>setEditStage({...s})}>Edit</button>
                      {!["received","completed"].includes(s.id)&&<button className="hover-up" style={S.btnDanger} onClick={()=>deleteStage(s)}>Delete</button>}
                    </div>
                  </div>
                ))}
              </div>
              {editStage&&(
                <Modal title={"Edit Stage — "+editStage.label} onClose={()=>setEditStage(null)}>
                  <div style={{marginBottom:12}}><span style={S.label}>Stage Name</span><input style={S.input} value={editStage.label} onChange={e=>setEditStage(p=>({...p,label:e.target.value}))}/></div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div><span style={S.label}>Color</span><input style={{...S.input,height:42,padding:4,cursor:"pointer"}} type="color" value={editStage.color} onChange={e=>setEditStage(p=>({...p,color:e.target.value}))}/></div>
                    <div><span style={S.label}>Owner Role</span><select style={S.input} value={editStage.ownerRole} onChange={e=>setEditStage(p=>({...p,ownerRole:e.target.value}))}>{ALL_ROLES.filter(r=>r!=="engineer"&&r!=="admin").map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></div>
                  </div>
                  <Toggle value={editStage.requiresPV} onChange={()=>setEditStage(p=>({...p,requiresPV:!p.requiresPV}))} label="Requires PV before moving in"/>
                  <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1}} onClick={saveEditStage}>Save</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setEditStage(null)}>Cancel</button></div>
                </Modal>
              )}
            </div>
          )}

          {/* PROJECTS */}
          {tab==="projects"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:16}}>Project Management</div>
              <div style={{...S.card,background:"rgba(11,122,135,0.03)",border:"1px solid rgba(11,122,135,0.2)"}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:12,color:"#0b7a87"}}>+ Add Project</div>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Name *</span><input style={S.input} value={newProject.name} onChange={e=>setNewProject(p=>({...p,name:e.target.value}))} placeholder="New Tower Project"/></div>
                  <div><span style={S.label}>Code *</span><input style={S.input} value={newProject.code} onChange={e=>setNewProject(p=>({...p,code:e.target.value.toUpperCase()}))} placeholder="NTP" maxLength={6}/></div>
                  <div><span style={S.label}>Color</span><input style={{...S.input,height:42,padding:4,cursor:"pointer"}} type="color" value={newProject.color} onChange={e=>setNewProject(p=>({...p,color:e.target.value}))}/></div>
                </div>
                <button className="hover-up" style={S.btn} onClick={saveNewProject}>Add Project</button>
              </div>
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["","Name","Code","Engineers","Status","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {projects.map(proj=>{
                      const engs = users.filter(u=>u.role==="engineer"&&u.projects.includes(proj.id));
                      return (
                        <tr key={proj.id} className="inv-row" style={{borderTop:"1px solid #edf2f3",opacity:proj.active?1:0.5}}>
                          <td style={{...S.td,width:20}}><div style={{width:12,height:12,borderRadius:"50%",background:proj.color}}/></td>
                          <td style={{...S.td,fontWeight:600}}>{proj.name}</td>
                          <td style={{...S.td,fontFamily:"monospace",color:"#4f676d"}}>{proj.code}</td>
                          <td style={{...S.td,fontSize:12}}>{engs.length?engs.map(u=>u.name).join(", "):"None"}</td>
                          <td style={S.td}><span style={{background:proj.active?"#20c07018":"#ff000018",color:proj.active?"#20c070":"#b42318",border:"1px solid "+(proj.active?"#20c07040":"#f3c6c1"),borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>{proj.active?"Active":"Archived"}</span></td>
                          <td style={{...S.td,whiteSpace:"nowrap"}}>
                            <div style={{display:"flex",gap:6}}>
                              <button className="hover-up" style={S.btnWarn} onClick={()=>setEditProject({...proj})}>Edit</button>
                              <button className="hover-up" style={{...S.btnSec,fontSize:11,padding:"4px 10px"}} onClick={()=>{setProjects(p=>p.map(x=>x.id===proj.id?{...x,active:!x.active}:x));showToast(proj.active?"Archived!":"Restored!");}}>{proj.active?"Archive":"Restore"}</button>
                              <button className="hover-up" style={S.btnDanger} onClick={()=>deleteProject(proj)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {editProject&&(
                <Modal title={"Edit — "+editProject.name} onClose={()=>setEditProject(null)}>
                  <div style={{marginBottom:12}}><span style={S.label}>Name</span><input style={S.input} value={editProject.name} onChange={e=>setEditProject(p=>({...p,name:e.target.value}))}/></div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                    <div><span style={S.label}>Code</span><input style={S.input} value={editProject.code} onChange={e=>setEditProject(p=>({...p,code:e.target.value.toUpperCase()}))} maxLength={6}/></div>
                    <div><span style={S.label}>Color</span><input style={{...S.input,height:42,padding:4,cursor:"pointer"}} type="color" value={editProject.color} onChange={e=>setEditProject(p=>({...p,color:e.target.value}))}/></div>
                  </div>
                  <Toggle value={editProject.active} onChange={()=>setEditProject(p=>({...p,active:!p.active}))} label="Active"/>
                  <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1}} onClick={saveEditProject}>Save</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setEditProject(null)}>Cancel</button></div>
                </Modal>
              )}
            </div>
          )}

          {/* ASSIGNMENTS */}
          {tab==="assignments"&&(
            <div>
              <div style={{fontSize:17,fontWeight:600,fontFamily:"'Bricolage Grotesque',sans-serif",color:"#12262b",marginBottom:4}}>Engineer ↔ Project Assignments</div>
              <div style={{fontSize:12,color:"#4f676d",marginBottom:16}}>Click a cell to toggle. ✅ = assigned.</div>
              <div style={{...S.card,padding:0,overflowX:"auto"}}>
                <table style={{borderCollapse:"collapse",minWidth:"100%"}}>
                  <thead>
                    <tr>
                      <th style={{...S.th,minWidth:160}}>Engineer</th>
                      {projects.map(p=>(
                        <th key={p.id} style={{...S.th,textAlign:"center",minWidth:110}}>
                          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                            <div style={{width:10,height:10,borderRadius:"50%",background:p.color}}/>
                            <span>{p.code}</span>
                            <span style={{fontSize:9,color:"#5a7278",fontWeight:400,maxWidth:90,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {engineers.map(eng=>(
                      <tr key={eng.id} style={{borderTop:"1px solid #edf2f3"}}>
                        <td style={{...S.td,fontWeight:600}}>{eng.name}<div style={{fontSize:11,color:"#4f676d"}}>{eng.username}</div></td>
                        {projects.map(p=>{
                          const assigned = eng.projects.includes(p.id);
                          return (
                            <td key={p.id} style={{...S.td,textAlign:"center",cursor:"pointer"}} onClick={()=>toggleProjectUser(eng.id,p.id)}>
                              <div style={{width:32,height:32,borderRadius:8,border:"2px solid "+(assigned?p.color:"#d5e1e4"),background:assigned?p.color+"18":"#f4f8f9",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto",transition:"all 0.15s",fontSize:16}}>
                                {assigned?"✅":""}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {engineers.length===0&&<tr><td colSpan={projects.length+1} style={{padding:30,textAlign:"center",color:"#4f676d"}}>No engineers found.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{marginTop:16,display:"flex",justifyContent:"flex-end"}}>
                <button className="hover-up" style={{...S.btn,padding:"10px 28px"}} onClick={saveAssignments}>💾 Save Assignments</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Attachments Panel ── */
function AttachmentsPanel({ inv, token, showToast, onAdd, onDelete, currentUser }) {
  const [uploading, setUploading] = useState(false);
  const hasRole = (r) => currentUser?.roles?.includes(r) || currentUser?.role===r;

  const attachments = inv.attachments || [];
  const submitterName = inv.history?.[0]?.note?.replace("Submitted by ","") || inv.history?.[0]?.by || "Engineer";
  const mainAttachment = inv.attachmentKey ? [{ key: inv.attachmentKey, name: inv.attachmentName || "Original Document", uploadedBy: submitterName, uploadedAt: inv.receivedDate||inv.date, isMain: true }] : [];
  const allAttachments = [...mainAttachment, ...attachments];

  const [preview, setPreview] = useState(null);
  const openPreview = async (a) => {
    try {
      const res = await fetch("/api/file-url?key=" + encodeURIComponent(a.key), { headers: { "Authorization": "Bearer " + token } });
      const data = await res.json();
      if (!data.url) return showToast("Could not open the file", "error");
      setPreview({ url: data.url, name: a.name || "Document", img: (a.type||"").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(a.name||"") });
    } catch(e) { showToast("Could not open the file", "error"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch("/api/invoices/" + encodeURIComponent(inv.id) + "/attachments", { method: "POST", headers: { "Authorization": "Bearer " + token }, body: formData });
      if (!res.ok) { showToast("Upload failed", "error"); setUploading(false); return; }
      const attachment = await res.json();
      onAdd(attachment);
      showToast("Attachment added!");
    } catch(e) { showToast("Cannot connect", "error"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (a) => {
    if(!window.confirm("Are you sure you want to delete \"" + a.name + "\"? This action cannot be undone.")) return;
    try {
      await fetch("/api/invoices/" + encodeURIComponent(inv.id) + "/attachments", { method: "DELETE", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token }, body: JSON.stringify({ key: a.key }) });
      onDelete(a.key);
      showToast("Attachment removed!");
    } catch(e) { showToast("Cannot connect", "error"); }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f4f8f9" }}>
      {preview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setPreview(null)}>
          <div style={{background:"#fff",borderRadius:16,width:"min(1000px,94vw)",height:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:"1px solid #edf2f3",flexShrink:0}}>
              <div style={{flex:1,fontSize:14,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📄 {preview.name}</div>
              <a href={preview.url} target="_blank" rel="noreferrer" style={{fontSize:12,color:"#0b7a87",fontWeight:600,textDecoration:"none"}}>Open in new tab ↗</a>
              <button onClick={()=>setPreview(null)} style={{background:"#f4f8f9",border:"none",color:"#4f676d",fontSize:18,cursor:"pointer",borderRadius:8,width:32,height:32}}>✕</button>
            </div>
            <div style={{flex:1,overflow:"auto",background:"#f4f8f9"}}>
              {preview.img
                ? <img src={preview.url} alt="" style={{width:"100%",display:"block"}}/>
                : <iframe src={preview.url} title="Document Preview" style={{width:"100%",height:"100%",border:"none"}}/>}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: "#4f676d", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Attachments ({allAttachments.length})</span>
        {inv.stage!=="completed" && !hasRole("engineer") && (
        <label style={{ fontSize: 11, color: "#0b7a87", fontWeight: 600, cursor: "pointer" }}>
          {uploading ? "Uploading..." : "+ Add"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={handleUpload} disabled={uploading}/>
        </label>
      )}
      </div>
      {allAttachments.length === 0 && <div style={{ fontSize: 11, color: "#5a7278", padding: "8px 0" }}>No attachments yet</div>}
      {allAttachments.map((a, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#f5fafa", borderRadius: 8, border: "1px solid rgba(11,122,135,0.15)", marginBottom: 6 }}>
          <span style={{ fontSize: 14 }}>📄</span>
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openPreview(a)}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#0b7a87", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
            <div style={{ fontSize: 10, color: "#5a7278" }}>{a.uploadedBy} · {a.uploadedAt}</div>
          </div>
          <span onClick={() => openPreview(a)} style={{ fontSize: 10, color: "#0b7a87", flexShrink: 0, cursor: "pointer", fontWeight: 600 }}>👁 Preview</span>
          {(()=>{
            const isFiled = inv.stage==="completed";
            const isAdmin = hasRole("admin");
            const isDC = hasRole("dc");
            const isUploader = a.uploadedBy===currentUser?.name;
            const isMainAttachment = a.isMain;
            const canDelete = !isFiled && (isAdmin || (!isMainAttachment && isUploader) || (isMainAttachment && (isAdmin||isDC)));
            return canDelete ? <button onClick={() => handleDelete(a)} style={{ background: "none", border: "1px solid #f3c6c1", borderRadius: 6, color: "#b42318", cursor: "pointer", fontSize: 11, fontWeight: 700, padding: "2px 8px", flexShrink: 0, marginLeft: 4 }}>✕</button> : null;
          })()}
        </div>
      ))}
    </div>
  );
}

/* ── Clickable document link: opens the file in an in-app viewer ── */
function DocPreviewLink({ fileKey, name, type, token }) {
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(false);
  const open = async (e) => {
    e.stopPropagation(); setErr(false);
    try {
      const res = await fetch("/api/file-url?key=" + encodeURIComponent(fileKey), { headers: { "Authorization": "Bearer " + token } });
      const data = await res.json();
      if (!data.url) return setErr(true);
      setPreview({ url: data.url, img: (type||"").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(name||"") });
    } catch(_) { setErr(true); }
  };
  if (!fileKey) return <span style={{color:"#5a7278",fontSize:12}}>—</span>;
  return (
    <>
      <span onClick={open} title={name} style={{display:"inline-flex",alignItems:"center",gap:5,cursor:"pointer",color:"#0b7a87",fontWeight:600,fontSize:12,maxWidth:200}}>
        <span style={{fontSize:15}}>{(type||"").startsWith("image/")?"🖼️":"📄"}</span>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name||"View"}</span>
        {err&&<span style={{color:"#b42318",fontSize:11}}>(could not open)</span>}
      </span>
      {preview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={(e)=>{e.stopPropagation();setPreview(null);}}>
          <div style={{background:"#fff",borderRadius:16,width:"min(1000px,94vw)",height:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:"1px solid #edf2f3",flexShrink:0}}>
              <div style={{flex:1,fontSize:14,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📄 {name}</div>
              <a href={preview.url} target="_blank" rel="noreferrer" style={{fontSize:12,color:"#0b7a87",fontWeight:600,textDecoration:"none"}}>Open in new tab ↗</a>
              <button onClick={()=>setPreview(null)} style={{background:"#f4f8f9",border:"none",color:"#4f676d",fontSize:18,cursor:"pointer",borderRadius:8,width:32,height:32}}>✕</button>
            </div>
            <div style={{flex:1,overflow:"auto",background:"#f4f8f9"}}>
              {preview.img ? <img src={preview.url} alt="" style={{width:"100%",display:"block"}}/> : <iframe src={preview.url} title="Document Preview" style={{width:"100%",height:"100%",border:"none"}}/>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Sub File Preview ── */
function SubFilePreview({ sub, token }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{
    if(!sub?.fileKey) { setLoading(false); return; }
    fetch("/api/file-url?key="+encodeURIComponent(sub.fileKey),{headers:{"Authorization":"Bearer "+token}})
      .then(r=>r.json()).then(d=>{ setUrl(d.url); setLoading(false); }).catch(()=>setLoading(false));
  },[sub?.fileKey, token]);
  if(loading) return <div style={{padding:40,textAlign:"center",color:"#4f676d"}}>⏳ Loading preview...</div>;
  if(!url) return <div style={{padding:20,textAlign:"center",color:"#b42318"}}>Could not load file</div>;
  if(sub.fileType?.startsWith("image/")) return <img src={url} alt="" style={{width:"100%",borderRadius:10,border:"1px solid #d5e1e4"}}/>;
  return <iframe src={url} style={{width:"100%",height:"70vh",borderRadius:10,border:"1px solid #d5e1e4"}} title="Document Preview"/>;
}

/* ── Small thumbnail for list rows (Intake, etc.) — same presigned-URL fetch, sized down ── */
function SubFileThumb({ sub, token, size }) {
  // Generic file-type icon only - the document itself is shown on the review screen
  const w = size || 52;
  const isPdf = sub?.fileType==="application/pdf" || /\.pdf$/i.test(sub?.fileName||"");
  const isImg = (sub?.fileType||"").startsWith("image/");
  const label = isPdf ? "PDF" : isImg ? "IMG" : "FILE";
  const color = isPdf ? "#d93025" : isImg ? "#1a73e8" : "#5a7278";
  return (
    <svg width={w} height={Math.round(w*1.25)} viewBox="0 0 48 60" style={{flexShrink:0}} role="img" aria-label={label+" file"}>
      <path d="M6 2h24l12 12v42a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#fff" stroke="#c9d6d9" strokeWidth="2"/>
      <path d="M30 2v10a2 2 0 0 0 2 2h10z" fill="#e3ebed" stroke="#c9d6d9" strokeWidth="2" strokeLinejoin="round"/>
      <rect x="4" y="30" width="32" height="15" rx="2" fill={color}/>
      <text x="20" y="41" textAnchor="middle" fontSize="10" fontWeight="800" fill="#fff" fontFamily="Arial, Helvetica, sans-serif">{label}</text>
    </svg>
  );
}

/* ══════════════════════════════════
   MAIN APP
══════════════════════════════════ */
export default function App() {
  const [users, setUsers]       = useState(DEFAULT_USERS);
  const [stages, setStages]     = useState(DEFAULT_STAGES);
  const [projects, setProjects] = useState(DEFAULT_PROJECTS);
  const [invoices, setInvoices] = useState(INIT_INV);
  const [vouchers, setVouchers] = useState(INIT_VOUCHERS);
  const [pendingSubs, setPendingSubs] = useState(INIT_SUBMISSIONS);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [auditLog, setAuditLog] = useState([]);
  const [lastSync, setLastSync] = useState(new Date().toISOString());
  const [alertRules, setAlertRules] = useState([
    { id:"r1", active:true,  type:"stuck",     threshold:7,     stage:"all",       label:"Any invoice stuck > 7 days" },
    { id:"r2", active:true,  type:"amount",    threshold:50000, stage:"all",       label:"Invoice amount exceeds AED 50,000" },
    { id:"r3", active:false, type:"overdue",   threshold:0,     stage:"all",       label:"Invoice past due date" },
  ]);
  const [currentUser, setCurrentUser] = useState(()=>{
    try { const u=localStorage.getItem("user"); return u?JSON.parse(u):null; } catch{ return null; }
  });
  // A user can now hold multiple roles (set by Admin). hasRole checks the full roles array;
  // falls back to the single `role` field for any session predating multi-role support.
  const hasRole = useCallback((r)=> currentUser?.roles?.includes(r) || currentUser?.role===r, [currentUser]);
  // Pure-admin (no other role) has no personal queue. Admin combined with another role (e.g. Admin+Accountant) keeps one.
  const isPureAdmin = useMemo(()=>{
    const set = currentUser?.roles?.length ? currentUser.roles : (currentUser?.role?[currentUser.role]:[]);
    return set.includes("admin") && set.filter(r=>r!=="admin").length===0;
  },[currentUser]);
  const getInitialView = () => {
    const saved = localStorage.getItem("lastView");
    const user = JSON.parse(localStorage.getItem("user")||"{}");
    const userIsAdmin = user?.roles?.includes("admin") || user?.role==="admin";
    const defaultView = userIsAdmin ? "dashboard" : "myqueue";
    if(saved==="detail" && localStorage.getItem("lastDetailId")) return "detail";
    // Only these views are safe to restore directly on a fresh page load —
    // anything that depends on a selected invoice/voucher/submission object (which is never
    // persisted) would otherwise render blank until the user manually clicks a nav tab.
    const restorableViews = ["dashboard","myqueue","vouchers","intake","reports","archive","suppliers","submitInvoice","mySubmissions","addInv","createVoucher"];
    if(saved && restorableViews.includes(saved)) return saved;
    return defaultView;
  };
  const [view, setView] = useState(getInitialView);
  const [selected, setSelected] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [toast, setToast]       = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const [search, setSearch]             = useState("");
  const [filterStage, setFilterStage]   = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [queueChip, setQueueChip] = useState("all");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(()=>localStorage.getItem("sidebarCollapsed")==="1");
  useEffect(()=>{ localStorage.setItem("sidebarCollapsed", sidebarCollapsed?"1":"0"); },[sidebarCollapsed]);

  const [moveStage, setMoveStage] = useState("");
  const [moveNote, setMoveNote]   = useState("");
  const [movePvRef, setMovePvRef] = useState("");
  const [moveTab, setMoveTab]     = useState("forward");

  const [vMoveStage, setVMoveStage] = useState("");
  const [vMoveNote, setVMoveNote]   = useState("");
  const [vMoveTab, setVMoveTab]     = useState("forward");

  const [vForm, setVForm]         = useState({no:"",date:todayStr()});
  const [vSelected, setVSelected] = useState([]);
  const [pvQuery, setPvQuery] = useState("");
  const [pvSearched, setPvSearched] = useState("");

  const [editingInv, setEditingInv]   = useState(null);
  const [editingHist, setEditingHist] = useState(null);
  const [histEdit, setHistEdit]       = useState({stage:"",date:"",note:"",by:""});
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileNumInput, setFileNumInput]   = useState("");
  const [pendingMove, setPendingMove]     = useState(null);
  const [showVFileModal, setShowVFileModal] = useState(false);
  const [vFileNums, setVFileNums]         = useState({});
  const [showPVModal, setShowPVModal]     = useState(null);
  const [pvModalForm, setPvModalForm]     = useState({no:"",date:todayStr()});
  const [invForm, setInvForm]             = useState(EMPTY_INV_FORM);
  const [invFile, setInvFile]             = useState(null);
  const [submitting, setSubmitting]       = useState(false);
  const [submitForm, setSubmitForm]       = useState({site:"",projectId:"",file:null,invoiceNo:"",lpo:"",supplierGuess:"",amountGuess:"",note:""});
  const [engProjFilter, setEngProjFilter] = useState("all");

  const [showDocModal, setShowDocModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [recycleBin, setRecycleBin] = useState({invoices:[], vouchers:[]});
  const [suppliers, setSuppliers] = useState([]);
  const [deletedInvoiceIds, setDeletedInvoiceIds] = useState([]);
  const [pendingDeletions, setPendingDeletions] = useState({invoices:[], vouchers:[]});

  // ── Pending Deletion Notification for CFO ──
  const pendingDeletionCount = pendingDeletions.invoices.length + pendingDeletions.vouchers.length;

  // ── Alert Checker ──
  useEffect(()=>{
    const checkAlerts = () => {
      if(!alertRules.length||!invoices.length) return;
      const now = new Date();
      const triggered = [];
      alertRules.forEach(rule=>{
        if(!rule.active) return;
        invoices.forEach(inv=>{
          if(inv.stage==="completed") return;
          if(rule.stage!=="all" && inv.stage!==rule.stage) return;
          const lastHist = inv.history?.length>0 ? inv.history[inv.history.length-1] : null;
          const lastDate = lastHist?.date ? new Date(lastHist.date) : now;
          const daysSince = Math.floor((now-lastDate)/(1000*60*60*24));
          if(rule.type==="stuck" && daysSince>=Number(rule.threshold)){
            triggered.push({id:inv.id+"-stuck-"+rule.id, type:"stuck", msg:inv.id+" ("+inv.supplier+") stuck for "+daysSince+" days at "+inv.stage, invId:inv.id});
          } else if(rule.type==="amount" && Number(inv.amount)>=Number(rule.threshold)){
            triggered.push({id:inv.id+"-amount-"+rule.id, type:"amount", msg:inv.id+" ("+inv.supplier+") exceeds AED "+Number(rule.threshold).toLocaleString(), invId:inv.id});
          } else if(rule.type==="overdue" && inv.dueDate && new Date(inv.dueDate)<now){
            triggered.push({id:inv.id+"-overdue-"+rule.id, type:"overdue", msg:inv.id+" ("+inv.supplier+") payment overdue since "+inv.dueDate, invId:inv.id});
          } else if(rule.type==="urgent_unactioned" && inv.urgent){
            const hoursSince = Math.floor((now-lastDate)/(1000*60*60));
            if(hoursSince>=Number(rule.threshold)) triggered.push({id:inv.id+"-urgent-"+rule.id, type:"urgent_unactioned", msg:inv.id+" ("+inv.supplier+") urgent and unactioned for "+hoursSince+" hours", invId:inv.id});
          } else if(rule.type==="no_pv" && inv.stage==="dc_return" && !inv.voucherNo){
            if(daysSince>=Number(rule.threshold)) triggered.push({id:inv.id+"-nopv-"+rule.id, type:"no_pv", msg:inv.id+" ("+inv.supplier+") at DC return with no PV for "+daysSince+" days", invId:inv.id});
          }
        });
      });
      setNotifications(triggered);
    };
    checkAlerts();
    const interval = setInterval(checkAlerts, 60000);
    return () => clearInterval(interval);
  }, [alertRules, invoices]);

  // ── Fetch all data from backend on load ──
  const loadAllData = async (token, light) => {
    const h = {"Authorization":"Bearer "+token,"Cache-Control":"no-cache"};
    try {
      if(light){
        // Lightweight refresh: only fast-changing operational data
        const [invoicesRes, vouchersRes, subsRes, deletedRes] = await Promise.all([
          fetch("/api/invoices",{headers:h,cache:"no-store"}),
          fetch("/api/vouchers",{headers:h,cache:"no-store"}),
          fetch("/api/submissions",{headers:h,cache:"no-store"}),
          fetch("/api/deleted-invoice-ids",{headers:h,cache:"no-store"}),
        ]);
        if(invoicesRes.status===401){ localStorage.removeItem("token"); localStorage.removeItem("user"); setCurrentUser(null); return; }
        const [invoices,vouchers,subs,deletedIds] = await Promise.all([
          invoicesRes.ok?invoicesRes.json():[], vouchersRes.ok?vouchersRes.json():[], subsRes.ok?subsRes.json():[], deletedRes.ok?deletedRes.json():[],
        ]);
        if(Array.isArray(deletedIds)) setDeletedInvoiceIds(deletedIds);
        if(Array.isArray(invoices)) setInvoices(invoices.filter(i=>!i.deleted).map(i=>({
          ...i,
          amount:parseFloat(i.amount)||0,
          attachments:i.attachments||[],
          projectId:i.project_id||i.projectId||"",
          receivedDate:i.received_date||i.receivedDate||"",
          paymentMethod:i.payment_method||i.paymentMethod||"",
          paymentType:i.payment_type||i.paymentType||"",
          paymentDays:i.payment_days||i.paymentDays||0,
          paymentPercent:i.payment_percent||i.paymentPercent||"",
          dueDate:i.due_date||i.dueDate||"",
          voucherNo:i.voucher_no||i.voucherNo||null,
          pvRef:i.pv_ref||i.pvRef||"",
          fileNumber:i.file_number||i.fileNumber||"",
          attachmentKey:i.attachment_key||i.attachmentKey||"",
          attachmentName:i.attachment_name||i.attachmentName||"",
          deletion_status:i.deletion_status||null,
          deletionRequestedBy:i.deletion_requested_by||i.deletionRequestedBy||null,
          deletionNote:i.deletion_note||i.deletionNote||null,
        })));
        if(Array.isArray(vouchers)) setVouchers(vouchers.filter(v=>!v.deleted));
        if(Array.isArray(subs)) setPendingSubs(subs);
        setLastSync(new Date().toISOString());
        return;
      }
      const [usersRes, projectsRes, invoicesRes, vouchersRes, subsRes, auditRes, alertsRes, stagesRes, suppliersRes, deletedRes] = await Promise.all([
        fetch("/api/users",{headers:h,cache:"no-store"}),
        fetch("/api/projects",{headers:h,cache:"no-store"}),
        fetch("/api/invoices",{headers:h,cache:"no-store"}),
        fetch("/api/vouchers",{headers:h,cache:"no-store"}),
        fetch("/api/submissions",{headers:h,cache:"no-store"}),
        fetch("/api/audit",{headers:h,cache:"no-store"}),
        fetch("/api/alerts",{headers:h,cache:"no-store"}),
        fetch("/api/stages",{headers:h,cache:"no-store"}),
        fetch("/api/suppliers",{headers:h,cache:"no-store"}),
        fetch("/api/deleted-invoice-ids",{headers:h,cache:"no-store"}),
      ]);
      if(usersRes.status===401){ localStorage.removeItem("token"); localStorage.removeItem("user"); setCurrentUser(null); return; }
      const [users,projects,invoices,vouchers,subs,audit,alerts,stagesData,suppliersData,deletedIds] = await Promise.all([
        usersRes.ok?usersRes.json():[], projectsRes.ok?projectsRes.json():[],
        invoicesRes.ok?invoicesRes.json():[], vouchersRes.ok?vouchersRes.json():[],
        subsRes.ok?subsRes.json():[], auditRes.ok?auditRes.json():[],
        alertsRes.ok?alertsRes.json():[], stagesRes.ok?stagesRes.json():[],
        suppliersRes.ok?suppliersRes.json():[], deletedRes.ok?deletedRes.json():[],
      ]);
      if(Array.isArray(deletedIds)) setDeletedInvoiceIds(deletedIds);
      if(Array.isArray(users)&&users.length>0) setUsers(users);
      if(Array.isArray(projects)) setProjects(projects);
      if(Array.isArray(invoices)) setInvoices(invoices.filter(i=>!i.deleted).map(i=>({
        ...i,
        amount:parseFloat(i.amount)||0,
        attachments:i.attachments||[],
        projectId:i.project_id||i.projectId||"",
        receivedDate:i.received_date||i.receivedDate||"",
        paymentMethod:i.payment_method||i.paymentMethod||"",
        paymentType:i.payment_type||i.paymentType||"",
        paymentDays:i.payment_days||i.paymentDays||0,
        paymentPercent:i.payment_percent||i.paymentPercent||"",
        dueDate:i.due_date||i.dueDate||"",
        voucherNo:i.voucher_no||i.voucherNo||null,
        pvRef:i.pv_ref||i.pvRef||"",
        fileNumber:i.file_number||i.fileNumber||"",
        attachmentKey:i.attachment_key||i.attachmentKey||"",
        attachmentName:i.attachment_name||i.attachmentName||"",
        deletion_status:i.deletion_status||null,
        deletionRequestedBy:i.deletion_requested_by||i.deletionRequestedBy||null,
        deletionNote:i.deletion_note||i.deletionNote||null,
        invoiceNo:i.invoiceNo||i.id,
        supplierPending:!!i.supplierPending,
        submittedSupplier:i.submittedSupplier||"",
      })));
      if(Array.isArray(vouchers)) setVouchers(vouchers.filter(v=>!v.deleted));
      if(Array.isArray(subs)) setPendingSubs(subs);
      if(Array.isArray(audit)) setAuditLog(audit.map(r=>({id:r.id,ts:r.ts,user:r.username,role:r.role,action:r.action,detail:r.detail})));
      if(Array.isArray(alerts)&&alerts.length>0) setAlertRules(alerts.map(r=>({id:r.id,type:r.type,threshold:r.threshold,stage:r.stage,active:r.active,label:r.label})));
      if(Array.isArray(stagesData)&&stagesData.length>0) setStages(stagesData);
      if(Array.isArray(suppliersData)) setSuppliers(suppliersData);
      setLastSync(new Date().toISOString());
    } catch(e) { console.error("Load error:", e); }
  };

  useEffect(()=>{
    const token = localStorage.getItem("token");
    if(!token) return;
    loadAllData(token).then(()=>{
      const lastId = localStorage.getItem("lastDetailId");
      if(lastId && localStorage.getItem("lastView")==="detail"){
        setInvoices(p=>{
          const found = p.find(i=>i.id===lastId);
          if(found){
            setSelected(found);
          } else {
            // The invoice this was pointing to no longer exists — fall back instead of showing a blank screen
            localStorage.removeItem("lastDetailId");
            const user = JSON.parse(localStorage.getItem("user")||"{}");
            const fallback = user?.role==="engineer"?"submitInvoice":user?.role==="admin"?"dashboard":user?.role==="pd"?"intake":"myqueue";
            localStorage.setItem("lastView",fallback);
            setView(fallback);
          }
          return p;
        });
      }
    });
  }, []);

  // ── Keep mouse/keyboard back-forward navigation inside the app instead of leaving it ──
  const poppingRef = useRef(false);
  const navRef = useRef<any>({});
  // Snapshot of where the app is, stored in every browser-history entry, so mouse back/forward
  // can restore the selected invoice / voucher / submission instead of landing on a blank screen.
  const navSnapshot = (v) => {
    const r = navRef.current;
    const snap: any = { view: v, prevView: r.prevView };
    if(v==="detail" && r.selected && r.selected.id){ snap.selType="inv"; snap.selId=r.selected.id; }
    else if(v==="voucherDetail" && r.selected && r.selected.no){ snap.selType="pv"; snap.selId=r.selected.no; }
    else if(v==="processSubmission" && r.selectedSub){ snap.selType="sub"; snap.selId=r.selectedSub.id; }
    return snap;
  };
  useEffect(()=>{
    window.history.replaceState(navSnapshot(view), "", "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(()=>{
    if(poppingRef.current){ poppingRef.current = false; return; }
    window.history.pushState(navSnapshot(view), "", "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  useEffect(()=>{
    const onPop = e => {
      const st = e.state;
      if(st && st.view){
        const r = navRef.current;
        const u = r.user || {};
        const roleSet = u.roles && u.roles.length ? u.roles : [u.role];
        const safeView = roleSet.includes("engineer") ? "submitInvoice" : roleSet.includes("admin") ? "dashboard" : roleSet.includes("pd") ? "intake" : "myqueue";
        let target = st.view;
        if(st.selType==="inv"){
          const inv = (r.invoices||[]).find(i=>i.id===st.selId);
          if(inv) setSelected(inv); else target = safeView;
        } else if(st.selType==="pv"){
          const pv = (r.vouchers||[]).find(v=>v.no===st.selId);
          if(pv) setSelected(pv); else target = safeView;
        } else if(st.selType==="sub"){
          const sub = (r.pendingSubs||[]).find(x=>x.id===st.selId);
          if(sub) setSelectedSub(sub); else target = safeView;
        } else if(["detail","voucherDetail","processSubmission"].includes(st.view)){
          target = safeView;
        }
        if(st.prevView) setPrevView(st.prevView);
        if(target!==r.view){ poppingRef.current = true; setView(target); }
      } else {
        // Nothing more to go back to inside the app — stay put instead of leaving it
        window.history.pushState(navSnapshot(view), "", "");
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ── Escape closes whichever overlay is currently open ──
  useEffect(()=>{
    const onKey = e => {
      if(e.key!=="Escape") return;
      if(showSignOutModal) setShowSignOutModal(false);
      else if(showDocModal) setShowDocModal(false);
      else if(showPVModal) setShowPVModal(null);
      else if(showVFileModal) setShowVFileModal(false);
      else if(showFileModal) setShowFileModal(false);
      else if(editingHist) setEditingHist(null);
      else if(editingInv) setEditingInv(null);
      else if(showNotifDropdown) setShowNotifDropdown(false);
      else if(showAdmin) setShowAdmin(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSignOutModal, showDocModal, showPVModal, showVFileModal, showFileModal, editingHist, editingInv, showNotifDropdown, showAdmin]);

  // ── Auto-refresh: fast operational data every 10s, full data (users/projects/stages/etc) every 60s ──
  useEffect(()=>{
    const token = localStorage.getItem("token");
    if(!token||!currentUser) return;
    const fastInterval = setInterval(()=>{ loadAllData(token, true); }, 10000);
    const fullInterval = setInterval(()=>{ loadAllData(token, false); }, 60000);
    return () => { clearInterval(fastInterval); clearInterval(fullInterval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(()=>{
    const token = localStorage.getItem("token");
    if(!token||!currentUser) return;
    if(["dashboard","myqueue","intake","vouchers","filed"].includes(view)){
      loadAllData(token);
    }
    // Refresh pending deletions count for CFO/admin/dc
    if(hasRole("cfo")||hasRole("admin")||hasRole("dc")){
      loadPendingDeletions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Auto-refresh pending deletions every 30s for CFO
  useEffect(()=>{
    if(!currentUser) return;
    if(!hasRole("cfo")&&!hasRole("admin")&&!hasRole("dc")) return;
    const interval = setInterval(()=>loadPendingDeletions(), 30000);
    return ()=>clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const showToast = useCallback((msg,type)=>{ setToast({msg,type:type||"success"}); setTimeout(()=>setToast(null),3200); },[]);

  const softDeleteInvoice = async (inv) => {
    if(!hasRole("admin")&&!hasRole("dc")) return showToast("No permission","error");
    if(isLockedInPV(inv,vouchers)) return showToast("This invoice is part of PV "+inv.voucherNo+". Dissolve the voucher first before deleting.","error");
    const note = window.prompt("Reason for deletion request (required):");
    if(!note||!note.trim()) return;
    const token=localStorage.getItem("token");
    const res = await fetch("/api/invoices/"+encodeURIComponent(inv.id)+"/request-delete",{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
      body:JSON.stringify({note:note.trim()})
    });
    if(res.ok){
      setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,deletion_status:"pending",deletionRequestedBy:currentUser.name,deletionNote:note.trim()}));
      showToast("Deletion request sent to CFO!");
      try { logAudit("Delete Requested", inv.id+" | "+inv.supplier+" | Reason: "+note.trim()); } catch(_){}
    } else showToast("Failed to send request","error");
  };

  const softDeleteVoucher = async (v) => {
    if(!hasRole("admin")&&!hasRole("dc")) return showToast("No permission","error");
    const note = window.prompt("Reason for deletion request (required):");
    if(!note||!note.trim()) return;
    const token=localStorage.getItem("token");
    const res = await fetch("/api/vouchers/"+encodeURIComponent(v.no)+"/request-delete",{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
      body:JSON.stringify({note:note.trim()})
    });
    if(res.ok){ showToast("Deletion request sent to CFO!"); try { logAudit("Delete Requested", v.no+" | "+v.supplier+" | Reason: "+note.trim()); } catch(_){} }
    else showToast("Failed to send request","error");
  };

  const addSupplier = async (name) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/suppliers",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({name})});
      const data = await res.json();
      if(!res.ok) { showToast(data.error||"Failed to add supplier","error"); return null; }
      setSuppliers(p=>[...p,data].sort((a,b)=>a.name.localeCompare(b.name)));
      showToast("Supplier added!");
      try { logAudit("Supplier Added", data.name+" (id: "+data.id+")"); } catch(_){}
      return data;
    } catch(e) { showToast("Cannot connect to server","error"); return null; }
  };

  const deleteSupplier = async (id) => {
    if(!["admin","dc","accountant"].some(hasRole)) return showToast("No permission","error");
    if(!window.confirm("Remove this supplier from the list?")) return;
    const token = localStorage.getItem("token");
    const sup = suppliers.find(s=>s.id===id);
    try {
      const res = await fetch("/api/suppliers/"+encodeURIComponent(id),{method:"DELETE",headers:{"Authorization":"Bearer "+token}});
      if(!res.ok) return showToast("Failed to remove supplier","error");
      setSuppliers(p=>p.filter(s=>s.id!==id));
      showToast("Supplier removed!");
      try { logAudit("Supplier Removed", (sup?.name||id)+" (id: "+id+")"); } catch(_){}
    } catch(e) { showToast("Cannot connect to server","error"); }
  };

  const loadRecycleBin = async () => {
    const token=localStorage.getItem("token");
    const res = await fetch("/api/recycle-bin",{headers:{"Authorization":"Bearer "+token}});
    if(res.ok){ const data=await res.json(); setRecycleBin(data); }
  };

  const loadPendingDeletions = async () => {
    const token=localStorage.getItem("token");
    const res = await fetch("/api/pending-deletions",{headers:{"Authorization":"Bearer "+token}});
    if(res.ok){ const data=await res.json(); setPendingDeletions(data); }
  };

  const approveDelete = async (id, itemType, requestType) => {
    const token=localStorage.getItem("token");
    let url;
    if(requestType==="pending_restore"){
      url = itemType==="invoice" ? "/api/invoices/"+encodeURIComponent(id)+"/restore" : "/api/vouchers/"+encodeURIComponent(id)+"/restore";
    } else {
      url = itemType==="invoice" ? "/api/invoices/"+encodeURIComponent(id)+"/approve-delete" : "/api/vouchers/"+encodeURIComponent(id)+"/approve-delete";
    }
    await fetch(url,{method:"PATCH",headers:{"Authorization":"Bearer "+token}});
    if(requestType==="pending_restore"){
      setRecycleBin(p=>({
        invoices: itemType==="invoice"?p.invoices.filter(i=>i.id!==id):p.invoices,
        vouchers: itemType==="voucher"?p.vouchers.filter(v=>v.no!==id):p.vouchers
      }));
      loadAllData(token);
      showToast("Restore approved!");
    } else {
      if(itemType==="invoice") setInvoices(p=>p.filter(i=>i.id!==id));
      else setVouchers(p=>p.filter(v=>v.no!==id));
      showToast("Deletion approved — moved to Recycle Bin!");
    }
    setPendingDeletions(p=>({
      invoices: itemType==="invoice"?p.invoices.filter(i=>i.id!==id):p.invoices,
      vouchers: itemType==="voucher"?p.vouchers.filter(v=>v.no!==id):p.vouchers
    }));
    // Clear deletion_status from vouchers/invoices state
    if(requestType==="pending_restore"){
      setVouchers(p=>p.map(v=>v.no!==id?v:{...v,deletion_status:null}));
      setInvoices(p=>p.map(i=>i.id!==id?i:{...i,deletion_status:null}));
    }
    try { logAudit(requestType==="pending_restore"?"Restore Approved":"Delete Approved", id); } catch(_){}
    const tok=localStorage.getItem("token");
    loadAllData(tok);
    loadRecycleBin();
    loadPendingDeletions();
  };

  const rejectDelete = async (id, itemType) => {
    const note = window.prompt("Reason for rejecting this request (required):");
    if(!note||!note.trim()) return;
    const token=localStorage.getItem("token");
    const url = itemType==="invoice" ? "/api/invoices/"+encodeURIComponent(id)+"/reject-delete" : "/api/vouchers/"+encodeURIComponent(id)+"/reject-delete";
    await fetch(url,{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({note:note.trim()})});
    setPendingDeletions(p=>({
      invoices: itemType==="invoice"?p.invoices.filter(i=>i.id!==id):p.invoices,
      vouchers: itemType==="voucher"?p.vouchers.filter(v=>v.no!==id):p.vouchers
    }));
    setInvoices(p=>p.map(i=>i.id!==id?i:{...i,deletion_status:null}));
    setVouchers(p=>p.map(v=>v.no!==id?v:{...v,deletion_status:null}));
    try { logAudit("Request Rejected", id+" | Reason: "+note.trim()); } catch(_){}
    showToast("Request rejected!");
    loadAllData(localStorage.getItem("token"));
    loadPendingDeletions();
  };

  const directRestoreInvoice = async (id) => {
    if(!hasRole("cfo")) return showToast("No permission","error");
    const note = window.prompt("Note for direct restore (optional):")||"";
    const token=localStorage.getItem("token");
    const res = await fetch("/api/invoices/"+encodeURIComponent(id)+"/direct-restore",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({note})});
    if(res.ok){
      setRecycleBin(p=>({...p,invoices:p.invoices.filter(i=>i.id!==id)}));
      try { logAudit("Restored Directly by CFO", id+(note?" | Note: "+note:"")); } catch(_){}
      loadAllData(token);
      showToast("Invoice restored!");
    } else showToast("Failed to restore","error");
  };

  const directRestoreVoucher = async (no) => {
    if(!hasRole("cfo")) return showToast("No permission","error");
    const note = window.prompt("Note for direct restore (optional):")||"";
    const token=localStorage.getItem("token");
    const res = await fetch("/api/vouchers/"+encodeURIComponent(no)+"/direct-restore",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({note})});
    if(res.ok){
      setRecycleBin(p=>({...p,vouchers:p.vouchers.filter(v=>v.no!==no)}));
      try { logAudit("Restored Directly by CFO", no+(note?" | Note: "+note:"")); } catch(_){}
      loadAllData(token);
      showToast("Voucher restored!");
    } else showToast("Failed to restore","error");
  };

  const requestRestoreInvoice = async (id) => {
    if(!hasRole("admin")) return showToast("No permission","error");
    const note = window.prompt("Reason for restore request (required):");
    if(!note||!note.trim()) return;
    const token=localStorage.getItem("token");
    const res = await fetch("/api/invoices/"+encodeURIComponent(id)+"/request-restore",{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
      body:JSON.stringify({note:note.trim()})
    });
    if(res.ok){
      setRecycleBin(p=>({...p,invoices:p.invoices.map(i=>i.id!==id?i:{...i,deletion_status:"pending_restore",restoreRequestedBy:currentUser.name,restoreNote:note.trim()})}));
      showToast("Restore request sent to CFO!");
      try { logAudit("Restore Requested", id+" | Reason: "+note.trim()); } catch(_){}
    } else showToast("Failed to send request","error");
  };





  const requestRestoreVoucher = async (no) => {
    if(!hasRole("admin")) return showToast("No permission","error");
    const note = window.prompt("Reason for restore request (required):");
    if(!note||!note.trim()) return;
    const token=localStorage.getItem("token");
    const res = await fetch("/api/vouchers/"+encodeURIComponent(no)+"/request-restore",{
      method:"PATCH",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
      body:JSON.stringify({note:note.trim()})
    });
    if(res.ok){
      setRecycleBin(p=>({...p,vouchers:p.vouchers.map(v=>v.no!==no?v:{...v,deletion_status:"pending_restore",restoreRequestedBy:currentUser.name,restoreNote:note.trim()})}));
      showToast("Restore request sent to CFO!");
      try { logAudit("Restore Requested", no+" | Reason: "+note.trim()); } catch(_){}
    } else showToast("Failed to send request","error");
  };





  const logAudit = useCallback((action, detail) => {
    if(!currentUser) return;
    const token = localStorage.getItem("token");
    const entry = {
      id: "a"+Math.random().toString(36).slice(2,9),
      ts: new Date().toISOString().slice(0,16).replace("T"," "),
      username: currentUser.name,
      role: currentUser.role,
      action, detail
    };
    setAuditLog(p=>[{...entry, user:entry.username},...p]);
    fetch("/api/audit",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify(entry)}).catch(()=>{});
  },[currentUser]);

  const sortedStages = useMemo(()=>[...stages].sort((a,b)=>a.order-b.order),[stages]);
  const stageIds     = useMemo(()=>sortedStages.map(s=>s.id),[sortedStages]);

  const canAct = useCallback((sid)=>{
    if(!currentUser) return false;
    const st = sortedStages.find(s=>s.id===sid);
    return st && hasRole(st.ownerRole);
  },[currentUser, sortedStages, hasRole]);

  const freeInvoices = useMemo(()=>invoices.filter(i=>!isLockedInPV(i,vouchers)),[invoices,vouchers]);

  const filtered = useMemo(()=>freeInvoices.filter(inv=>{
    const q=search.toLowerCase();
    return (!q||[inv.id,inv.supplier,inv.lpo||""].some(x=>x.toLowerCase().includes(q)))
      && (filterStage==="all"||inv.stage===filterStage)
      && (filterProject==="all"||inv.projectId===filterProject);
  }),[freeInvoices,search,filterStage,filterProject]);

  const stageCounts  = useMemo(()=>sortedStages.reduce((a,s)=>({...a,[s.id]:invoices.filter(i=>i.stage===s.id).length}),{}),[invoices,sortedStages]);
  const archivedInvs = useMemo(()=>invoices.filter(i=>i.stage==="completed"),[invoices]);
  const [archQ, setArchQ] = useState("");
  const archShown = (()=>{
    const q=archQ.trim().toLowerCase();
    if(!q) return archivedInvs;
    return archivedInvs.filter(i=>{
      const proj=projects.find(p=>p.id===i.projectId);
      return [i.id,i.invoiceNo,i.supplier,i.lpo,i.pvRef,i.fileNumber,proj&&proj.name,String(i.amount)].some(t=>String(t||"").toLowerCase().includes(q));
    });
  })();
  const urgentCount  = useMemo(()=>invoices.filter(i=>i.urgent&&i.stage!=="completed").length,[invoices]);

  const myQueueFree = useMemo(()=>freeInvoices
    .filter(inv=>inv.stage!=="completed"&&canAct(inv.stage)&&!inv.deletion_status&&!isPureAdmin)
    .map(inv=>({...inv,_days:daysDiff(inv.history?.[inv.history.length-1]?.date||inv.receivedDate)}))
    .sort((a,b)=>{
      // Priority first, then urgent, then oldest first (FILO)
      if(a.priority&&!b.priority) return -1;
      if(!a.priority&&b.priority) return 1;
      if(a.urgent&&!b.urgent) return -1;
      if(!a.urgent&&b.urgent) return 1;
      return new Date(a.receivedDate)-new Date(b.receivedDate);
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ,[freeInvoices,canAct,currentUser,isPureAdmin]);

  const myQueueVouchers = useMemo(()=>{
    if(isPureAdmin) return [];
    const seen=new Set();
    return vouchers.filter(v=>{
      if(v.deletion_status) return false;
      const vI=invoices.filter(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed"&&!i.deletion_status);
      if(!vI.length) return false;
      const minIdx=Math.min(...vI.map(i=>stageIds.indexOf(i.stage)));
      return canAct(stageIds[minIdx]);
    }).filter(v=>{ if(seen.has(v.no))return false; seen.add(v.no); return true; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[vouchers,invoices,canAct,stageIds,currentUser,isPureAdmin]);

  const pendingIntakeCount = useMemo(()=>pendingSubs.filter(s=>s.status==="pending").length,[pendingSubs]);

  const vCurStage = v => {
    const vI=invoices.filter(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed");
    if(!vI.length) return null;
    return stageIds[Math.min(...vI.map(i=>stageIds.indexOf(i.stage)))];
  };
  const vNext = v => { const c=vCurStage(v); if(!c)return[]; const i=stageIds.indexOf(c); return sortedStages.slice(i+1,i+2); };
  const vPrev = v => { const c=vCurStage(v); if(!c)return[]; const i=stageIds.indexOf(c); return sortedStages.slice(0,i); };

  const addHist = (inv,stage,note,by,extra) => ({...inv,...(extra||{}),stage,history:[...inv.history,{stage,date:todayStr(),note:note||"",by:by||currentUser.name}]});

  const syncInvoiceStage = async (inv, updated) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/invoices/"+encodeURIComponent(inv.id)+"/stage", {
        method:"PATCH",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body:JSON.stringify({stage:updated.stage, history:updated.history, voucherNo:updated.voucherNo||null, pvRef:updated.pvRef||"", fileNumber:updated.fileNumber||""})
      });
      return res.ok;
    } catch(e) { console.error("Stage sync failed",e); return false; }
  };

  const [movingInvoice, setMovingInvoice] = useState(false);
  const [pdNotes, setPdNotes] = useState({});
  const [pdBusy, setPdBusy] = useState("");
  const [pdPrio, setPdPrio] = useState(false);
  const [pdUrgent, setPdUrgent] = useState(false);
  const [pvMode, setPvMode] = useState("pv");
  const [pvInvQ, setPvInvQ] = useState("");
  const [pvInvScope, setPvInvScope] = useState("active");
  const openPdReview = sub => { setSelectedSub(sub); setView("processSubmission"); };
  const pdApproveSub = async sub => {
    if(pdBusy) return;
    if(!window.confirm("Approve invoice "+sub.invoiceNo+" and release it to the Document Controller?")) return;
    setPdBusy(sub.id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/submissions/"+encodeURIComponent(sub.id)+"/pd-approve",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({priority:pdPrio,urgent:pdUrgent})});
      const data = await res.json().catch(()=>({}));
      if(!res.ok) return showToast(data.error||"Could not approve","error");
      if(data.invoice) setInvoices(p=>[data.invoice,...p.filter(i=>i.id!==data.invoice.id)]);
      setPendingSubs(p=>p.map(x=>x.id===sub.id?{...x,status:"processed",processedInvoiceId:(data.invoice&&data.invoice.id)||sub.invoiceNo,pdApprovedBy:currentUser.name}:x));
      try { logAudit("Approved by PD", sub.invoiceNo+" | "+sub.supplierGuess+" | AED "+Number(sub.amountGuess).toLocaleString()); } catch(_){}
      setPdPrio(false); setPdUrgent(false);
      showToast(data.invoice&&data.invoice.supplierPending?"Approved - released to DC (new supplier, DC will verify)":"Approved - released to DC");
      setSelectedSub(null); setView("intake");
    } catch(e) { showToast("Cannot connect to server","error"); }
    finally { setPdBusy(""); }
  };
  const pdRejectSub = async sub => {
    if(pdBusy) return;
    const note = ((pdNotes[sub.id])||"").trim();
    if(!note) return showToast("A reason is required - the engineer will see it","error");
    setPdBusy(sub.id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/submissions/"+encodeURIComponent(sub.id)+"/pd-reject",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({note})});
      const data = await res.json().catch(()=>({}));
      if(!res.ok) return showToast(data.error||"Could not reject","error");
      setPendingSubs(p=>p.map(x=>x.id===sub.id?{...x,status:"rejected",rejectReason:"Rejected by PD: "+note}:x));
      try { logAudit("Rejected by PD", sub.invoiceNo+" | "+sub.supplierGuess+" | Reason: "+note); } catch(_){}
      showToast("Rejected - sent back to the engineer");
      setSelectedSub(null); setView("intake");
    } catch(e) { showToast("Cannot connect to server","error"); }
    finally { setPdBusy(""); }
  };
  const verifySupplier = async (inv, action, name) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/invoices/"+encodeURIComponent(inv.id)+"/verify-supplier",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({action,supplier:name})});
      const data = await res.json().catch(()=>({}));
      if(!res.ok) return showToast(data.error||"Could not verify supplier","error");
      setInvoices(p=>p.map(i=>i.id===inv.id?{...i,supplier:data.supplier,supplierPending:false}:i));
      if(data.supplierId && !suppliers.some(x=>x.id===data.supplierId)) setSuppliers(p=>[...p,{id:data.supplierId,name:data.supplier}].sort((a,b)=>a.name.localeCompare(b.name)));
      try { logAudit(action==="approve_new"?"New Supplier Approved":"Supplier Matched", inv.id+" | "+(inv.submittedSupplier||inv.supplier)+" -> "+data.supplier); } catch(_){}
      showToast(action==="approve_new"?"Supplier approved and added to the list":"Supplier set to "+data.supplier);
    } catch(e) { showToast("Cannot connect to server","error"); }
  };
  const moveInvoice = inv => {
    if(movingInvoice) return;
    if(inv.supplierPending) return showToast("Verify the new supplier first","error");
    const ci=stageIds.indexOf(inv.stage), ti=stageIds.indexOf(moveStage);
    const destStage = sortedStages.find(s=>s.id===moveStage);
    const pvStageIds = sortedStages.filter(s=>s.requiresPV).map(s=>s.id);
    const hadPVBefore = inv.history?.some(h=>pvStageIds.includes(h.stage));
    if(isLockedInPV(inv,vouchers)) return showToast("Move via Voucher","error");
    if(!isAdmin && !canAct(inv.stage)) return showToast("No permission","error");
    if(!moveStage) return showToast("Select a stage","error");
    if(ti>ci+1) return showToast("Cannot skip stages","error");
    if(ti<ci) return showToast("Use the Return tab to send an invoice back a stage","error");
    // Payment Vouchers are an Accountant-only action. Whenever the Accountant moves an invoice
    // forward and there's a PV-required stage still ahead in the workflow, they must attach a PV now
    // (create new, or attach to an existing one) — regardless of exactly which stage is next.
    const hasUpcomingPVStage = pvStageIds.some(id=>stageIds.indexOf(id)>ci);
    if(hasRole("accountant") && !inv.voucherNo && hasUpcomingPVStage){
      if(!hadPVBefore){
        if(!movePvRef.trim()) return showToast("PV number required","error");
        setShowPVModal({invoiceIds:[inv.id],supplier:inv.supplier,note:moveNote,singleInvId:inv.id,isReturn:false});
        setPvModalForm({no:movePvRef.trim(),date:todayStr()}); return;
      } else {
        setShowPVModal({invoiceIds:[inv.id],supplier:inv.supplier,note:moveNote,singleInvId:inv.id,isReturn:true});
        setPvModalForm({no:inv.voucherNo||movePvRef.trim()||"",date:todayStr()}); return;
      }
    }
    // Anyone else moving into a stage that actually requires a PV must already have one —
    // only the Accountant is allowed to create/attach it, earlier in the journey
    if(destStage?.requiresPV && !inv.voucherNo && !hadPVBefore){
      return showToast("This invoice needs a Payment Voucher — ask the Accountant to attach one first","error");
    }
    if(moveStage==="completed"){ setPendingMove({inv,note:moveNote}); setFileNumInput(""); setShowFileModal(true); return; }
    setMovingInvoice(true);
    const updated = addHist(inv,moveStage,moveNote);
    setInvoices(p=>p.map(i=>i.id!==inv.id?i:updated));
    setSelected(updated);
    syncInvoiceStage(inv, updated).then(ok=>{
      if(!ok) showToast("Warning: move may not have saved — refresh to check","error");
    }).finally(()=>setMovingInvoice(false));
    logAudit("Stage Move", inv.id+": "+inv.stage+" to "+moveStage+(moveNote?" | Note: "+moveNote:""));
    setMoveStage(""); setMoveNote(""); setMovePvRef(""); showToast("Moved!");
  };

  const returnInvoice = inv => {
    if(movingInvoice) return;
    if((!isAdmin && !canAct(inv.stage))||!moveStage||!moveNote.trim()) return showToast("Stage and reason required","error");
    setMovingInvoice(true);
    const updated = addHist(inv,moveStage,"Returned: "+moveNote);
    setInvoices(p=>p.map(i=>i.id!==inv.id?i:updated));
    setSelected(updated);
    syncInvoiceStage(inv, updated).then(ok=>{
      if(!ok) showToast("Warning: return may not have saved — refresh to check","error");
    }).finally(()=>setMovingInvoice(false));
    logAudit("Invoice Returned", inv.id+": "+inv.stage+" back to "+moveStage+" | "+moveNote);
    setMoveStage(""); setMoveNote(""); setMoveTab("forward"); showToast("Returned!");
  };

  const [creatingPV, setCreatingPV] = useState(false);
  const confirmCreatePV = async () => {
    if(creatingPV) return;
    if(!pvModalForm.no.trim()) return showToast("PV number required","error");
    setCreatingPV(true);
    try {
      const pvNum = pvModalForm.no.trim();
      const invoiceIds = showPVModal.invoiceIds;
      const sup = invoices.find(i=>i.id===invoiceIds[0])?.supplier||"";
      const token2 = localStorage.getItem("token");
      const existingVoucher = vouchers.find(v=>v.no===pvNum);

      if(existingVoucher){
        const res = await fetch("/api/vouchers/"+encodeURIComponent(pvNum)+"/add-invoice",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token2},body:JSON.stringify({invoiceId:invoiceIds[0]})});
        if(!res.ok) return showToast("Failed to attach to existing PV","error");
        const data = await res.json();
        setVouchers(p=>p.map(v=>v.no===pvNum?{...v,invoiceIds:data.invoiceIds}:v));
      } else {
        const res = await fetch("/api/vouchers",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token2},body:JSON.stringify({no:pvNum,supplier:sup,date:pvModalForm.date,invoiceIds,createdBy:currentUser.name})});
        if(!res.ok) return showToast("Failed to create PV on server","error");
        setVouchers(p=>[...p,{no:pvNum,supplier:sup,date:pvModalForm.date,invoiceIds,createdBy:currentUser.name}]);
      }

      const updatedInvoices = [];
      invoiceIds.forEach(id=>{
        const i = invoices.find(x=>x.id===id);
        if(!i) return;
        const ci=stageIds.indexOf(i.stage);
        const noteText = existingVoucher ? "Attached to existing PV "+pvNum+(showPVModal.note?" — "+showPVModal.note:"") : "PV "+pvNum+(showPVModal.note?" — "+showPVModal.note:"");
        const updated = addHist(i,stageIds[ci+1]||i.stage,noteText,currentUser.name,{voucherNo:pvNum,pvRef:pvNum});
        updatedInvoices.push(updated);
      });
      await Promise.all(updatedInvoices.map(updated=>
        fetch("/api/invoices/"+encodeURIComponent(updated.id)+"/stage",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token2},body:JSON.stringify({stage:updated.stage,history:updated.history,voucherNo:pvNum,pvRef:pvNum,fileNumber:""})}).catch(()=>{ showToast("Warning: "+updated.id+" may not have synced","error"); })
      ));
      setInvoices(p=>p.map(i=>updatedInvoices.find(u=>u.id===i.id)||i));
      logAudit(existingVoucher?"Attached to Existing PV":"PV Created", pvNum+" | "+sup);
      setShowPVModal(null); setMoveStage(""); setMoveNote(""); setMovePvRef(""); showToast(existingVoucher?"Attached to PV "+pvNum+"!":"PV "+pvNum+" created!");
    } catch(e) {
      showToast("Cannot connect to server","error");
    } finally {
      setCreatingPV(false);
    }
  };

  const moveVoucher = async v => {
    if(!vMoveStage) return showToast("Select stage","error");
    const cs=vCurStage(v);
    if(cs&&!isAdmin&&!canAct(cs)) return showToast("No permission","error");
    if(vMoveStage==="completed"){ setVFileNums({_all:""}); setShowVFileModal(true); return; }
    const token = localStorage.getItem("token");
    const updatedList = [];
    invoices.forEach(i=>{
      if(!v.invoiceIds.includes(i.id)||i.stage==="completed") return;
      updatedList.push(addHist(i,vMoveStage,vMoveNote||""));
    });
    await Promise.all(updatedList.map(updated=>
      fetch("/api/invoices/"+encodeURIComponent(updated.id)+"/stage",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({stage:updated.stage,history:updated.history,voucherNo:updated.voucherNo||null,pvRef:updated.pvRef||"",fileNumber:updated.fileNumber||""})}).catch(()=>{ showToast("Warning: "+updated.id+" may not have synced","error"); })
    ));
    setInvoices(p=>p.map(i=>updatedList.find(u=>u.id===i.id)||i));
    setVMoveStage(""); setVMoveNote(""); showToast("All moved!");
  };

  const returnVoucher = async v => {
    if(!vMoveStage||!vMoveNote.trim()) return showToast("Stage and reason required","error");
    const token = localStorage.getItem("token");
    const updatedList = [];
    invoices.forEach(i=>{
      if(!v.invoiceIds.includes(i.id)||i.stage==="completed") return;
      // Keep voucherNo/pvRef exactly as they are — returning the group does not ungroup it,
      // it just sends the whole PV-grouped batch back a stage together.
      updatedList.push(addHist(i,vMoveStage,"Returned: "+vMoveNote,currentUser.name,{voucherNo:i.voucherNo,pvRef:i.pvRef}));
    });
    await Promise.all(updatedList.map(updated=>
      fetch("/api/invoices/"+encodeURIComponent(updated.id)+"/stage",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({stage:updated.stage,history:updated.history,voucherNo:updated.voucherNo,pvRef:updated.pvRef,fileNumber:""})}).catch(()=>{ showToast("Warning: "+updated.id+" may not have synced","error"); })
    ));
    // The voucher itself is untouched — it is not dissolved or deleted by returning it.
    setInvoices(p=>p.map(i=>updatedList.find(u=>u.id===i.id)||i));
    setVMoveStage(""); setVMoveNote(""); setVMoveTab("forward"); setView("vouchers"); showToast("Group returned!");
  };

  const confirmFiled = () => {
    if(!fileNumInput.trim()) return showToast("File number required","error");
    const {inv,note}=pendingMove;
    const updated = addHist(inv,"completed","File No: "+fileNumInput+(note?" — "+note:""),currentUser.name,{fileNumber:fileNumInput});
    setInvoices(p=>p.map(i=>i.id!==inv.id?i:updated));
    syncInvoiceStage(inv, updated);
    setShowFileModal(false); setPendingMove(null); setFileNumInput(""); setMoveStage(""); setMoveNote(""); showToast("Filed!");
  };

  const confirmVFiled = async v => {
    const fileNo=(vFileNums._all||"").trim();
    if(!fileNo) return showToast("File number is required","error");
    const token = localStorage.getItem("token");
    const updates = invoices.filter(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed")
      .map(i=>addHist(i,"completed","File: "+fileNo,currentUser.name,{fileNumber:fileNo}));
    setInvoices(p=>p.map(i=>updates.find(u=>u.id===i.id)||i));
    await Promise.all(updates.map(u=>
      fetch("/api/invoices/"+encodeURIComponent(u.id)+"/stage",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({stage:"completed",history:u.history,voucherNo:u.voucherNo||null,pvRef:u.pvRef||"",fileNumber:fileNo})}).catch(()=>{ showToast("Warning: "+u.id+" may not have synced","error"); })
    ));
    try { logAudit("Voucher Filed", v.no+" | File No "+fileNo+" | "+updates.length+" invoice(s)"); } catch(_){}
    setShowVFileModal(false); setVFileNums({}); setVMoveStage(""); setVMoveNote(""); showToast("Voucher filed under "+fileNo);
  };

  const createPVBulk = async () => {
    if(!vForm.no||!vSelected.length) return showToast("Enter a PV number and select at least one invoice","error");
    const pvSup=[...new Set(invoices.filter(i=>vSelected.includes(i.id)).map(i=>i.supplier))].join(" / ");
    if(vouchers.find(v=>v.no===vForm.no)) return showToast("PV exists","error");
    const pvNum=vForm.no.trim();
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/vouchers",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({no:pvNum,supplier:pvSup,date:vForm.date,invoiceIds:vSelected,createdBy:currentUser.name})});
      if(!res.ok){ return showToast("Failed to create PV on server","error"); }
    } catch(e) { return showToast("Cannot connect to server","error"); }
    setVouchers(p=>[...p,{no:pvNum,supplier:pvSup,date:vForm.date,invoiceIds:vSelected,createdBy:currentUser.name}]);
    // Build the updates up front (not inside the state updater, which React may run later) so the
    // server always receives the PV number for every selected invoice.
    const updates = invoices.filter(i=>vSelected.includes(i.id)).map(i=>{
      const ci=stageIds.indexOf(i.stage);
      return addHist(i,stageIds[ci+1]||i.stage,"PV "+pvNum,currentUser.name,{voucherNo:pvNum,pvRef:pvNum});
    });
    setInvoices(p=>p.map(i=>updates.find(u=>u.id===i.id)||i));
    await Promise.all(updates.map(updated=>
      fetch("/api/invoices/"+encodeURIComponent(updated.id)+"/stage",{method:"PATCH",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({stage:updated.stage,history:updated.history,voucherNo:pvNum,pvRef:pvNum,fileNumber:updated.fileNumber||""})}).catch(()=>{
        showToast("Warning: "+updated.id+" may not have synced","error");
      })
    ));
    logAudit("PV Created", pvNum+" | "+pvSup+" | "+vSelected.length+" invoice(s)");
    setVForm({no:"",date:todayStr()});  setVSelected([]); setView("vouchers"); showToast("PV created!");
  };

  const addInvoice = async () => {
    if(!isAdmin && !canAct("received")) return showToast("No permission","error");
    if(!invForm.id) return showToast("Invoice # is required","error");
    if(!invForm.supplier) return showToast("Supplier is required","error");
    if(!invForm.amount) return showToast("Amount is required","error");
    if(!invForm.date) return showToast("Invoice Date is required","error");
    if(!invForm.receivedDate) return showToast("Received Date is required","error");
    if(!invForm.projectId) return showToast("Project is required","error");
    if(!invForm.paymentMethod) return showToast("Payment Method is required","error");
    if(!invForm.paymentType) return showToast("Payment Terms is required","error");
    if(invoices.find(i=>(i.invoiceNo||i.id)===invForm.id && String(i.supplier||"").trim().toLowerCase()===String(invForm.supplier||"").trim().toLowerCase())) return showToast("Invoice "+invForm.id+" already exists for this supplier","error");
    // Upload file if selected
    let attachmentKey = ""; let attachmentName = "";
    if(invFile) {
      const token = localStorage.getItem("token");
      const fd = new FormData(); fd.append("file", invFile);
      const uploadRes = await fetch("/api/upload",{method:"POST",headers:{"Authorization":"Bearer "+token},body:fd});
      if(uploadRes.ok){ const d=await uploadRes.json(); attachmentKey=d.key; attachmentName=invFile.name; }
    }
    const newInv = {...invForm,amount:parseFloat(invForm.amount),dueDate:makeDueDate(invForm),stage:"received",voucherNo:null,pvRef:"",fileNumber:"",attachmentKey,attachmentName,attachments:[],history:[{stage:"received",date:invForm.receivedDate,note:"Invoice received",by:currentUser.name}]};
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/invoices",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify(newInv)});
      const data = await res.json();
      if(!res.ok) return showToast(data.error||"Failed to save invoice","error");
      const savedInv = {...newInv,id:data.id||newInv.id,invoiceNo:newInv.id};
      setInvoices(p=>[savedInv,...p]);
      try { logAudit("Invoice Created", savedInv.id+" | "+newInv.supplier+" | AED "+newInv.amount.toLocaleString()); } catch(_){}
      setInvForm(EMPTY_INV_FORM); setInvFile(null); setView("dashboard"); showToast("Invoice added!");
    } catch(e) { showToast("Cannot connect to server","error"); }
  };

  const handleFileChange = e => {
    const f=e.target.files?.[0]; if(!f) return;
    const r=new FileReader();
    r.onload=()=>setSubmitForm(p=>({...p,file:{name:f.name,type:f.type,data:r.result,_raw:f}}));
    r.readAsDataURL(f);
  };

  const submitRequest = async () => {
    if(!submitForm.projectId) return showToast("Please select a project","error");
    if(!submitForm.file) return showToast("Please attach the invoice file","error");
    if(!(submitForm.invoiceNo||"").trim()) return showToast("Invoice number is required","error");
    if(!(submitForm.lpo||"").trim()) return showToast("LPO number is required","error");
    if(!(submitForm.supplierGuess||"").trim()) return showToast("Supplier is required","error");
    if(submitForm.amountGuess===""||submitForm.amountGuess==null) return showToast("Amount is required","error");
    const guessNum = Number(submitForm.amountGuess);
    if(!Number.isFinite(guessNum)||guessNum<=0) return showToast("Amount must be greater than zero","error");
    if(submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const proj = projects.find(p=>p.id===submitForm.projectId);
      // Send file + fields in one multipart request
      const formData = new FormData();
      formData.append("file", submitForm.file._raw);
      formData.append("site", proj?.name||"");
      formData.append("projectId", submitForm.projectId);
      formData.append("invoiceNo", (submitForm.invoiceNo||"").trim());
      formData.append("lpo", (submitForm.lpo||"").trim());
      formData.append("supplierGuess", submitForm.supplierGuess||"");
      formData.append("amountGuess", submitForm.amountGuess||"");
      formData.append("note", submitForm.note||"");
      const res = await fetch("/api/submissions", {
        method:"POST",
        headers:{"Authorization":"Bearer "+token},
        body: formData
      });
      if(!res.ok){ const err=await res.json().catch(()=>({})); setSubmitting(false); return showToast(err.error||"Submission failed","error"); }
      await loadAllData(token, true);
      setSubmitForm({site:"",projectId:"",file:null,invoiceNo:"",lpo:"",supplierGuess:"",amountGuess:"",note:""}); 
      setSubmitting(false); setView("mySubmissions"); showToast("Submitted!");
    } catch(e) { showToast("Cannot connect to server","error"); setSubmitting(false); }
  };


  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
    setUsers(DEFAULT_USERS);
  };

  const [prevView, setPrevView] = useState("dashboard");
  const [stashedSelected, setStashedSelected] = useState(null);
  const [stashedPrevView, setStashedPrevView] = useState(null);
  navRef.current = { view, prevView, selected, selectedSub, invoices, vouchers, pendingSubs, user: currentUser };
  const setViewAndSave = (v) => { if(!["detail","processSubmission","recycleBin","pendingDeletions","voucherDetail"].includes(v)) localStorage.setItem("lastView",v); setView(v); };
  const openDetail  = inv => {
    // If we're navigating here from a voucher's detail screen, stash that voucher (and where IT came from)
    // so Back can fully restore both — otherwise `selected`/`prevView` get overwritten with this invoice's
    // values and the voucher screen either has nothing to render, or its own Back button gets stuck on itself.
    if(view==="voucherDetail"&&selected){ setStashedSelected(selected); setStashedPrevView(prevView); }
    setPrevView(view); setSelected(inv); setMoveTab("forward"); setMoveStage(""); setMoveNote(""); setMovePvRef("");
    localStorage.setItem("lastView","detail"); localStorage.setItem("lastDetailId",inv.id); setView("detail");
  };
  const goBackFromDetail = () => {
    if(prevView==="voucherDetail"&&stashedSelected){
      setSelected(stashedSelected);
      setPrevView(stashedPrevView||"vouchers");
      setStashedSelected(null); setStashedPrevView(null);
      setView("voucherDetail");
      return;
    }
    setView(prevView||"dashboard");
  };
  const openVoucher = v   => { setPrevView(view); setSelected(v);   setVMoveTab("forward"); setVMoveStage(""); setVMoveNote(""); setView("voucherDetail"); };

  const pvSuppliers    = [...new Set(invoices.filter(i=>i.stage==="accountant"&&!i.voucherNo).map(i=>i.supplier))];
  const engStatusOf = s => {
    const mk = (label,c,bg,bd) => ({label,c,bg,bd});
    if(s.status==="rejected") return mk((s.rejectReason||"").startsWith("Rejected by PD")?"❌ Rejected by PD":"❌ Rejected","#b42318","#fde8e6","#f3c6c1");
    if(s.processedInvoiceId&&deletedInvoiceIds.includes(s.processedInvoiceId)) return mk("🔒 Closed","#4f676d","#edf2f3","#ccc");
    const inv = invoices.find(x=>x.id===s.processedInvoiceId);
    if(!s.processedInvoiceId||!inv) return s.status==="pending" ? mk("🧐 Pending PD Approval","#5b3fa0","#efe9fb","#d6c9f2") : mk("📤 Submitted","#7a4506","#fdf3e3","#f3d9ad");
    if(["paid","completed"].includes(inv.stage)) return mk("✅ Completed","#20c070","#20c07018","#20c07040");
    if(inv.stage==="received") return s.pdApprovedBy ? mk("✅ Approved by PD · With DC","#1a9a60","#e9faf1","#b7ecd2") : mk("📥 With DC","#085c66","#e3f5f7","#b7e3e8");
    return mk("⏳ In Progress","#0b7a87","#e3f5f7","#b7e3e8");
  };
  const engStatusBadge = s => { const st=engStatusOf(s); return <span style={{background:st.bg,color:st.c,border:"1px solid "+st.bd,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{st.label}</span>; };

  /* ── render login ── */
  if(!currentUser) return (
    <div style={{minHeight:"100vh",background:"#f4f8f9",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <style>{CSS}</style>
      <div style={{background:"#fff",borderRadius:16,padding:32,width:380,boxShadow:"0 20px 60px rgba(0,0,0,0.1)",border:"1px solid rgba(11,122,135,0.2)"}}>
        <img src="/assets/bg-logo.png" alt="BG Group" style={{display:"block",height:48,margin:"0 auto 16px"}}/>
        <div style={{...S.h,fontSize:22,color:"#0b7a87",textAlign:"center",marginBottom:4}}>Invoice Tracker</div>
        <div style={{fontSize:13,color:"#4f676d",textAlign:"center",marginBottom:24}}>Sign in to continue</div>
        <LoginForm onLogin={u=>{
          setCurrentUser(u);
          const defaultView = u.role==="engineer"?"submitInvoice":u.role==="admin"?"dashboard":u.role==="pd"?"intake":"myqueue";
          localStorage.setItem("lastView", defaultView);
          setView(defaultView);
          const token = localStorage.getItem("token");
          if(token){
            const h = {"Authorization":"Bearer "+token};
            fetch("/api/users",{headers:h}).then(r=>r.json()).then(data=>{if(Array.isArray(data)&&data.length>0)setUsers(data);}).catch(()=>{});
            fetch("/api/projects",{headers:h}).then(r=>r.json()).then(data=>{if(Array.isArray(data))setProjects(data);}).catch(()=>{});
            fetch("/api/invoices",{headers:h}).then(r=>r.json()).then(data=>{
              if(Array.isArray(data)) setInvoices(data.map(i=>({...i,amount:parseFloat(i.amount)||0})));
            }).catch(()=>{});
            fetch("/api/vouchers",{headers:h}).then(r=>r.json()).then(data=>{if(Array.isArray(data))setVouchers(data);}).catch(()=>{});
            fetch("/api/submissions",{headers:h}).then(r=>r.json()).then(data=>{
              if(Array.isArray(data)) setPendingSubs(data);
            }).catch(()=>{});
          }
        }}/>
        <div style={{marginTop:20,textAlign:"center",fontSize:10,color:"#ccc",letterSpacing:"0.04em"}}>© 2026 Property of BG Group. All rights reserved.</div>
      </div>
    </div>
  );

  const isAdmin    = hasRole("admin");
  const canSetPrio = isAdmin || ["dc","pd","om"].some(r=>hasRole(r));
  const isEngineer = hasRole("engineer");

  const navItems = isEngineer
    ? [["submitInvoice","Submit"],["mySubmissions","My Projects"]]
    : [["myqueue","My Queue"],["dashboard","Dashboard"],["addInv","+ Invoice"],["vouchers","Vouchers"],["createVoucher","+ PV"],["intake","Pending Approval"],["suppliers","Suppliers"],["reports","Reports"],["archive","Filed"]]
        .filter(x=>isAdmin||canAct("received")||(x[0]==="createVoucher"&&hasRole("accountant"))||(x[0]==="intake"&&hasRole("pd"))||(x[0]!=="addInv"&&x[0]!=="createVoucher"&&x[0]!=="intake"))
        .filter(x=>x[0]!=="intake"||isAdmin||hasRole("pd"))
        .filter(x=>x[0]!=="createVoucher"||isAdmin||hasRole("accountant"))
        .filter(x=>!(isPureAdmin&&x[0]==="myqueue"))
        .filter(x=>x[0]!=="suppliers"||["admin","dc","accountant"].some(hasRole));

  const stageIcons = ["🧐","📥","🔍","💰","↩️","🏗️","✍️","🔍","💳","✅","📁"];
  const NAV_ICONS = {myqueue:"📥",dashboard:"📊",addInv:"➕",vouchers:"📄",createVoucher:"🧾",intake:"📬",suppliers:"🏷️",reports:"📈",archive:"📁",submitInvoice:"📤",mySubmissions:"🗂️"};
  const userInitials = (currentUser.name||"?").trim().split(/\s+/).map(w=>w[0]).slice(0,2).join("").toUpperCase();

  return (
    <div style={{minHeight:"100vh",background:"#f4f8f9",color:"#12262b",display:"flex"}}>
      <style>{CSS}</style>

      {toast&&<div style={{position:"fixed",top:20,right:20,zIndex:9999,background:toast.type==="error"?"#ff4444":"#0b7a87",color:"#fff",padding:"12px 20px",borderRadius:12,fontWeight:600,fontSize:14,boxShadow:"0 8px 24px rgba(0,0,0,0.15)"}}>{toast.msg}</div>}

      {showAdmin&&<AdminPanel users={users} setUsers={setUsers} stages={sortedStages} setStages={setStages} projects={projects} setProjects={setProjects} settings={settings} setSettings={setSettings} auditLog={auditLog} setAuditLog={setAuditLog} alertRules={alertRules} setAlertRules={setAlertRules} invoices={invoices} vouchers={vouchers} onClose={()=>setShowAdmin(false)} showToast={showToast} logAudit={logAudit}/>}

      {/* PV MODAL */}
      {showPVModal&&(()=>{
        const extraOptions = invoices.filter(i=>
          i.id !== showPVModal.singleInvId &&
          i.stage === "accountant" &&
          !i.voucherNo &&
          i.supplier === showPVModal.supplier
        );
        const sel = showPVModal.invoiceIds;
        const toggleExtra = id => setShowPVModal(p=>({...p,invoiceIds:p.invoiceIds.includes(id)?p.invoiceIds.filter(x=>x!==id):[...p.invoiceIds,id]}));
        const total = invoices.filter(i=>sel.includes(i.id)).reduce((s,i)=>s+i.amount,0);
        return(
          <Modal title="Create Payment Voucher" onClose={()=>setShowPVModal(null)}>
            <div style={{background:"rgba(11,122,135,0.06)",border:"1px solid rgba(11,122,135,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#085c66"}}>
              {showPVModal.isReturn?"Assign PV for":"Creating PV for"} <b>{showPVModal.supplier}</b>
            </div>
            {showPVModal.isReturn&&(()=>{
              const existingPVs = vouchers.filter(v=>v.supplier===showPVModal.supplier||v.invoiceIds?.includes(showPVModal.singleInvId));
              return existingPVs.length>0?(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:600,color:"#3b545a",marginBottom:6}}>Select existing PV:</div>
                  {existingPVs.map(v=>(
                    <div key={v.no} onClick={()=>setPvModalForm(p=>({...p,no:v.no}))} style={{padding:"8px 12px",borderRadius:8,border:"1px solid "+(pvModalForm?.no===v.no?"#0b7a87":"#d5e1e4"),background:pvModalForm?.no===v.no?"rgba(11,122,135,0.08)":"#f9f9f9",cursor:"pointer",marginBottom:6,fontSize:12}}>
                      <b>{v.no}</b> · {v.supplier} · {v.date}
                    </div>
                  ))}
                  <div style={{fontSize:11,color:"#5a7278",margin:"8px 0 4px"}}>Or enter a new PV number below:</div>
                </div>
              ):null;
            })()}
            <div style={{marginBottom:12}}><span style={S.label}>PV Number *</span><input style={S.input} value={pvModalForm.no} onChange={e=>setPvModalForm(p=>({...p,no:e.target.value}))}/></div>
            <div style={{marginBottom:14}}><span style={S.label}>Date</span><input style={S.input} type="date" value={pvModalForm.date} onChange={e=>setPvModalForm(p=>({...p,date:e.target.value}))}/></div>
            <div style={{marginBottom:extraOptions.length?12:16}}>
              <span style={S.label}>Invoices under this PV</span>
              {invoices.filter(i=>i.id===showPVModal.singleInvId).map(inv=>(
                <div key={inv.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,marginBottom:6,background:"rgba(11,122,135,0.06)",border:"2px solid #0b7a87"}}>
                  <div style={{width:18,height:18,borderRadius:5,background:"#0b7a87",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",flexShrink:0}}>✓</div>
                  <span style={{fontWeight:700,color:"#0b7a87",fontSize:12}}>{inv.id}</span>
                  {inv.lpo&&<span style={{color:"#4f676d",fontSize:12}}>{inv.lpo}</span>}
                  <span style={{marginLeft:"auto",fontWeight:700,fontSize:12}}>AED {inv.amount.toLocaleString()}</span>
                </div>
              ))}
              {extraOptions.length>0&&(
                <div>
                  <div style={{fontSize:11,color:"#4f676d",marginBottom:6,marginTop:4}}>Other invoices from <b>{showPVModal.supplier}</b> at Accountant — add to same PV:</div>
                  {extraOptions.map(inv=>{
                    const isChosen=sel.includes(inv.id);
                    return(
                      <div key={inv.id} onClick={()=>toggleExtra(inv.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,marginBottom:6,cursor:"pointer",border:"1px solid "+(isChosen?"#0b7a87":"#d5e1e4"),background:isChosen?"rgba(11,122,135,0.06)":"#fafafa"}}>
                        <div style={{width:18,height:18,borderRadius:5,border:"2px solid "+(isChosen?"#0b7a87":"#ccc"),background:isChosen?"#0b7a87":"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",flexShrink:0}}>{isChosen?"✓":""}</div>
                        <span style={{fontWeight:700,color:isChosen?"#0b7a87":"#12262b",fontSize:12}}>{inv.id}</span>
                        {inv.lpo&&<span style={{color:"#4f676d",fontSize:12}}>{inv.lpo}</span>}
                        <span style={{marginLeft:"auto",fontWeight:700,fontSize:12}}>AED {inv.amount.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{background:"rgba(11,122,135,0.06)",border:"1px solid rgba(11,122,135,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:"#0b7a87",fontWeight:600}}>{sel.length} invoice(s) selected</span>
              <span style={{fontWeight:800,color:"#0b7a87"}}>Total: AED {total.toLocaleString()}</span>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button className="hover-up" style={{...S.btn,flex:1,opacity:creatingPV?0.7:1}} onClick={confirmCreatePV} disabled={creatingPV}>{creatingPV?"⏳ Creating...":"Create PV"}</button>
              <button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setShowPVModal(null)}>Cancel</button>
            </div>
          </Modal>
        );
      })()}

      {showFileModal&&<Modal title="File Invoice" onClose={()=>setShowFileModal(false)}>
        <div style={{marginBottom:14}}><span style={S.label}>File Number *</span><input style={S.input} value={fileNumInput} onChange={e=>setFileNumInput(e.target.value)}/></div>
        <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1,background:"linear-gradient(135deg,#20c070,#18a058)"}} onClick={confirmFiled}>File</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setShowFileModal(false)}>Cancel</button></div>
      </Modal>}

      {showVFileModal&&selected&&<Modal title={"File Voucher — "+selected.no} onClose={()=>setShowVFileModal(false)}>
        <div style={{marginBottom:10}}><span style={S.label}>File No * (applies to all {selected.invoiceIds.length} invoice(s) in this voucher)</span><input style={S.input} value={vFileNums._all||""} onChange={e=>setVFileNums({_all:e.target.value})} autoFocus/></div>
        <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1,background:"linear-gradient(135deg,#20c070,#18a058)"}} onClick={()=>confirmVFiled(selected)}>File All</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setShowVFileModal(false)}>Cancel</button></div>
      </Modal>}

      {editingInv&&<Modal title="Edit Invoice" onClose={()=>setEditingInv(null)}>
        {editingInv._locked&&!editingInv._flagsOnly&&<div style={{background:"rgba(48,217,138,0.06)",border:"1px solid rgba(48,217,138,0.3)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#085c66"}}>🔒 This invoice is grouped under PV {editingInv.voucherNo}. Only Priority and Urgent can be changed here — everything else needs the group returned first.</div>}
        {!editingInv._locked&&<>
        {[["Invoice #","id","text"],["LPO","lpo","text"],["Invoice Date","date","date"],["Received","receivedDate","date"],["Amount","amount","number"]].map(x=><div key={x[1]} style={{marginBottom:10}}><span style={S.label}>{x[0]}</span><input style={S.input} type={x[2]} value={editingInv[x[1]]||""} onChange={e=>setEditingInv(p=>({...p,[x[1]]:e.target.value}))}/></div>)}
        <div style={{marginBottom:10}}><span style={S.label}>Supplier</span><input style={S.input} value={editingInv.supplier||""} onChange={e=>setEditingInv(p=>({...p,supplier:e.target.value}))}/></div>
        <div style={{marginBottom:10}}><span style={S.label}>Project</span><select style={S.input} value={editingInv.projectId||""} onChange={e=>setEditingInv(p=>({...p,projectId:e.target.value}))}><option value="">— None —</option>{projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <PaymentFields form={editingInv} setForm={setEditingInv}/>
        </>}
        {canSetPrio&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,padding:"10px 0",borderTop:"1px solid #f4f8f9"}}>
          {canSetPrio&&<div onClick={()=>setEditingInv(p=>({...p,priority:!p.priority}))} style={{border:"0.5px solid "+(editingInv.priority?"#f3c98a":"#d5e1e4"),borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",background:editingInv.priority?"#fdecd6":"#fff"}}>
            <div style={{width:36,height:20,borderRadius:10,background:editingInv.priority?"#f59e0b":"#d5e1e4",position:"relative",transition:"background 0.2s",flexShrink:0}}>
              <div style={{position:"absolute",top:2,left:editingInv.priority?16:2,width:16,height:16,borderRadius:8,background:"#fff",transition:"left 0.2s"}}/>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:500,color:editingInv.priority?"#8a4b08":"#12262b"}}>Priority</div>
              <div style={{fontSize:11,color:editingInv.priority?"#8a4b08":"#5a7278"}}>{editingInv.priority?"On":"Off"}</div>
            </div>
          </div>}
          <div onClick={()=>setEditingInv(p=>({...p,urgent:!p.urgent}))} style={{border:"0.5px solid "+(editingInv.urgent?"#f3c6c1":"#d5e1e4"),borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",background:editingInv.urgent?"#fde8e6":"#fff"}}>
            <div style={{width:36,height:20,borderRadius:10,background:editingInv.urgent?"#ef4444":"#d5e1e4",position:"relative",transition:"background 0.2s",flexShrink:0}}>
              <div style={{position:"absolute",top:2,left:editingInv.urgent?16:2,width:16,height:16,borderRadius:8,background:"#fff",transition:"left 0.2s"}}/>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:500,color:editingInv.urgent?"#912018":"#12262b"}}>Urgent</div>
              <div style={{fontSize:11,color:editingInv.urgent?"#912018":"#5a7278"}}>{editingInv.urgent?"On":"Off"}</div>
            </div>
          </div>
        </div>}
        <div style={{display:"flex",gap:10}}>
          <button className="hover-up" style={{...S.btn,flex:1}} onClick={async()=>{
            const token = localStorage.getItem("token");
            try {
              if(editingInv._locked){
                // Grouped invoice — only Priority/Urgent can change, via the dedicated flags endpoint
                await fetch("/api/invoices/"+encodeURIComponent(editingInv._origId)+"/flags", {
                  method:"PATCH",
                  headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
                  body: JSON.stringify({priority:editingInv.priority||false, urgent:editingInv.urgent||false})
                });
                setInvoices(p=>p.map(i=>i.id===editingInv._origId?{...i,priority:editingInv.priority||false,urgent:editingInv.urgent||false}:i));
                logAudit("Invoice Flags Updated", editingInv._origId+" | priority:"+(!!editingInv.priority)+" urgent:"+(!!editingInv.urgent));
                setEditingInv(null); showToast("Updated!");
                return;
              }
              const updated = {...editingInv, amount:parseFloat(editingInv.amount), dueDate:makeDueDate(editingInv)};
              await fetch("/api/invoices/"+encodeURIComponent(editingInv._origId), {
                method:"PUT",
                headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
                body: JSON.stringify(updated)
              });
              setInvoices(p=>p.map(i=>i.id===editingInv._origId?{...i,...updated}:i));
              logAudit("Invoice Edited", editingInv._origId+" | "+updated.supplier+" | AED "+parseFloat(updated.amount).toLocaleString());
              setEditingInv(null); showToast("Updated!");
            } catch(e) { showToast("Cannot connect to server","error"); }
          }}>Save</button>
          <button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setEditingInv(null)}>Cancel</button>
        </div>
      </Modal>}

      {editingHist&&<Modal title="Edit History" onClose={()=>setEditingHist(null)}>
        <div style={{marginBottom:10}}><span style={S.label}>Stage</span><select style={S.input} value={histEdit.stage} onChange={e=>setHistEdit(p=>({...p,stage:e.target.value}))}>{sortedStages.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
        <div style={{marginBottom:10}}><span style={S.label}>Date</span><input style={S.input} type="date" value={histEdit.date||""} onChange={e=>setHistEdit(p=>({...p,date:e.target.value}))}/></div>
        <div style={{marginBottom:10}}><span style={S.label}>Note</span><input style={S.input} value={histEdit.note||""} onChange={e=>setHistEdit(p=>({...p,note:e.target.value}))}/></div>
        <div style={{marginBottom:10}}><span style={S.label}>By</span><input style={S.input} value={histEdit.by||""} onChange={e=>setHistEdit(p=>({...p,by:e.target.value}))}/></div>
        <div style={{display:"flex",gap:10}}>
          <button className="hover-up" style={{...S.btn,flex:1}} onClick={async()=>{
            const token = localStorage.getItem("token");
            const inv = invoices.find(i=>i.id===editingHist.invId);
            if(!inv) return;
            const before = inv.history[editingHist.idx];
            const h=[...inv.history]; h[editingHist.idx]={...histEdit};
            const newStage = h[h.length-1].stage;
            try {
              await fetch("/api/invoices/"+encodeURIComponent(editingHist.invId)+"/stage", {
                method:"PATCH",
                headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
                body: JSON.stringify({stage:newStage, history:h, voucherNo:inv.voucherNo||null, pvRef:inv.pvRef||"", fileNumber:inv.fileNumber||""})
              });
              setInvoices(p=>p.map(i=>{ if(i.id!==editingHist.invId) return i; return {...i,history:h,stage:newStage}; }));
              try { logAudit("History Entry Edited", editingHist.invId+" | Before: {stage:"+before.stage+",by:"+before.by+",note:\""+(before.note||"")+"\"} → After: {stage:"+histEdit.stage+",by:"+histEdit.by+",note:\""+(histEdit.note||"")+"\"}"); } catch(_){}
              setEditingHist(null); showToast("Updated!");
            } catch(e) { showToast("Cannot connect to server","error"); }
          }}>Save</button>
          <button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setEditingHist(null)}>Cancel</button>
        </div>
      </Modal>}

      {/* DOCUMENT PREVIEW MODAL */}
      {showDocModal&&selectedSub&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setShowDocModal(false)}>
          <div style={{background:"#fff",borderRadius:16,width:"85vw",height:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 20px",borderBottom:"1px solid #edf2f3",flexShrink:0}}>
              <div style={{fontSize:14,fontWeight:700}}>📄 {selectedSub.fileName}</div>
              <button onClick={()=>setShowDocModal(false)} style={{background:"#f4f8f9",border:"none",color:"#4f676d",fontSize:18,cursor:"pointer",borderRadius:8,width:32,height:32}}>✕</button>
            </div>
            <div style={{flex:1,overflow:"hidden"}}>
              <SubFilePreview sub={selectedSub} token={localStorage.getItem("token")}/>
            </div>
          </div>
        </div>
      )}

      {/* SIGN OUT CONFIRM */}
      {showSignOutModal&&(
        <Modal title="Sign out" onClose={()=>setShowSignOutModal(false)}>
          <div style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:20}}>
            <div style={{width:44,height:44,flexShrink:0,borderRadius:12,background:"#fdf3e3",color:"#7a4506",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>⏻</div>
            <div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Sign out of Invoice Tracker?</div>
              <div style={{fontSize:13,color:"#4f676d"}}>You'll need to log in again to access your queue and invoices.</div>
            </div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setShowSignOutModal(false)}>Cancel</button>
            <button className="hover-up" style={{...S.btnDanger,flex:1,padding:"10px 22px",fontSize:14,borderRadius:10}} onClick={()=>{setShowSignOutModal(false);logout();}}>Sign out</button>
          </div>
        </Modal>
      )}

      {/* SIDEBAR */}
      <aside style={{width:sidebarCollapsed?72:236,flexShrink:0,boxSizing:"border-box",background:"#fff",borderRight:"1px solid #e3ebed",padding:sidebarCollapsed?"20px 10px":"20px 14px",display:"flex",flexDirection:"column",gap:22,height:"100vh",position:"sticky",top:0,overflowY:"auto",overflowX:"hidden",transition:"width 0.15s ease"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"0 6px",justifyContent:sidebarCollapsed?"center":"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
            <img src="/assets/bg-logo.png" alt="BG Group" style={{height:28,flexShrink:0}}/>
            {!sidebarCollapsed&&<span style={{...S.h,fontSize:15,lineHeight:1.2,whiteSpace:"nowrap"}}>Invoice<br/>Tracker</span>}
          </div>
          {!sidebarCollapsed&&<button className="hover-up" title="Collapse sidebar" onClick={()=>setSidebarCollapsed(true)} style={{background:"none",border:"none",cursor:"pointer",color:"#5a7278",fontSize:16,padding:4,flexShrink:0}}>«</button>}
        </div>
        {sidebarCollapsed&&<button className="hover-up" title="Expand sidebar" onClick={()=>setSidebarCollapsed(false)} style={{background:"#f4f8f9",border:"none",borderRadius:8,cursor:"pointer",color:"#5a7278",fontSize:14,padding:"6px 0",margin:"-10px 0 0"}}>»</button>}
        <nav style={{display:"flex",flexDirection:"column",gap:3}}>
          {navItems.map(x=>{
            const active = view===x[0];
            return (
              <button key={x[0]} className="hover-up" title={sidebarCollapsed?x[1]:undefined} onClick={()=>setViewAndSave(x[0])} style={{display:"flex",alignItems:"center",gap:10,height:40,boxSizing:"border-box",padding:sidebarCollapsed?0:"0 10px",justifyContent:sidebarCollapsed?"center":"flex-start",borderRadius:10,border:"none",cursor:"pointer",background:active?"#e3f5f7":"transparent",color:active?"#085c66":"#3b545a",fontWeight:active?600:500,fontSize:13,fontFamily:"'DM Sans',sans-serif",textAlign:"left",position:"relative"}}>
                <span style={{fontSize:15}}>{NAV_ICONS[x[0]]||"•"}</span>
                {!sidebarCollapsed&&<span style={{flex:1}}>{x[1]}</span>}
                {x[0]==="intake"&&pendingIntakeCount>0&&(sidebarCollapsed?
                  <span style={{position:"absolute",top:2,right:10,width:8,height:8,borderRadius:4,background:"#0b7a87"}}/>
                :<span style={{minWidth:18,height:18,boxSizing:"border-box",padding:"0 5px",borderRadius:9,background:"#0b7a87",color:"#fff",fontSize:10,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}}>{pendingIntakeCount}</span>)}
              </button>
            );
          })}
        </nav>
        {(isAdmin||hasRole("dc")||hasRole("cfo"))&&<div style={{display:"flex",flexDirection:"column",gap:3}}>
          {!sidebarCollapsed&&<span style={{padding:"0 10px 4px",fontSize:10,fontWeight:600,letterSpacing:"0.08em",color:"#5a7278"}}>ADMIN</span>}
          <button className="hover-up" title={sidebarCollapsed?"Approvals":undefined} style={{display:"flex",alignItems:"center",gap:10,height:40,boxSizing:"border-box",padding:sidebarCollapsed?0:"0 10px",justifyContent:sidebarCollapsed?"center":"flex-start",borderRadius:10,border:"none",cursor:"pointer",background:"transparent",color:"#3b545a",fontWeight:500,fontSize:13,fontFamily:"'DM Sans',sans-serif",textAlign:"left",position:"relative"}} onClick={()=>{setPrevView(view);loadPendingDeletions();loadRecycleBin();setView("pendingDeletions");}}>
            <span style={{fontSize:15}}>📋</span>{!sidebarCollapsed&&<span style={{flex:1}}>Approvals</span>}
            {pendingDeletions.invoices.length+pendingDeletions.vouchers.length>0&&(sidebarCollapsed?
              <span style={{position:"absolute",top:2,right:10,width:8,height:8,borderRadius:4,background:"#e8a020"}}/>
            :<span style={{minWidth:18,height:18,boxSizing:"border-box",padding:"0 5px",borderRadius:9,background:"#e8a020",color:"#fff",fontSize:10,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}}>{pendingDeletions.invoices.length+pendingDeletions.vouchers.length}</span>)}
          </button>
          <button className="hover-up" title={sidebarCollapsed?"Recycle Bin":undefined} style={{display:"flex",alignItems:"center",gap:10,height:40,boxSizing:"border-box",padding:sidebarCollapsed?0:"0 10px",justifyContent:sidebarCollapsed?"center":"flex-start",borderRadius:10,border:"none",cursor:"pointer",background:"transparent",color:"#3b545a",fontWeight:500,fontSize:13,fontFamily:"'DM Sans',sans-serif",textAlign:"left"}} onClick={()=>{setPrevView(view);loadRecycleBin();setView("recycleBin");}}>
            <span style={{fontSize:15}}>🗑</span>{!sidebarCollapsed&&<span style={{flex:1}}>Recycle Bin</span>}
          </button>
          {isAdmin&&<button className="hover-up" title={sidebarCollapsed?"Admin Panel":undefined} style={{display:"flex",alignItems:"center",gap:10,height:40,boxSizing:"border-box",padding:sidebarCollapsed?0:"0 10px",justifyContent:sidebarCollapsed?"center":"flex-start",borderRadius:10,border:"none",cursor:"pointer",background:"transparent",color:"#3b545a",fontWeight:500,fontSize:13,fontFamily:"'DM Sans',sans-serif",textAlign:"left"}} onClick={()=>setShowAdmin(true)}>
            <span style={{fontSize:15}}>⚙️</span>{!sidebarCollapsed&&<span style={{flex:1}}>Admin Panel</span>}
          </button>}
        </div>}
        <div style={{marginTop:"auto",display:"flex",alignItems:"center",gap:10,padding:sidebarCollapsed?"10px 0":10,justifyContent:sidebarCollapsed?"center":"flex-start",borderRadius:12,background:"#f4f8f9"}}>
          <div title={sidebarCollapsed?currentUser.name:undefined} style={{width:34,height:34,borderRadius:17,background:"#0b7a87",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:12,flexShrink:0}}>{userInitials}</div>
          {!sidebarCollapsed&&<div style={{display:"flex",flexDirection:"column",flex:1,minWidth:0}}>
            <span style={{fontSize:13,fontWeight:600,lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{currentUser.name}</span>
            <RoleBadge role={currentUser.role}/>
          </div>}
          {!sidebarCollapsed&&<button className="hover-up" onClick={()=>setShowSignOutModal(true)} title="Sign out" style={{background:"none",border:"none",cursor:"pointer",color:"#5a7278",fontSize:15,padding:4}}>⏻</button>}
        </div>
        {sidebarCollapsed&&<button className="hover-up" onClick={()=>setShowSignOutModal(true)} title="Sign out" style={{background:"none",border:"none",cursor:"pointer",color:"#5a7278",fontSize:15,padding:4,alignSelf:"center"}}>⏻</button>}
      </aside>

      {/* MAIN */}
      <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",height:"100vh",overflow:"hidden"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e3ebed",padding:"0 24px",display:"flex",alignItems:"center",gap:10,minHeight:60,flexShrink:0,boxShadow:"0 1px 2px rgba(18,38,43,0.04)"}}>
          <div style={{flex:1}}/>
          {urgentCount>0&&!isEngineer&&<div className="pulse" style={{background:"#fde8e6",border:"1px solid #f3c6c1",borderRadius:20,padding:"4px 12px",fontSize:11,color:"#912018",fontWeight:600}}>🔴 {urgentCount}</div>}
          {!isEngineer&&<div style={{position:"relative"}}>
            <button className="hover-up" style={{...S.btnSec,padding:"6px 12px",fontSize:13,position:"relative"}} onClick={()=>setShowNotifDropdown(p=>!p)}>
              🔔{(notifications.length+pendingDeletionCount)>0&&<span style={{position:"absolute",top:-4,right:-4,background:"#b42318",color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>{notifications.length+pendingDeletionCount}</span>}
            </button>
            {showNotifDropdown&&<div style={{position:"absolute",right:0,top:38,width:340,background:"#fff",borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,0.15)",border:"1px solid #e3ebed",zIndex:999,maxHeight:400,overflowY:"auto"}}>
              <div style={{padding:"12px 16px",borderBottom:"1px solid #edf2f3",fontWeight:600,fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>🔔 Alerts ({notifications.length})</span>
                <span onClick={()=>setShowNotifDropdown(false)} style={{cursor:"pointer",color:"#5a7278",fontSize:16}}>✕</span>
              </div>
              {notifications.length===0&&<div style={{padding:24,textAlign:"center",color:"#5a7278",fontSize:12}}>No active alerts</div>}
              {notifications.map(n=>(
                <div key={n.id} onClick={()=>{setShowNotifDropdown(false); const inv=invoices.find(i=>i.id===n.invId); if(inv){setSelected(inv);setView("detail");}}} style={{padding:"12px 16px",borderBottom:"1px solid #f5f5f5",cursor:"pointer",display:"flex",gap:10,alignItems:"flex-start"}} className="hover-up">
                  <span style={{fontSize:16}}>{n.type==="stuck"?"⏱":n.type==="amount"?"💰":n.type==="urgent_unactioned"?"🔴":n.type==="no_pv"?"📋":"📅"}</span>
                  <div><div style={{fontSize:12,fontWeight:600,color:"#12262b"}}>{n.type==="stuck"?"Invoice Stuck":n.type==="amount"?"High Value":n.type==="urgent_unactioned"?"Urgent Unactioned":n.type==="no_pv"?"No PV Created":"Payment Overdue"}</div>
                  <div style={{fontSize:11,color:"#4f676d",marginTop:2}}>{n.msg}</div></div>
                </div>
              ))}
            </div>}
          </div>}
          {pendingIntakeCount>0&&(isAdmin||hasRole("pd"))&&<div onClick={()=>setView("intake")} className="hover-up" style={{cursor:"pointer",background:"#fdf3e3",border:"1px solid #f3d9ad",borderRadius:20,padding:"4px 12px",fontSize:11,color:"#7a4506",fontWeight:600}}>📥 {pendingIntakeCount}</div>}
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
      <div style={{maxWidth:1400,margin:"0 auto",padding:"16px 24px"}} className="fade-in">


        {/* MY QUEUE */}
        {view==="pendingDeletions"&&(
          <div style={{padding:24,maxWidth:900,margin:"0 auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{fontSize:18,fontWeight:700}}>📋 Pending Approvals</div>
                <div style={{fontSize:12,color:"#4f676d",marginTop:4}}>Deletion and restoration requests awaiting CFO approval</div>
              </div>
              <button className="hover-up" style={S.btnSec} onClick={()=>setView(prevView||"dashboard")}>← Back</button>
            </div>
            <div style={S.card}>
              {(()=>{
                const merged = [
                  ...pendingDeletions.invoices.map(x=>({...x,_type:"invoice",_key:x.id,_label:x.id,_sub:x.supplier+" · AED "+Number(x.amount).toLocaleString()+" · "+(sortedStages.find(s=>s.id===x.stage)?.label||x.stage)})),
                  ...pendingDeletions.vouchers.map(x=>({...x,_type:"voucher",_key:x.no,_label:x.no,_sub:x.supplier}))
                ];
                if(merged.length===0) return <div style={{color:"#5a7278",fontSize:13}}>No pending requests</div>;
                return merged.map(item=>(
                  <div key={item._type+item._key} style={{padding:"14px 0",borderBottom:"1px solid #f5f5f5"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                          <span style={{fontWeight:600,color:"#0b7a87"}}>{item._label}</span>
                          <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:4,background:item._type==="invoice"?"#4a9eff18":"#a070f018",color:item._type==="invoice"?"#4a9eff":"#a070f0"}}>{item._type==="invoice"?"INVOICE":"VOUCHER"}</span>
                          <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:4,background:item.type==="pending_restore"?"#20c07018":"#ff444418",color:item.type==="pending_restore"?"#20c070":"#b42318",border:"0.5px solid "+(item.type==="pending_restore"?"#20c07040":"#ff444440")}}>{item.type==="pending_restore"?"RESTORE REQUEST":"DELETE REQUEST"}</span>
                        </div>
                        <div style={{fontSize:12,color:"#3b545a"}}>{item._sub}</div>
                        <div style={{fontSize:11,color:"#5a7278",marginTop:2}}>Requested by {item.requestedBy} on {item.requestedAt}</div>
                        {item.note&&<div style={{fontSize:11,color:"#7a4506",background:"#fdf3e3",border:"1px solid #e8a02040",borderRadius:6,padding:"4px 8px",marginTop:6,fontStyle:"italic"}}>Reason: "{item.note}"</div>}
                      </div>
                      <div style={{display:"flex",gap:8,flexShrink:0,marginLeft:16}}>
                        <button className="hover-up" style={{...S.btnSec,padding:"5px 10px",fontSize:11}} onClick={()=>{
                          const existing = item._type==="invoice"
                            ? (invoices.find(x=>x.id===item._key)||recycleBin.invoices.find(x=>x.id===item._key))
                            : (vouchers.find(x=>x.no===item._key)||recycleBin.vouchers.find(x=>x.no===item._key));
                          if(!existing){ showToast("Cannot load — try refreshing","error"); return; }
                          if(item._type==="voucher"){ openVoucher(existing); setPrevView("pendingDeletions"); return; }
                          openDetail({...existing,amount:Number(existing.amount)||0,attachments:existing.attachments||[],history:existing.history||[]});
                          setPrevView("pendingDeletions");
                        }}>👁 View</button>
                        {(hasRole("cfo")||hasRole("admin"))&&<>
                          <button className="hover-up" style={{...S.btn,padding:"5px 10px",fontSize:11,background:item.type==="pending_restore"?"#20c070":"#b42318"}} onClick={()=>approveDelete(item._key,item._type,item.type)}>✓ {item.type==="pending_restore"?"Approve Restore":"Approve Delete"}</button>
                          <button className="hover-up" style={{...S.btnSec,padding:"5px 10px",fontSize:11}} onClick={()=>rejectDelete(item._key,item._type)}>✕ Reject</button>
                        </>}
                        {!hasRole("cfo")&&!hasRole("admin")&&<div style={{fontSize:12,color:"#e8a020",fontWeight:600}}>Awaiting CFO</div>}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {view==="suppliers"&&(
          <div style={{maxWidth:700,margin:"0 auto"}}>
            <div style={{marginBottom:16}}>
              <div style={{...S.h,fontSize:22}}>Suppliers</div>
              <div style={{fontSize:13,color:"#4f676d",marginTop:2}}>{suppliers.length} supplier{suppliers.length===1?"":"s"} on file</div>
            </div>
            <div style={{...S.card,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>Add New Supplier</div>
              <div style={{display:"flex",gap:8}}>
                <input id="newSupplierInput" style={{...S.input,flex:1}} placeholder="Supplier name..." onKeyDown={e=>{
                  if(e.key==="Enter"){ const v=e.target.value.trim(); if(v){ addSupplier(v); e.target.value=""; } }
                }}/>
                <button className="hover-up" style={S.btn} onClick={()=>{
                  const el=document.getElementById("newSupplierInput");
                  const v=el.value.trim();
                  if(v){ addSupplier(v); el.value=""; }
                }}>+ Add</button>
              </div>
            </div>
            <div style={S.card}>
              {suppliers.length===0?<div style={{color:"#5a7278",fontSize:13,textAlign:"center",padding:20}}>No suppliers yet</div>:
                suppliers.map(s=>(
                  <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #edf2f3"}}>
                    <div>
                      <div style={{fontSize:13}}>{s.name}</div>
                      {s.createdBy&&<div style={{fontSize:11,color:"#5a7278"}}>Added by {s.createdBy}{s.createdAt?" on "+s.createdAt:""}</div>}
                    </div>
                    <button className="hover-up" style={{...S.btnDanger,padding:"4px 10px",fontSize:11}} onClick={()=>deleteSupplier(s.id)}>Remove</button>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {view==="recycleBin"&&(
          <div style={{padding:24,maxWidth:900,margin:"0 auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{fontSize:18,fontWeight:700}}>🗑 Recycle Bin</div>
                <div style={{fontSize:12,color:"#4f676d",marginTop:4}}>Deleted invoices and vouchers — restore or request restore</div>
              </div>
              <button className="hover-up" style={S.btnSec} onClick={()=>setView(prevView||"dashboard")}>← Back</button>
            </div>
            <div style={S.card}>
              {(()=>{
                const merged = [
                  ...recycleBin.invoices.map(x=>({...x,_type:"invoice",_key:x.id,_label:x.id,_sub:x.supplier+" · AED "+Number(x.amount).toLocaleString()})),
                  ...recycleBin.vouchers.map(x=>({...x,_type:"voucher",_key:x.no,_label:x.no,_sub:x.supplier}))
                ];
                if(merged.length===0) return <div style={{color:"#5a7278",fontSize:13}}>Recycle Bin is empty</div>;
                return merged.map(item=>(
                  <div key={item._type+item._key} style={{padding:"12px 0",borderBottom:"1px solid #f5f5f5"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontWeight:600,color:"#0b7a87"}}>{item._label}</span>
                          <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:4,background:item._type==="invoice"?"#4a9eff18":"#a070f018",color:item._type==="invoice"?"#4a9eff":"#a070f0"}}>{item._type==="invoice"?"INVOICE":"VOUCHER"}</span>
                        </div>
                        <div style={{fontSize:12,color:"#3b545a",marginTop:2}}>{item._sub}</div>
                        <div style={{fontSize:11,color:"#5a7278"}}>Deleted by {item.deletedBy} on {item.deletedAt}</div>
                      </div>
                      <div style={{display:"flex",gap:8,flexDirection:"column",alignItems:"flex-end"}}>
                        {item.deletion_status==="pending_restore"?
                          <span style={{fontSize:11,fontWeight:700,color:"#e8a020",background:"#fdf3e3",border:"1px solid #f3d9ad",borderRadius:20,padding:"3px 10px"}}>⏳ Sent to CFO</span>
                        :<>
                          {isAdmin&&<button className="hover-up" style={{...S.btn,padding:"6px 14px",fontSize:12}} onClick={()=>item._type==="invoice"?requestRestoreInvoice(item._key):requestRestoreVoucher(item._key)}>↩ Request Restore</button>}
                          {hasRole("cfo")&&<button className="hover-up" style={{...S.btn,padding:"6px 14px",fontSize:12,background:"#20c070"}} onClick={()=>item._type==="invoice"?directRestoreInvoice(item._key):directRestoreVoucher(item._key)}>↩ Restore Now</button>}
                          {!isAdmin&&!hasRole("cfo")&&<div style={{fontSize:11,color:"#5a7278"}}>Contact admin to restore</div>}
                        </>}
                      </div>
                    </div>
                    {item.deletion_status==="pending_restore"&&(
                      <div style={{fontSize:11,color:"#7a4506",background:"#fdf3e3",border:"1px solid #e8a02040",borderRadius:6,padding:"5px 10px",marginTop:8}}>
                        Requested by {item.restoreRequestedBy} on {item.restoreRequestedAt}
                        {item.restoreNote&&<div style={{fontStyle:"italic",marginTop:2}}>Reason: "{item.restoreNote}"</div>}
                      </div>
                    )}
                    {!item.deletion_status&&item.rejectionNote&&(
                      <div style={{fontSize:11,color:"#b42318",background:"#fde8e6",border:"1px solid #f3c6c140",borderRadius:6,padding:"5px 10px",marginTop:8}}>
                        ✕ Restore request rejected by {item.rejectedBy}
                        <div style={{fontStyle:"italic",marginTop:2}}>Reason: "{item.rejectionNote}"</div>
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {view==="myqueue"&&(()=>{
          const chipFiltered = myQueueFree.filter(inv=>queueChip==="all"||(queueChip==="urgent"&&inv.urgent)||(queueChip==="priority"&&inv.priority));
          const projFiltered = chipFiltered.filter(inv=>filterProject==="all"||inv.projectId===filterProject);
          const urgentTotal = myQueueFree.filter(i=>i.urgent).length;
          const priorityTotal = myQueueFree.filter(i=>i.priority).length;
          const exportCsv = () => {
            const rows = [["Invoice #","Supplier","Project","Stage","Amount","Waiting"]];
            projFiltered.forEach(inv=>{
              const proj=projects.find(p=>p.id===inv.projectId);
              rows.push([inv.id, inv.supplier, proj?.name||"", sortedStages.find(s=>s.id===inv.stage)?.label||inv.stage, inv.amount, inv._days+"d"]);
            });
            const csv = rows.map(r=>r.map(c=>'"'+String(c).replace(/"/g,'""')+'"').join(",")).join("\n");
            const blob = new Blob([csv],{type:"text/csv"});
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href=url; a.download="my-queue.csv"; a.click(); URL.revokeObjectURL(url);
          };
          return (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:16,flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{...S.h,fontSize:26}}>My Queue</div>
                <div style={{fontSize:13,color:"#4f676d",marginTop:3}}>{myQueueFree.length+myQueueVouchers.length===0?"All caught up!":"Priority first, then urgent, then oldest."}</div>
              </div>
              {myQueueFree.length>0&&<button className="hover-up" style={S.btnSec} onClick={exportCsv}>Export</button>}
            </div>
            {(isAdmin||hasRole("dc")||hasRole("cfo"))&&pendingDeletionCount>0&&<div className="hover-up" onClick={()=>{setPrevView(view);loadPendingDeletions();loadRecycleBin();setView("pendingDeletions");}} style={{...S.card,cursor:"pointer",background:"#fdf3e3",border:"1px solid #f3d9ad",marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
              <div style={{fontSize:22}}>📋</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:13,color:"#7a4506"}}>{pendingDeletionCount} Pending Approval{pendingDeletionCount>1?"s":""}</div>
                <div style={{fontSize:11,color:"#4f676d"}}>Deletion/restore requests awaiting CFO decision — tap to review</div>
              </div>
              <div style={{fontSize:13,color:"#7a4506",fontWeight:600}}>View →</div>
            </div>}
            {myQueueVouchers.length>0&&<div>
              <div style={{fontSize:11,fontWeight:600,color:"#1a9a60",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>📄 Vouchers</div>
              {myQueueVouchers.map(v=>{
                const vI=invoices.filter(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed");
                const tot=vI.reduce((s,i)=>s+i.amount,0); const cs=vCurStage(v);
                const anyPriority=vI.some(i=>i.priority); const anyUrgent=vI.some(i=>i.urgent);
                return <div key={v.no} className="hover-up" onClick={()=>openVoucher(v)} style={{...S.card,cursor:"pointer",border:"1px solid #b7ecd2",borderLeft:anyPriority?"3px solid #d97706":anyUrgent?"3px solid #b42318":undefined}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}><div style={{fontSize:22}}>📄</div><div><div style={{display:"flex",alignItems:"center",gap:6}}><div style={{...S.h,fontSize:15,color:"#1a9a60"}}>{v.no}</div>{anyPriority&&<span style={{background:"#fdecd6",color:"#8a4b08",border:"0.5px solid #f3c98a",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:600}}>priority</span>}{anyUrgent&&<span style={{background:"#fde8e6",color:"#912018",border:"0.5px solid #f3c6c1",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:600}}>urgent</span>}</div><div style={{fontSize:11,color:"#4f676d"}}>{v.supplier} · {vI.length} invoice(s)</div></div></div>
                    <div style={{textAlign:"right"}}><div style={{fontWeight:600,fontSize:13}}>AED {tot.toLocaleString()}</div>{cs&&<Badge stage={cs} stages={sortedStages}/>}</div>
                  </div>
                </div>;
              })}
            </div>}
            {myQueueFree.length>0&&<>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                {[["all","All",myQueueFree.length],["urgent","Urgent",urgentTotal],["priority","Priority",priorityTotal]].map(c=>(
                  <button key={c[0]} onClick={()=>setQueueChip(c[0])} style={{height:36,boxSizing:"border-box",padding:"0 14px",borderRadius:18,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,background:queueChip===c[0]?"#0b7a87":"#fff",color:queueChip===c[0]?"#fff":"#3b545a",border:"1px solid "+(queueChip===c[0]?"#0b7a87":"#d5e1e4")}}>{c[1]} {c[2]}</button>
                ))}
                <div style={{flex:1}}/>
                <select style={{...S.input,width:170}} value={filterProject} onChange={e=>setFilterProject(e.target.value)}>
                  <option value="all">All projects</option>
                  {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
                  <thead><tr>{["Invoice #","Supplier","Project","Stage","Amount","Waiting",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{projFiltered.map((inv,i)=>{
                    const proj=projects.find(p=>p.id===inv.projectId);
                    const rowBorder = inv.priority?"3px solid #d97706":inv.urgent?"3px solid #b42318":"3px solid transparent";
                    return <tr key={inv.id} className="inv-row" style={{borderTop:"1px solid #edf2f3",borderLeft:rowBorder}}>
                      <td style={{...S.td,fontWeight:600}}>
                        <span style={{color:"#0b7a87"}}>{inv.id}</span>
                        <div style={{display:"flex",gap:4,marginTop:3}}>
                          {inv.priority&&<span style={{background:"#fdecd6",color:"#8a4b08",border:"0.5px solid #f3c98a",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>priority</span>}{inv.supplierPending&&<span style={{background:"#fdf3e3",color:"#7a4506",border:"0.5px solid #f3d9ad",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>⚠ new supplier</span>}
                          {inv.urgent&&<span style={{background:"#fde8e6",color:"#912018",border:"0.5px solid #f3c6c1",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>urgent</span>}
                        </div>
                      </td>
                      <td style={S.td}>{inv.supplier}</td>
                      <td style={S.td}><ProjBadge proj={proj}/></td>
                      <td style={S.td}><Badge stage={inv.stage} stages={sortedStages}/></td>
                      <td style={{...S.td,fontWeight:600}}>AED {inv.amount.toLocaleString()}</td>
                      <td style={S.td}><DaysBadge days={inv._days}/></td>
                      <td style={S.td}><button className="hover-up" style={S.btnSm} onClick={()=>openDetail(inv)}>Open</button></td>
                    </tr>;
                  })}
                  {projFiltered.length===0&&<tr><td colSpan={7} style={{padding:24,textAlign:"center",color:"#4f676d"}}>No invoices match this filter.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{fontSize:13,color:"#4f676d",marginTop:8}}>Showing {projFiltered.length} of {myQueueFree.length}. Click any row to open the invoice.</div>
            </>}
            {!myQueueFree.length&&!myQueueVouchers.length&&<div style={{...S.card,textAlign:"center",padding:48,color:"#4f676d"}}>🎉 Nothing needs your action.</div>}
          </div>
          );
        })()}

        {/* DASHBOARD */}
        {view==="dashboard"&&(()=>{
          const waitingCount = myQueueFree.length + myQueueVouchers.length;
          const urgentWaiting = myQueueFree.filter(i=>i.urgent).length;
          const activeCount = invoices.filter(i=>i.stage!=="completed").length;
          const nonFiledStageCount = sortedStages.filter(s=>s.id!=="completed").length;
          const readyPaidCount = stageCounts["ready_paid"]||0;
          const readyPaidStage = sortedStages.find(s=>s.id==="ready_paid");
          const readyPaidOwner = readyPaidStage?ROLE_LABELS[readyPaidStage.ownerRole]||"":"";
          const queuePreview = [
            ...myQueueVouchers.map(v=>{
              const vI=invoices.filter(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed");
              return {_type:"voucher",_key:v.no,v,tot:vI.reduce((s,i)=>s+i.amount,0),cs:vCurStage(v)};
            }),
            ...myQueueFree.map(inv=>({_type:"invoice",_key:inv.id,inv}))
          ].slice(0,5);
          const recentActivity = (isAdmin?auditLog:auditLog.filter(a=>a.role!=="admin")).slice(0,5);
          return (
            <div>
              <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{...S.h,fontSize:28}}>{greeting()}, {currentUser.name?.split(" ")[0]}</div>
                  <div style={{fontSize:14,color:"#4f676d",marginTop:2}}>Here's where every invoice stands right now.</div>
                </div>
                <span style={{fontSize:12,color:"#5a7278"}}>Updated {timeAgo(lastSync)}</span>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:20}}>
                <div className="hover-up" onClick={()=>!isPureAdmin&&setView("myqueue")} style={{background:"#0b7a87",borderRadius:16,padding:"18px 20px",color:"#fff",cursor:isPureAdmin?"default":"pointer",display:"flex",flexDirection:"column",gap:6}}>
                  <span style={{fontSize:13,fontWeight:500}}>Waiting on you</span>
                  <span style={{...S.h,fontSize:36,color:"#fff"}}>{waitingCount}</span>
                  <span style={{fontSize:12,opacity:0.9}}>{waitingCount===0?"All caught up":urgentWaiting>0?urgentWaiting+" marked urgent":"in your queue"}</span>
                </div>
                <div style={{...S.card,margin:0,padding:"18px 20px",display:"flex",flexDirection:"column",gap:6}}>
                  <span style={{fontSize:13,fontWeight:500,color:"#3b545a"}}>In workflow</span>
                  <span style={{...S.h,fontSize:36}}>{activeCount}</span>
                  <span style={{fontSize:12,color:"#4f676d"}}>across {nonFiledStageCount} stages</span>
                </div>
                <div className="hover-up" onClick={()=>{setFilterStage("ready_paid");}} style={{...S.card,margin:0,padding:"18px 20px",cursor:"pointer",display:"flex",flexDirection:"column",gap:6}}>
                  <span style={{fontSize:13,fontWeight:500,color:"#3b545a"}}>Ready to be paid</span>
                  <span style={{...S.h,fontSize:36}}>{readyPaidCount}</span>
                  <span style={{fontSize:12,color:"#4f676d"}}>{readyPaidOwner?"with "+readyPaidOwner:"—"}</span>
                </div>
                <div className="hover-up" onClick={()=>{if(isAdmin||hasRole("dc")||hasRole("cfo")){setPrevView("dashboard");loadPendingDeletions();loadRecycleBin();setView("pendingDeletions");}}} style={{...S.card,margin:0,padding:"18px 20px",cursor:(isAdmin||hasRole("dc")||hasRole("cfo"))?"pointer":"default",display:"flex",flexDirection:"column",gap:6}}>
                  <span style={{fontSize:13,fontWeight:500,color:"#3b545a"}}>Pending approvals</span>
                  <span style={{...S.h,fontSize:36}}>{pendingDeletionCount}</span>
                  <span style={{fontSize:12,color:"#4f676d"}}>deletion and restore requests</span>
                </div>
              </div>

              <section style={{...S.card,marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{...S.h,fontSize:16}}>Invoice pipeline</div>
                  <span onClick={()=>setFilterStage("all")} style={{fontSize:13,fontWeight:600,color:"#0b7a87",cursor:"pointer"}}>View all invoices</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(90px,1fr))",gap:8}}>
                  {sortedStages.filter(s=>s.id!=="completed").map(s=>{
                    const owned = canAct(s.id);
                    const active = filterStage===s.id;
                    return (
                      <div key={s.id} className="hover-up" onClick={()=>{ if(s.ownerRole==="pd"){ if(isAdmin||hasRole("pd")) setView("intake"); return; } setFilterStage(filterStage===s.id?"all":s.id); }} style={{borderRadius:12,padding:"12px 10px",cursor:"pointer",border:active?"2px solid "+s.color:owned?"1px solid #b7e3e8":"1px solid #e3ebed",background:active?s.color+"14":owned?"#e3f5f7":"#f4f8f9"}}>
                        <div style={{...S.h,fontSize:22,color:owned||active?"#085c66":"#12262b"}}>{s.ownerRole==="pd"?pendingIntakeCount:(stageCounts[s.id]||0)}</div>
                        <div style={{fontSize:11,fontWeight:500,color:owned||active?"#12262b":"#4f676d",marginTop:3,lineHeight:1.3}}>{s.ownerRole==="pd"?"PD Approval (pending)":s.label}</div>
                      </div>
                    );
                  })}
                  <div className="hover-up" onClick={()=>setView("archive")} style={{borderRadius:12,padding:"12px 10px",cursor:"pointer",border:"1px solid #b7ecd2",background:"#e9faf1"}}>
                    <div style={{...S.h,fontSize:22,color:"#1a9a60"}}>{archivedInvs.length}</div>
                    <div style={{fontSize:11,fontWeight:500,color:"#12262b",marginTop:3}}>Filed</div>
                  </div>
                </div>
                <div style={{fontSize:12,color:"#5a7278",marginTop:10}}>Tinted stages are the ones you own.</div>
              </section>

              <div style={{display:"grid",gridTemplateColumns:"minmax(0,2fr) minmax(0,1fr)",gap:16,marginBottom:16}}>
                <section style={{...S.card,margin:0,padding:0,overflow:"hidden",display:"flex",flexDirection:"column"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 10px"}}>
                    <div style={{...S.h,fontSize:16}}>My Queue</div>
                    {!isPureAdmin&&<span onClick={()=>setView("myqueue")} style={{fontSize:13,fontWeight:600,color:"#0b7a87",cursor:"pointer"}}>Open queue</span>}
                  </div>
                  {queuePreview.length===0?
                    <div style={{padding:"20px",textAlign:"center",color:"#4f676d",fontSize:13}}>{isPureAdmin?"Admin accounts don't carry a personal queue.":"🎉 Nothing needs your action."}</div>
                  :<>
                    <div style={{display:"grid",gridTemplateColumns:"1.1fr 1.4fr 1fr 0.9fr 0.7fr",gap:8,padding:"8px 20px",background:"#f4f8f9",fontSize:11,fontWeight:600,letterSpacing:"0.04em",color:"#4f676d"}}>
                      <span>INVOICE</span><span>SUPPLIER</span><span>STAGE</span><span>AMOUNT</span><span>WAITING</span>
                    </div>
                    {queuePreview.map((item,i)=>item._type==="voucher"?(
                      <div key={"v-"+item._key} onClick={()=>openVoucher(item.v)} className="hover-up inv-row" style={{display:"grid",gridTemplateColumns:"1.1fr 1.4fr 1fr 0.9fr 0.7fr",gap:8,alignItems:"center",padding:"10px 20px",borderTop:i?"1px solid #edf2f3":"none",cursor:"pointer",fontSize:13}}>
                        <span style={{fontWeight:600,color:"#1a9a60"}}>📄 {item.v.no}</span>
                        <span style={{color:"#3b545a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.v.supplier}</span>
                        {item.cs?<Badge stage={item.cs} stages={sortedStages}/>:<span/>}
                        <span style={{fontWeight:500}}>AED {item.tot.toLocaleString()}</span>
                        <span style={{fontSize:12,color:"#5a7278"}}>—</span>
                      </div>
                    ):(
                      <div key={item._key} onClick={()=>openDetail(item.inv)} className="hover-up inv-row" style={{display:"grid",gridTemplateColumns:"1.1fr 1.4fr 1fr 0.9fr 0.7fr",gap:8,alignItems:"center",padding:"10px 20px",borderTop:i?"1px solid #edf2f3":"none",cursor:"pointer",fontSize:13}}>
                        <span style={{fontWeight:600,color:"#0b7a87"}}>{item.inv.id}</span>
                        <span style={{color:"#3b545a",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.inv.supplier}</span>
                        <Badge stage={item.inv.stage} stages={sortedStages}/>
                        <span style={{fontWeight:500}}>AED {item.inv.amount.toLocaleString()}</span>
                        <DaysBadge days={item.inv._days}/>
                      </div>
                    ))}
                  </>}
                </section>

                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  <section style={{...S.card,margin:0}}>
                    <div style={{...S.h,fontSize:16,marginBottom:12}}>Needs attention</div>
                    {notifications.length===0?<div style={{fontSize:13,color:"#4f676d"}}>Nothing flagged right now.</div>:
                      notifications.slice(0,3).map(n=>(
                        <div key={n.id} onClick={()=>{const inv=invoices.find(i=>i.id===n.invId); if(inv){setSelected(inv);setView("detail");}}} className="hover-up" style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 0",cursor:"pointer"}}>
                          <span style={{fontSize:16}}>{n.type==="stuck"?"⏱":n.type==="amount"?"💰":n.type==="urgent_unactioned"?"🔴":n.type==="no_pv"?"📋":"📅"}</span>
                          <div>
                            <div style={{fontSize:13,fontWeight:600}}>{n.type==="stuck"?"Stuck invoice":n.type==="amount"?"High value":n.type==="urgent_unactioned"?"Urgent unactioned":n.type==="no_pv"?"No PV created":"Payment overdue"}</div>
                            <div style={{fontSize:12,color:"#4f676d",marginTop:1}}>{n.msg}</div>
                          </div>
                        </div>
                      ))
                    }
                  </section>
                  <section style={{...S.card,margin:0,flex:1}}>
                    <div style={{...S.h,fontSize:16,marginBottom:12}}>Recent activity</div>
                    {recentActivity.length===0?<div style={{fontSize:13,color:"#4f676d"}}>No activity logged yet.</div>:
                      recentActivity.map(a=>(
                        <div key={a.id} style={{display:"flex",gap:10,alignItems:"center",padding:"6px 0"}}>
                          <span style={{width:7,height:7,borderRadius:4,background:"#0b7a87",flexShrink:0}}/>
                          <span style={{fontSize:12,color:"#3b545a",flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.user||a.username||"Someone"} {a.action.toLowerCase()}</span>
                          <span style={{fontSize:11,color:"#5a7278",flexShrink:0}}>{timeAgo(a.ts)}</span>
                        </div>
                      ))
                    }
                  </section>
                </div>
              </div>

            {(isAdmin||hasRole("dc"))&&invoices.some(i=>i.supplierPending)&&(
              <div style={{background:"#fdf3e3",border:"0.5px solid #f3d9ad",borderRadius:12,padding:"12px 16px",marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:700,color:"#7a4506",marginBottom:8}}>⚠ {invoices.filter(i=>i.supplierPending).length} new supplier(s) waiting for your verification</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {invoices.filter(i=>i.supplierPending).map(i=><button key={i.id} className="hover-up" style={S.btnSm} onClick={()=>openDetail(i)}>{i.invoiceNo||i.id} · {i.supplier} - Verify</button>)}
                </div>
              </div>
            )}
            {(()=>{
              const q=(search||"").trim().toLowerCase();
              const act=vouchers.map(v=>({v,vI:invoices.filter(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed")})).filter(x=>x.vI.length>0)
                .filter(x=>!q||[x.v.no,x.v.supplier,...x.vI.map(i=>i.invoiceNo||i.id)].some(t=>String(t||"").toLowerCase().includes(q)));
              if(!vouchers.some(v=>invoices.some(i=>v.invoiceIds.includes(i.id)&&i.stage!=="completed"))) return null;
              return (<>
                <div style={{...S.h,fontSize:16,margin:"4px 0 10px"}}>Active PVs <span style={{fontSize:12,color:"#5a7278",fontWeight:400}}>({act.length})</span></div>
                <div style={{...S.card,padding:0,overflow:"hidden",marginBottom:18}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
                      <thead><tr>{["PV #","Supplier","Date","Invoices","Amount","Stage","In Stage",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {act.map(({v,vI},k)=>{
                          const cs=vCurStage(v);
                          const tot=vI.reduce((t,i)=>t+i.amount,0);
                          const atStage=vI.filter(i=>i.stage===cs);
                          const d=daysDiff((atStage[0]||vI[0]).history[(atStage[0]||vI[0]).history.length-1].date);
                          const pr=vI.some(i=>i.priority), ur=vI.some(i=>i.urgent);
                          return <tr key={v.no} className="inv-row" style={{borderTop:"1px solid #edf2f3",background:k%2?"#fafcfc":"#fff",borderLeft:pr?"3px solid #f59e0b":ur?"3px solid #ef4444":"3px solid transparent"}}>
                            <td style={{...S.td,fontWeight:600}}>
                              <span style={{color:"#1a9a60"}}>{v.no}</span>
                              <div style={{display:"flex",gap:4,marginTop:3,flexWrap:"wrap"}}>
                                {pr&&<span style={{background:"#fdecd6",color:"#8a4b08",border:"0.5px solid #f3c98a",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>priority</span>}
                                {ur&&<span style={{background:"#fde8e6",color:"#912018",border:"0.5px solid #f3c6c1",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>urgent</span>}
                              </div>
                            </td>
                            <td style={S.td}>{v.supplier}</td>
                            <td style={{...S.td,fontSize:12,color:"#4f676d"}}>{v.date}</td>
                            <td style={{...S.td,fontSize:12}}>{vI.map(i=>i.invoiceNo||i.id).join(", ")}</td>
                            <td style={{...S.td,fontWeight:600}}>AED {tot.toLocaleString()}</td>
                            <td style={S.td}>{cs&&<Badge stage={cs} stages={sortedStages}/>}</td>
                            <td style={S.td}><DaysBadge days={d}/></td>
                            <td style={S.td}><button className="hover-up" style={S.btnSm} onClick={()=>openVoucher(v)}>View</button></td>
                          </tr>;
                        })}
                        {act.length===0&&<tr><td colSpan={8} style={{padding:24,textAlign:"center",color:"#4f676d"}}>No matching PVs</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>);
            })()}
            <div style={{...S.h,fontSize:16,margin:"4px 0 10px"}}>All Invoices</div>
            <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
              <input style={{...S.input,flex:1,minWidth:160}} placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/>
              <select style={{...S.input,width:160}} value={filterProject} onChange={e=>setFilterProject(e.target.value)}>
                <option value="all">All Projects</option>
                {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{...S.card,padding:0,overflow:"hidden"}}>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
                  <thead><tr>{["Invoice #","Supplier","Project","Amount","Received","Method","Terms","Stage","In Stage",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filtered.filter(i=>i.stage!=="completed").map((inv,i)=>{
                      const d=daysDiff(inv.history[inv.history.length-1].date);
                      const proj=projects.find(p=>p.id===inv.projectId);
                      return <tr key={inv.id} className="inv-row" style={{borderTop:"1px solid #edf2f3",background:inv.deletion_status?"#fdf3e3":i%2?"#fafcfc":"#fff",borderLeft:inv.deletion_status?"3px solid #e8a020":inv.priority?"3px solid #f59e0b":inv.urgent?"3px solid #ef4444":"3px solid transparent"}}>
                        <td style={{...S.td,fontWeight:600}}>
                          <span style={{color:"#0b7a87"}}>{inv.id}</span>
                          <div style={{display:"flex",gap:4,marginTop:3,flexWrap:"wrap"}}>
                            {inv.deletion_status==="pending"&&<span style={{background:"#fdf3e3",color:"#7a4506",border:"0.5px solid #f3d9ad",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:600}}>⏳ deletion requested</span>}
                            {inv.deletion_status==="pending_restore"&&<span style={{background:"#e9faf1",color:"#1a9a60",border:"0.5px solid #a0e0c0",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:600}}>⏳ restore requested</span>}
                            {inv.priority&&<span style={{background:"#fdecd6",color:"#8a4b08",border:"0.5px solid #f3c98a",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>priority</span>}{inv.supplierPending&&<span style={{background:"#fdf3e3",color:"#7a4506",border:"0.5px solid #f3d9ad",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>⚠ new supplier</span>}
                            {inv.urgent&&<span style={{background:"#fde8e6",color:"#912018",border:"0.5px solid #f3c6c1",borderRadius:4,padding:"1px 7px",fontSize:11,fontWeight:500}}>urgent</span>}
                          </div>
                        </td>
                        <td style={S.td}>{inv.supplier}</td>
                        <td style={S.td}><ProjBadge proj={proj}/></td>
                        <td style={{...S.td,fontWeight:600}}>AED {inv.amount.toLocaleString()}</td>
                        <td style={{...S.td,fontSize:12,color:"#4f676d"}}>{inv.receivedDate}</td>
                        <td style={{...S.td,fontSize:12}}>{fmtMethod(inv)}</td>
                        <td style={{...S.td,fontSize:12}}>{fmtTerms(inv)}</td>
                        <td style={S.td}><Badge stage={inv.stage} stages={sortedStages}/></td>
                        <td style={S.td}><DaysBadge days={d}/></td>
                        <td style={S.td}><button className="hover-up" style={S.btnSm} onClick={()=>openDetail(inv)}>View</button></td>
                      </tr>;
                    })}
                    {filtered.filter(i=>i.stage!=="completed").length===0&&<tr><td colSpan={10} style={{padding:32,textAlign:"center",color:"#4f676d"}}>No invoices</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          );
        })()}

        {/* INVOICE DETAIL */}
        {view==="detail"&&selected&&(()=>{
          const inv=invoices.find(i=>i.id===selected.id)||selected;
          const locked=isLockedInPV(inv,vouchers);
          const ci=stageIds.indexOf(inv.stage);
          const nextSt=sortedStages.slice(ci+1,ci+2);
          const prevSt=sortedStages.slice(0,ci).filter(s=>s.ownerRole!=="pd");
          const pendingDel = !!inv.deletion_status || vouchers.some(v=>v.no===inv.voucherNo && v.deletion_status);
          const allowed=(isAdmin||canAct(inv.stage)) && !pendingDel;
          const isFiled=inv.stage==="completed";
          const pvObj=locked?vouchers.find(v=>v.no===inv.voucherNo):null;
          const pvStageIdsDetail = sortedStages.filter(s=>s.requiresPV).map(s=>s.id);
          const hadPVBeforeDetail = inv.history?.some(h=>pvStageIdsDetail.includes(h.stage));
          const ciDetail = stageIds.indexOf(inv.stage);
          const hasUpcomingPVStageDetail = pvStageIdsDetail.some(id=>stageIds.indexOf(id)>ciDetail);
          const isAcct = hasRole("accountant") && !inv.voucherNo && hasUpcomingPVStageDetail && !hadPVBeforeDetail;
          const proj=projects.find(p=>p.id===inv.projectId);
          const d=daysDiff(inv.history[inv.history.length-1].date);
          const ownerLabel=ROLE_LABELS[sortedStages.find(s=>s.id===inv.stage)?.ownerRole]||"";
          return (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,marginBottom:10}}>
                <span onClick={goBackFromDetail} style={{fontWeight:600,color:"#0b7a87",cursor:"pointer"}}>{prevView==="myqueue"?"My Queue":prevView==="vouchers"?"Vouchers":"Dashboard"}</span>
                <span style={{color:"#5a7278"}}>/</span>
                <span style={{color:"#3b545a"}}>{inv.id}</span>
              </div>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16,marginBottom:16,flexWrap:"wrap"}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{...S.h,fontSize:24}}>Invoice {inv.id}</span>
                    {inv.urgent&&<span style={{height:24,boxSizing:"border-box",padding:"0 10px",borderRadius:12,background:"#fde8e6",color:"#912018",fontSize:12,fontWeight:600,display:"flex",alignItems:"center"}}>Urgent</span>}
                    {inv.priority&&<span style={{height:24,boxSizing:"border-box",padding:"0 10px",borderRadius:12,background:"#fdecd6",color:"#8a4b08",fontSize:12,fontWeight:600,display:"flex",alignItems:"center"}}>Priority</span>}
                    <Badge stage={inv.stage} stages={sortedStages}/>
                  </div>
                  <div style={{fontSize:14,color:"#4f676d",marginTop:4}}>{inv.supplier} · AED {inv.amount.toLocaleString()}{ownerLabel?" · with "+ownerLabel+" for "+d+" day"+(d===1?"":"s"):""}</div>
                </div>
                <button className="hover-up" style={S.btnSec} onClick={goBackFromDetail}>← Back</button>
              </div>
              {locked&&<div style={{background:"#e9faf1",border:"2px solid #b7ecd2",borderRadius:14,padding:"12px 18px",marginBottom:14,display:"flex",gap:12,alignItems:"center"}}>
                <div style={{fontSize:22}}>🔒</div>
                <div style={{flex:1}}><div style={{fontWeight:600,color:"#1a9a60",fontSize:13}}>Grouped under {inv.voucherNo}</div><div style={{fontSize:12,color:"#4f676d"}}>Move via Voucher page.</div></div>
                <button className="hover-up" style={{...S.btn,background:"#1a9a60",fontSize:12,padding:"7px 14px"}} onClick={()=>pvObj&&openVoucher(pvObj)}>Open PV</button>
              </div>}
              {inv.deletion_status==="pending"&&<div style={{background:"#fdf3e3",border:"2px solid #f3d9ad",borderRadius:14,padding:"12px 18px",marginBottom:14}}>
                <div style={{fontWeight:600,color:"#7a4506",fontSize:13}}>⏳ Deletion Requested</div>
                <div style={{fontSize:12,color:"#4f676d",marginTop:2}}>Requested by {inv.deletionRequestedBy||"—"}. Awaiting CFO approval — no further action possible until this is resolved.</div>
              </div>}
              {inv.deletion_status==="pending_restore"&&<div style={{background:"#e9faf1",border:"2px solid #1a9a60",borderRadius:14,padding:"12px 18px",marginBottom:14}}>
                <div style={{fontWeight:600,color:"#1a9a60",fontSize:13}}>⏳ Restore Requested</div>
                <div style={{fontSize:12,color:"#4f676d",marginTop:2}}>Awaiting CFO approval.</div>
              </div>}
              <div style={S.card}><StageStepper currentStage={inv.stage} stages={sortedStages}/></div>
              <div style={{position:"relative",...S.card,overflow:"hidden"}}>
                {isFiled&&<Confetti/>}
                <div style={{...S.h,fontSize:15,marginBottom:14}}>{isFiled?"🎉 Journey Complete!":"📍 Journey"}</div>
                {inv.history.map((h,i)=>{
                  const s=sortedStages.find(x=>x.id===h.stage);
                  const dur=stageDuration(inv.history,i);
                  const isLast=i===inv.history.length-1;
                  const sender=h.by;
                  const icon=stageIcons[stageIds.indexOf(h.stage)]||"📋";
                  const isReturned = h.note?.startsWith("Returned:");
                  const displayNote = isReturned ? h.note.replace("Returned:","").trim() : h.note;
                  return (
                    <div key={i} style={{display:"flex",gap:12,marginBottom:isLast?0:16,...(isReturned?{background:"#fde8e6",borderRadius:10,padding:"8px 10px",marginLeft:-10,marginRight:-10}:{})}}>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:isReturned?"#b42318":(s||{}).color||"#0b7a87",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>{isReturned?"↩️":icon}</div>
                        {!isLast&&<div style={{width:2,flex:1,background:"linear-gradient(to bottom,"+(isReturned?"#b42318":(s||{}).color||"#0b7a87")+",#e3ebed)",marginTop:4,minHeight:14}}/>}
                      </div>
                      <div style={{flex:1,paddingBottom:isLast?0:4}}>
                        <div style={{fontWeight:600,fontSize:12,color:isReturned?"#b42318":(s||{}).color}}>{(s||{}).label}{isReturned&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,background:"#fde8e6",color:"#b42318",border:"1px solid #f3c6c1",borderRadius:20,padding:"1px 8px"}}>RETURNED</span>}</div>
                        <div style={{fontSize:11,color:"#4f676d"}}>{h.date} · <b>{h.by}</b></div>
                        {h.note&&<div style={{marginTop:6,background:isReturned?"#fde8e6":"#fdf3e3",borderRadius:6,padding:"7px 12px",fontSize:12,color:"#12262b",borderLeft:"3px solid "+(isReturned?"#b42318":"#e8a020"),display:"flex",gap:6,alignItems:"flex-start"}}>
                          <span style={{flexShrink:0}}>{isReturned?"↩️":"💬"}</span>
                          <div><b style={{color:isReturned?"#b42318":"#7a4506",fontSize:11}}>{sender} {isReturned?"returned with reason":"noted"}:</b><br/>{displayNote}</div>
                        </div>}
                        <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                          <span style={{fontSize:10,color:"#5a7278"}}>{isLast&&!isFiled?"ongoing ":""}{dur}d</span>
                          {isAdmin&&<button className="hover-up" style={{...S.btnWarn,padding:"1px 8px",fontSize:10}} onClick={()=>{setEditingHist({invId:inv.id,idx:i});setHistEdit({stage:h.stage,date:h.date,note:h.note||"",by:h.by||""});}}>Edit</button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {inv.supplierPending&&<SupplierVerifyCard inv={inv} suppliers={suppliers} canVerify={isAdmin||hasRole("dc")} onVerify={verifySupplier}/>}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={S.card}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
                    <div style={{...S.h,fontSize:15}}>Details</div>
                    {!pendingDel&&(allowed||isAdmin||hasRole("dc"))&&<button className="hover-up" style={S.btnWarn} onClick={()=>setEditingInv({...inv,_origId:inv.id,_locked:locked})}>Edit</button>}
                    {!pendingDel&&!(allowed||isAdmin||hasRole("dc"))&&(hasRole("pd")||hasRole("om"))&&<button className="hover-up" style={S.btnWarn} onClick={()=>setEditingInv({...inv,_origId:inv.id,_locked:true,_flagsOnly:true})}>⚑ Priority</button>}
                    {(hasRole("admin")||hasRole("dc"))&&!locked&&!pendingDel&&<button className="hover-up" style={{...S.btnDanger,fontSize:11,padding:"5px 12px"}} onClick={()=>softDeleteInvoice(inv)}>🗑</button>}
                  </div>
                  {(()=>{
                    const originSub = pendingSubs.find(s=>s.processedInvoiceId===inv.id);
                    const rows = [
                      originSub?["Submission #",<span style={{color:"#a070f0",fontWeight:600}}>{originSub.displayId||originSub.id}</span>]:null,
                      ["Invoice #",<span style={{color:"#0b7a87",fontWeight:600}}>{inv.id}</span>],
                      ["LPO",inv.lpo||"—"],
                      ["Supplier",inv.supplier],
                      ["Amount","AED "+inv.amount.toLocaleString()],
                      ["Project",<ProjBadge proj={proj}/>],
                      ["Received",inv.receivedDate],
                      ["Method",fmtMethod(inv)],
                      ["Terms",fmtTerms(inv)],
                      inv.pvRef?["PV Ref",<span style={{color:"#1a9a60",fontWeight:600}}>{inv.pvRef}</span>]:null,
                      inv.fileNumber?["File No",<span style={{color:"#1a9a60",fontWeight:600}}>{inv.fileNumber}</span>]:null
                    ];
                    return rows.filter(Boolean).map(([k,v],i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #edf2f3"}}><span style={{color:"#4f676d",fontSize:12}}>{k}</span><span style={{fontSize:12}}>{v}</span></div>
                    ));
                  })()}
                  <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0"}}><span style={{color:"#4f676d",fontSize:12}}>Stage</span><Badge stage={inv.stage} stages={sortedStages}/></div>
                  <AttachmentsPanel inv={inv} token={localStorage.getItem("token")} showToast={showToast} currentUser={currentUser} onAdd={att=>{setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,attachments:[...(i.attachments||[]),att]})); try{logAudit("Attachment Added",inv.id+" | "+att.name);}catch(_){}}} onDelete={key=>{setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,attachments:(i.attachments||[]).filter(a=>a.key!==key)})); try{logAudit("Attachment Removed",inv.id+" | key:"+key);}catch(_){}}}/>
                </div>
                <div>
                  {!isFiled&&(locked
                    ?<div style={{...S.card,border:"2px solid #b7ecd2"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"#1a9a60",marginBottom:8}}>🔒 Grouped</div>
                      <div style={{fontSize:13,color:"#3b545a",marginBottom:12}}>Inside <b>{inv.voucherNo}</b>. Move via Voucher.</div>
                      <AttachmentsPanel inv={inv} token={localStorage.getItem("token")} showToast={showToast} currentUser={currentUser} onAdd={att=>{setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,attachments:[...(i.attachments||[]),att]})); setSelected(s=>({...s,attachments:[...(s.attachments||[]),att]})); try{logAudit("Attachment Added",inv.id+" | "+att.name);}catch(_){}}} onDelete={key=>{setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,attachments:(i.attachments||[]).filter(a=>a.key!==key)})); setSelected(s=>({...s,attachments:(s.attachments||[]).filter(a=>a.key!==key)})); try{logAudit("Attachment Removed",inv.id+" | key:"+key);}catch(_){}}}/>
                      {pvObj&&<button className="hover-up" style={{...S.btn,width:"100%",background:"#1a9a60"}} onClick={()=>openVoucher(pvObj)}>Go to Voucher</button>}
                    </div>
                    :allowed
                      ?<div style={S.card}>
                        <div style={{...S.h,fontSize:15,marginBottom:12}}>Move Invoice</div>
                        {(()=>{
                          const lastHist = inv.history[inv.history.length-1];
                          const prevHist = inv.history[inv.history.length-2];
                          const isReturned = lastHist?.note?.startsWith("Returned:");
                          if(!isReturned) return null;
                          const reason = lastHist.note.replace("Returned:","").trim();
                          return (
                            <div style={{background:"#fdf3e3",border:"1px solid #f3d9ad",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                              <div style={{fontSize:12,fontWeight:600,color:"#7a4506",marginBottom:4}}>⚠️ Returned to you by {prevHist?.by||"previous approver"}</div>
                              {reason&&<div style={{fontSize:12,color:"#3b545a",fontStyle:"italic"}}>"{reason}"</div>}
                              <div style={{fontSize:11,color:"#5a7278",marginTop:4}}>{lastHist.date}</div>
                            </div>
                          );
                        })()}

                        <div style={{display:"flex",gap:8,marginBottom:12}}>
                          {[["forward","→ Forward"],["return","← Return"]].map(x=>(
                            <div key={x[0]} onClick={()=>{setMoveTab(x[0]);setMoveStage("");setMoveNote("");setMovePvRef("");}} style={{flex:1,textAlign:"center",padding:"8px",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,background:moveTab===x[0]?(x[0]==="return"?"#fde8e6":"#e3f5f7"):"#f4f8f9",color:moveTab===x[0]?(x[0]==="return"?"#b42318":"#0b7a87"):"#4f676d",border:"2px solid "+(moveTab===x[0]?(x[0]==="return"?"#f3c6c1":"#0b7a87"):"#e3ebed")}}>{x[1]}</div>
                          ))}
                        </div>
                        {moveTab==="forward"
                          ?<div>
                            <select style={{...S.input,marginBottom:8}} value={moveStage} onChange={e=>setMoveStage(e.target.value)}>
                              <option value="">— Select stage —</option>
                              {nextSt.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                            {isAcct&&(
                              <div style={{background:"#e3f5f7",border:"1px solid #b7e3e8",borderRadius:10,padding:"10px",marginBottom:8}}>
                                <div style={{fontSize:11,fontWeight:600,color:"#085c66",marginBottom:6}}>📄 Payment Voucher Number *</div>
                                <input style={S.input} placeholder="e.g. PV-2024-020" value={movePvRef} onChange={e=>setMovePvRef(e.target.value)}/>
                                <div style={{fontSize:10,color:"#4f676d",marginTop:5}}>A PV will be created. You can also add other invoices from the same supplier in the next step.</div>
                              </div>
                            )}
                            {hasRole("accountant") && !inv.voucherNo && hasUpcomingPVStageDetail && hadPVBeforeDetail && (
                              <div style={{background:"#e9faf1",border:"1px solid #b7ecd2",borderRadius:10,padding:"10px",marginBottom:8}}>
                                <div style={{fontSize:11,fontWeight:600,color:"#1a9a60"}}>📄 This invoice already has a PV</div>
                                <div style={{fontSize:10,color:"#4f676d",marginTop:3}}>You'll be able to pick the existing voucher or start a new one on the next screen.</div>
                              </div>
                            )}
                            <input style={{...S.input,marginBottom:8}} placeholder="Add a comment for the next approver..." value={moveNote} onChange={e=>setMoveNote(e.target.value)}/>
                            <button className="hover-up" style={{...S.btn,width:"100%",opacity:movingInvoice?0.7:1}} onClick={()=>moveInvoice(inv)} disabled={movingInvoice}>{movingInvoice?"⏳ Moving...":"Move Forward"}</button>
                          </div>
                          :<div>
                            <select style={{...S.input,marginBottom:8}} value={moveStage} onChange={e=>setMoveStage(e.target.value)}>
                              <option value="">— Return to —</option>
                              {prevSt.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                            <input style={{...S.input,marginBottom:8}} placeholder="Reason (required)" value={moveNote} onChange={e=>setMoveNote(e.target.value)}/>
                            <button className="hover-up" style={{...S.btnDanger,width:"100%",padding:"10px",fontSize:13,borderRadius:10,opacity:movingInvoice?0.7:1}} onClick={()=>returnInvoice(inv)} disabled={movingInvoice}>{movingInvoice?"⏳ Returning...":"Return"}</button>
                          </div>
                        }
                      </div>
                      :<div style={S.card}>
                        <div style={{fontSize:13,color:"#4f676d",marginBottom:12}}>Currently with <b style={{color:"#12262b"}}>{ROLE_LABELS[sortedStages.find(s=>s.id===inv.stage)?.ownerRole]||"—"}</b>.</div>
                        <AttachmentsPanel inv={inv} token={localStorage.getItem("token")} showToast={showToast} currentUser={currentUser} onAdd={att=>{setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,attachments:[...(i.attachments||[]),att]})); setSelected(s=>({...s,attachments:[...(s.attachments||[]),att]})); try{logAudit("Attachment Added",inv.id+" | "+att.name);}catch(_){}}} onDelete={key=>{setInvoices(p=>p.map(i=>i.id!==inv.id?i:{...i,attachments:(i.attachments||[]).filter(a=>a.key!==key)})); setSelected(s=>({...s,attachments:(s.attachments||[]).filter(a=>a.key!==key)})); try{logAudit("Attachment Removed",inv.id+" | key:"+key);}catch(_){}}}/>
                      </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* VOUCHER DETAIL */}
        {view==="voucherDetail"&&selected&&(()=>{
          const v=vouchers.find(x=>x.no===selected.no)||selected;
          const vInvs=invoices.filter(i=>v.invoiceIds.includes(i.id));
          const active=vInvs.filter(i=>i.stage!=="completed");
          const total=vInvs.reduce((s,i)=>s+i.amount,0);
          const cs=vCurStage(v);
          const vPending = !!v.deletion_status;
          const allowed=cs&&(isAdmin||canAct(cs))&&!vPending;
          const allFiled=active.length===0;
          return (
            <div>
              <button className="hover-up" style={{...S.btnSec,marginBottom:14}} onClick={()=>setView(prevView||"vouchers")}>← Back</button>
              {allFiled
                ?<div style={{position:"relative",...S.card,overflow:"hidden",background:"rgba(32,192,112,0.04)",border:"2px solid rgba(32,192,112,0.3)"}}><Confetti/><div style={{fontSize:16,fontWeight:800,color:"#20c070"}}>🎉 All filed!</div></div>
                :<div style={S.card}><StageStepper currentStage={cs||"received"} stages={sortedStages}/></div>
              }
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={S.card}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:14}}>
                    <div><div style={{fontSize:18,fontWeight:800,color:"#1a9a60"}}>{v.no}</div><div style={{fontSize:12,color:"#4f676d"}}>{v.supplier} · {v.date}</div></div>
                    <div style={{fontWeight:800,fontSize:15}}>AED {total.toLocaleString()}</div>
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr>{["Invoice","Amount","Terms","Stage",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>{vInvs.map(inv=>(
                      <tr key={inv.id} style={{borderBottom:"1px solid #edf2f3"}}>
                        <td style={{...S.td,fontWeight:700,color:"#0b7a87"}}>{inv.id}</td>
                        <td style={S.td}>AED {inv.amount.toLocaleString()}</td>
                        <td style={{...S.td,fontSize:11}}>{fmtTerms(inv)}</td>
                        <td style={S.td}><Badge stage={inv.stage} stages={sortedStages}/></td>
                        <td style={S.td}><button className="hover-up" style={S.btnSm} onClick={()=>openDetail(inv)}>View</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                {vPending&&<div style={{background:"#fdf3e3",border:"2px solid #e8a020",borderRadius:14,padding:"12px 18px",marginBottom:14}}><div style={{fontWeight:700,color:"#7a4506",fontSize:13}}>Deletion requested</div><div style={{fontSize:12,color:"#4f676d",marginTop:2}}>Awaiting CFO approval. This voucher cannot be moved or changed until the request is resolved.</div></div>}
                {!allFiled&&(allowed
                  ?<div style={S.card}>
                    <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>Move All ({active.length})</div>
                    <div style={{display:"flex",gap:8,marginBottom:12}}>
                      {[["forward","→ Forward"],["return","← Return"]].map(x=>(
                        <div key={x[0]} onClick={()=>{setVMoveTab(x[0]);setVMoveStage("");setVMoveNote("");}} style={{flex:1,textAlign:"center",padding:"8px",borderRadius:10,cursor:"pointer",fontSize:11,fontWeight:600,background:vMoveTab===x[0]?(x[0]==="return"?"#fde8e6":"rgba(11,122,135,0.08)"):"#f4f8f9",color:vMoveTab===x[0]?(x[0]==="return"?"#b42318":"#0b7a87"):"#4f676d",border:"2px solid "+(vMoveTab===x[0]?(x[0]==="return"?"#f3c6c1":"#0b7a87"):"#d5e1e4")}}>{x[1]}</div>
                      ))}
                    </div>
                    {vMoveTab==="forward"
                      ?<div>
                        <select style={{...S.input,marginBottom:8}} value={vMoveStage} onChange={e=>setVMoveStage(e.target.value)}><option value="">— Select —</option>{vNext(v).map(s=><option key={s.id} value={s.id}>{s.label}</option>)}</select>
                        <input style={{...S.input,marginBottom:8}} placeholder="Note (optional)" value={vMoveNote} onChange={e=>setVMoveNote(e.target.value)}/>
                        <button className="hover-up" style={{...S.btn,width:"100%"}} onClick={()=>moveVoucher(v)}>Move All</button>
                      </div>
                      :<div>
                        <div style={{background:"#fde8e6",border:"1px solid #f3c6c1",borderRadius:8,padding:"8px 12px",marginBottom:8,fontSize:12,color:"#b42318"}}>⚠️ Sends the whole PV-grouped batch back together. Reason required.</div>
                        <select style={{...S.input,marginBottom:8}} value={vMoveStage} onChange={e=>setVMoveStage(e.target.value)}><option value="">— Return to —</option>{vPrev(v).map(s=><option key={s.id} value={s.id}>{s.label}</option>)}</select>
                        <input style={{...S.input,marginBottom:8}} placeholder="Reason (required)" value={vMoveNote} onChange={e=>setVMoveNote(e.target.value)}/>
                        <button className="hover-up" style={{...S.btnDanger,width:"100%",padding:"10px",fontSize:13,borderRadius:10}} onClick={()=>returnVoucher(v)}>Return Group</button>
                      </div>
                    }
                  </div>
                  :<div style={S.card}><div style={{fontSize:13,color:"#4f676d"}}>With <b style={{color:"#12262b"}}>{ROLE_LABELS[sortedStages.find(s=>s.id===cs)?.ownerRole]||"—"}</b>.</div></div>
                )}
                {!vPending&&(isAdmin||(hasRole("dc")&&allowed))&&<div style={{...S.card,marginTop:12}}>
                  <div style={{fontSize:12,color:"#4f676d",marginBottom:8}}>Delete this voucher entirely (goes to CFO for approval)</div>
                  <button className="hover-up" style={{...S.btnDanger,width:"100%"}} onClick={()=>softDeleteVoucher(v)}>🗑 Delete Voucher</button>
                </div>}
              </div>
            </div>
          );
        })()}

        {/* VOUCHERS LIST */}
        {view==="vouchers"&&(()=>{
          // Only show vouchers that still have at least one live invoice (orphaned vouchers are hidden)
          const liveVouchers = vouchers.map(v=>({v, vI:invoices.filter(i=>v.invoiceIds.includes(i.id))})).filter(x=>x.vI.length>0);
          const activeList = liveVouchers.filter(x=>!x.vI.every(i=>i.stage==="completed"));
          const filedList = liveVouchers.filter(x=>x.vI.every(i=>i.stage==="completed"));
          const renderCard = ({v,vI}) => {
            const tot=vI.reduce((s,i)=>s+i.amount,0); const cs=vCurStage(v); const allFiled=vI.every(i=>i.stage==="completed");
            const anyPriority=vI.some(i=>i.priority); const anyUrgent=vI.some(i=>i.urgent);
            const anyDeletionPending=vI.some(i=>i.deletion_status)||v.deletion_status;
            return <div key={v.no} className="hover-up" style={{...S.card,cursor:"pointer",borderLeft:anyDeletionPending?"3px solid #e8a020":anyPriority?"3px solid #f59e0b":anyUrgent?"3px solid #ef4444":undefined}} onClick={()=>openVoucher(v)}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{fontWeight:800,fontSize:14,color:"#1a9a60"}}>{v.no}</div>
                    {anyPriority&&<span style={{background:"#fdecd6",color:"#8a4b08",border:"0.5px solid #f3c98a",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:600}}>priority</span>}
                    {anyUrgent&&<span style={{background:"#fde8e6",color:"#912018",border:"0.5px solid #f3c6c1",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:600}}>urgent</span>}
                    {anyDeletionPending&&<span style={{background:"#fdf3e3",color:"#7a4506",border:"0.5px solid #f3d9ad",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:700}}>⏳ pending</span>}
                  </div>
                  <div style={{fontSize:12,color:"#4f676d"}}>{v.supplier} · {v.date} · {vI.length} invoice(s)</div>
                </div>
                <div style={{textAlign:"right"}}><div style={{fontWeight:700}}>AED {tot.toLocaleString()}</div>{allFiled?<span style={{background:"#20c07018",color:"#20c070",border:"1px solid #20c07040",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>All Filed</span>:cs?<Badge stage={cs} stages={sortedStages}/>:null}</div>
              </div>
            </div>;
          };
          return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:18}}>
                <div>
                  <div style={{...S.h,fontSize:22}}>Payment Vouchers</div>
                  <div style={{fontSize:13,color:"#4f676d",marginTop:2}}>{activeList.length} active · {filedList.length} filed</div>
                </div>
                {(hasRole("accountant")||isAdmin)&&<button className="hover-up" style={S.btn} onClick={()=>setView("createVoucher")}>+ Create PV</button>}
              </div>
              <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                <button className="hover-up" style={pvMode==="pv"?S.btnSm:S.btnSec} onClick={()=>setPvMode("pv")}>By PV</button>
                <button className="hover-up" style={pvMode==="inv"?S.btnSm:S.btnSec} onClick={()=>setPvMode("inv")}>By invoice</button>
                {pvMode==="inv"&&<>
                  <input style={{...S.input,maxWidth:280,marginLeft:8}} placeholder="Search invoice #, supplier, LPO, PV..." value={pvInvQ} onChange={e=>setPvInvQ(e.target.value)}/>
                  <select style={{...S.input,maxWidth:130}} value={pvInvScope} onChange={e=>setPvInvScope(e.target.value)}><option value="active">Active</option><option value="filed">Filed</option><option value="all">All</option></select>
                </>}
              </div>
              {pvMode==="inv"?(()=>{
                const q=pvInvQ.trim().toLowerCase();
                const rows=liveVouchers.flatMap(x=>x.vI.map(i=>({i,v:x.v})))
                  .filter(r=>pvInvScope==="all"||(pvInvScope==="filed"?r.i.stage==="completed":r.i.stage!=="completed"))
                  .filter(r=>!q||[r.i.invoiceNo||r.i.id,r.i.supplier,r.i.lpo,r.v.no].some(t=>String(t||"").toLowerCase().includes(q)));
                return rows.length===0?<div style={{...S.card,textAlign:"center",padding:30,color:"#5a7278",fontSize:13}}>No invoices match.</div>:(
                  <div style={{...S.card,padding:0,overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead><tr>{["Invoice #","Supplier","LPO","Amount (AED)","PV","Stage"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>{rows.map(({i,v})=><tr key={i.id} className="hover-up" style={{cursor:"pointer"}} onClick={()=>openDetail(i)}>
                        <td style={S.td}>{i.invoiceNo||i.id}{i.priority&&<span style={{marginLeft:6,background:"#fdecd6",color:"#8a4b08",border:"0.5px solid #f3c98a",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:600}}>priority</span>}{i.urgent&&<span style={{marginLeft:6,background:"#fde8e6",color:"#912018",border:"0.5px solid #f3c6c1",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:600}}>urgent</span>}</td>
                        <td style={S.td}>{i.supplier}</td>
                        <td style={S.td}>{i.lpo||"—"}</td>
                        <td style={S.td}>{Number(i.amount).toLocaleString()}</td>
                        <td style={S.td}><span style={{color:"#1a9a60",fontWeight:700}}>{v.no}</span></td>
                        <td style={S.td}><Badge stage={i.stage} stages={sortedStages}/></td>
                      </tr>)}</tbody>
                    </table>
                  </div>);
              })():liveVouchers.length===0?<div style={{...S.card,textAlign:"center",padding:40,color:"#4f676d"}}>No vouchers yet.</div>:(
                <>
                  <div style={{fontSize:11,fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",color:"#5a7278",margin:"6px 0 10px"}}>Active ({activeList.length})</div>
                  {activeList.length===0?<div style={{...S.card,textAlign:"center",padding:20,color:"#5a7278",marginBottom:20,fontSize:13}}>No active vouchers</div>:activeList.map(renderCard)}
                  <div style={{fontSize:11,fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",color:"#5a7278",margin:"20px 0 10px"}}>Filed ({filedList.length})</div>
                  {filedList.length===0?<div style={{...S.card,textAlign:"center",padding:20,color:"#5a7278",fontSize:13}}>No filed vouchers yet</div>:filedList.map(renderCard)}
                </>
              )}
            </div>
          );
        })()}

        {/* CREATE PV */}
        {view==="createVoucher"&&(isAdmin||hasRole("accountant"))&&(
          <div style={{maxWidth:960,margin:"0 auto"}}>
            <button className="hover-up" style={{...S.btnSec,marginBottom:14}} onClick={()=>setView("vouchers")}>← Back</button>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,alignItems:"start"}}>
              <div style={S.card}>
                <div style={{...S.h,fontSize:16,marginBottom:6}}>Create Payment Voucher</div>
                <div style={{fontSize:12,color:"#4f676d",marginBottom:14}}>For batching several invoices - they can be from different suppliers - that are already sitting at Accountant. Most PVs are created automatically when an Accountant moves a single invoice forward — use this only when more than one needs to go together.</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>PV Number *</span><input style={S.input} value={vForm.no} onChange={e=>setVForm(p=>({...p,no:e.target.value}))} placeholder="PV-2024-020"/></div>
                  <div><span style={S.label}>Date</span><input style={S.input} type="date" value={vForm.date} onChange={e=>setVForm(p=>({...p,date:e.target.value}))}/></div>
                </div>
                {pvSuppliers.length>0&&(()=>{
                  const q = pvSearched.trim().toLowerCase();
                  const binInvs = invoices.filter(i=>i.stage==="accountant"&&!i.voucherNo);
                  const results = q ? binInvs.filter(i=>[i.id,i.invoiceNo||"",i.supplier,i.lpo||"",String(i.amount)].some(x=>String(x).toLowerCase().includes(q))) : binInvs;
                  const runSearch = () => setPvSearched(pvQuery);
                  const pick = inv => {
                    setVSelected(p=>p.includes(inv.id)?p.filter(x=>x!==inv.id):[...p,inv.id]);
                  };
                  return (
                    <div style={{marginBottom:14}}>
                      <span style={S.label}>Invoices in your bin (stage: Accountant, no PV yet) - tick any, from any supplier</span>
                      <div style={{display:"flex",gap:8}}>
                        <input style={{...S.input,flex:1}} placeholder="Invoice #, LPO, supplier or amount" value={pvQuery} onChange={e=>setPvQuery(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") runSearch(); }}/>
                        <button className="hover-up" style={{...S.btnSm,padding:"5px 16px"}} onClick={runSearch}>🔍 Search</button>
                        {(pvQuery||pvSearched)&&<button className="hover-up" style={S.btnSec} onClick={()=>{ setPvQuery(""); setPvSearched(""); }}>Clear</button>}
                      </div>
                      {(results.length===0
                        ?<div style={{color:"#4f676d",fontSize:13,marginTop:8}}>No matching invoices in your bin.</div>
                        :<div style={{marginTop:8}}>{results.map(inv=>(
                          <div key={inv.id} onClick={()=>pick(inv)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,marginBottom:6,cursor:"pointer",border:"1px solid "+(vSelected.includes(inv.id)?"#0b7a87":"#d5e1e4"),background:vSelected.includes(inv.id)?"#e3f5f7":"#fff"}}>
                            <div style={{width:18,height:18,borderRadius:5,border:"2px solid "+(vSelected.includes(inv.id)?"#0b7a87":"#ccc"),background:vSelected.includes(inv.id)?"#0b7a87":"transparent",color:"#fff",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center"}}>{vSelected.includes(inv.id)?"✓":""}</div>
                            <span style={{fontWeight:700,color:"#0b7a87",fontSize:12}}>{inv.id}</span>
                            <span style={{flex:1,color:"#4f676d",fontSize:12}}>{inv.supplier}{inv.lpo?" · "+inv.lpo:""}</span>
                            <span style={{fontWeight:700,fontSize:12}}>AED {inv.amount.toLocaleString()}</span>
                          </div>))}</div>)}
                    </div>
                  );
                })()}
                {pvSuppliers.length===0&&<div style={{background:"#f4f8f9",border:"1px solid #e3ebed",borderRadius:10,padding:"14px 16px",marginBottom:12,fontSize:13,color:"#3b545a"}}>No invoices are currently sitting at Accountant without a PV, so there is nothing to batch right now.</div>}
                {vSelected.length>0&&(()=>{
                  const sel=invoices.filter(i=>vSelected.includes(i.id));
                  const sups=[...new Set(sel.map(i=>i.supplier))];
                  return <div style={{background:"#e3f5f7",border:"1px solid #b7dfe4",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:13,color:"#085c66"}}><b>{sel.length}</b> invoice(s) selected · {sups.length} supplier(s): {sups.join(", ")} · <b>AED {sel.reduce((t,i)=>t+i.amount,0).toLocaleString()}</b></div>;
                })()}
                <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1,opacity:pvSuppliers.length===0?0.5:1}} onClick={createPVBulk} disabled={pvSuppliers.length===0}>Create PV</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setView("vouchers")}>Cancel</button></div>
              </div>

              <div style={{...S.card,padding:0,overflow:"hidden"}}>
                <div style={{padding:"16px 20px 10px"}}>
                  <div style={{...S.h,fontSize:16}}>Existing Payment Vouchers</div>
                  <div style={{fontSize:12,color:"#4f676d",marginTop:2}}>{vouchers.length} total</div>
                </div>
                <div style={{maxHeight:480,overflowY:"auto"}}>
                  {[...vouchers].sort((a,b)=>new Date(b.date)-new Date(a.date)).length===0?
                    <div style={{padding:"20px",textAlign:"center",color:"#4f676d",fontSize:13}}>No PVs created yet.</div>
                  :[...vouchers].sort((a,b)=>new Date(b.date)-new Date(a.date)).map(v=>{
                    const vI=invoices.filter(i=>v.invoiceIds.includes(i.id));
                    const tot=vI.reduce((s,i)=>s+i.amount,0);
                    const cs=vCurStage(v);
                    const allFiled=vI.length>0&&vI.every(i=>i.stage==="completed");
                    return (
                      <div key={v.no} onClick={()=>openVoucher(v)} className="hover-up" style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"12px 20px",borderTop:"1px solid #edf2f3",cursor:"pointer"}}>
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontWeight:600,fontSize:13,color:"#1a9a60"}}>{v.no}</span>
                            <span style={{fontSize:12,color:"#4f676d"}}>{v.supplier}</span>
                          </div>
                          <div style={{fontSize:11,color:"#5a7278",marginTop:1}}>{v.date} · {vI.length} invoice(s)</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontWeight:600,fontSize:13}}>AED {tot.toLocaleString()}</div>
                          {allFiled?<span style={{background:"#e9faf120",color:"#1a9a60",border:"1px solid #b7ecd2",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600}}>All Filed</span>:cs?<Badge stage={cs} stages={sortedStages}/>:null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ADD INVOICE */}
        {view==="addInv"&&(
          <div style={{maxWidth:500,margin:"0 auto"}}>
            <div style={S.card}>
              <div style={{fontSize:16,fontWeight:700,marginBottom:14}}>New Invoice</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div><span style={S.label}>Invoice # *</span><input style={S.input} value={invForm.id} onChange={e=>setInvForm(p=>({...p,id:e.target.value}))}/></div>
                <div><span style={S.label}>LPO</span><input style={S.input} value={invForm.lpo} onChange={e=>setInvForm(p=>({...p,lpo:e.target.value}))}/></div>
              </div>
              <div style={{marginBottom:10}}><span style={S.label}>Supplier *</span><SupplierAutocomplete value={invForm.supplier} suppliers={suppliers} onChange={v=>setInvForm(p=>({...p,supplier:v}))} canManage={["admin","dc","accountant"].some(hasRole)} onAddSupplier={addSupplier}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div><span style={S.label}>Invoice Date *</span><input style={S.input} type="date" value={invForm.date} onChange={e=>setInvForm(p=>({...p,date:e.target.value}))}/></div>
                <div><span style={S.label}>Received *</span><input style={S.input} type="date" value={invForm.receivedDate} onChange={e=>setInvForm(p=>({...p,receivedDate:e.target.value}))}/></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div><span style={S.label}>Amount *</span><input style={S.input} type="number" value={invForm.amount} onChange={e=>setInvForm(p=>({...p,amount:e.target.value}))}/></div>
                <div><span style={S.label}>Project *</span><select style={{...S.input,border:!invForm.projectId?"1px solid #f3c6c1":S.input.border}} value={invForm.projectId} onChange={e=>setInvForm(p=>({...p,projectId:e.target.value}))}><option value="">— Select Project —</option>{projects.filter(p=>p.active).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              </div>
              <div style={{marginBottom:10}}>
                <span style={S.label}>Attachment (PDF / Image)</span>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <label style={{flex:1,padding:"10px 14px",background:"#f5fafa",border:"1px dashed rgba(11,122,135,0.4)",borderRadius:8,cursor:"pointer",fontSize:12,color:invFile?"#0b7a87":"#5a7278",display:"flex",alignItems:"center",gap:8}}>
                    <span>📎</span>
                    <span>{invFile?invFile.name:"Click to attach invoice scan (optional)"}</span>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:"none"}} onChange={e=>setInvFile(e.target.files?.[0]||null)}/>
                  </label>
                  {invFile&&<button style={{...S.btnSec,padding:"4px 10px",fontSize:11}} onClick={()=>setInvFile(null)}>✕</button>}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              </div>
              <PaymentFields form={invForm} setForm={setInvForm}/>
              <div style={{display:"flex",gap:10}}><button className="hover-up" style={{...S.btn,flex:1}} onClick={addInvoice}>Add Invoice</button><button className="hover-up" style={{...S.btnSec,flex:1}} onClick={()=>setView("dashboard")}>Cancel</button></div>
            </div>
          </div>
        )}

        {/* PENDING APPROVAL (PD) - list */}
        {view==="intake"&&(()=>{
          const pending=pendingSubs.filter(s=>s.status==="pending");
          return (
            <div>
              <div style={{marginBottom:16}}>
                <div style={{...S.h,fontSize:22}}>Pending Approval</div>
                <div style={{fontSize:13,color:"#4f676d",marginTop:2}}>{pending.length} invoice{pending.length===1?"":"s"} waiting for PD approval. Open one to review the document and approve or reject.</div>
              </div>
              {pending.length===0?<div style={{...S.card,textAlign:"center",padding:40,color:"#4f676d"}}>Nothing waiting for approval.</div>
              :pending.map(s=>{
                const proj=projects.find(p=>p.id===s.projectId);
                return (
                  <div key={s.id} className="hover-up" onClick={()=>openPdReview(s)} style={{...S.card,cursor:"pointer",display:"flex",alignItems:"center",gap:14}}>
                    <SubFileThumb sub={s} token={localStorage.getItem("token")}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,color:"#0b7a87"}}>{s.invoiceNo||s.displayId||s.id}{s.lpo?<span style={{fontWeight:500,color:"#4f676d",fontSize:12}}> · LPO {s.lpo}</span>:null}</div>
                      <div style={{fontSize:13,color:"#12262b",fontWeight:600,marginTop:2}}>{s.supplierGuess}{s.amountGuess!=null?" · AED "+Number(s.amountGuess).toLocaleString():""}</div>
                      <div style={{fontSize:12,color:"#4f676d",marginTop:2,display:"flex",alignItems:"center",gap:6}}><ProjBadge proj={proj}/> · {s.submittedBy} · {s.submittedDate}</div>
                    </div>
                    <button className="hover-up" style={S.btnSm} onClick={e=>{e.stopPropagation();openPdReview(s);}}>Review</button>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* PD REVIEW SCREEN */}
        {view==="processSubmission"&&selectedSub&&(()=>{
          const sub=selectedSub;
          const proj=projects.find(p=>p.id===sub.projectId);
          const busy=pdBusy===sub.id;
          const row=(k,v)=><div style={{display:"flex",justifyContent:"space-between",gap:12,padding:"7px 0",borderBottom:"1px solid #f1f5f6",fontSize:13}}><span style={{color:"#4f676d"}}>{k}</span><span style={{fontWeight:600,color:"#12262b",textAlign:"right"}}>{v||"—"}</span></div>;
          return (
            <div style={{maxWidth:1200,margin:"0 auto"}}>
              <button className="hover-up" style={{...S.btnSec,marginBottom:14}} onClick={()=>{setSelectedSub(null);setView("intake");}}>← Back to Pending Approval</button>
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,1.6fr) minmax(300px,1fr)",gap:16,alignItems:"start"}}>
                <div style={S.card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div style={{fontSize:14,fontWeight:700}}>📄 {sub.fileName}</div>
                    <span style={{fontSize:12,color:"#0b7a87",fontWeight:600,cursor:"pointer"}} onClick={async()=>{
                      const token=localStorage.getItem("token");
                      const res=await fetch("/api/file-url?key="+encodeURIComponent(sub.fileKey),{headers:{"Authorization":"Bearer "+token}});
                      const data=await res.json().catch(()=>({}));
                      if(data.url) window.open(data.url,"_blank");
                    }}>Open in new tab ↗</span>
                  </div>
                  <SubFilePreview sub={sub} token={localStorage.getItem("token")}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={S.card}>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Invoice details</div>
                    {row("Invoice No.",sub.invoiceNo)}
                    {row("LPO No.",sub.lpo)}
                    {row("Supplier",sub.supplierGuess)}
                    {!suppliers.some(x=>normName(x.name)===normName(sub.supplierGuess))&&<div style={{background:"#fdf3e3",border:"0.5px solid #f3d9ad",borderRadius:8,padding:"8px 12px",margin:"6px 0",fontSize:12,color:"#7a4506"}}>⚠ This supplier is not on the supplier list. You can still approve - the Document Controller will verify the name.{suggestSuppliers(sub.supplierGuess,suppliers,2).length>0&&<div style={{marginTop:4}}>Similar: {suggestSuppliers(sub.supplierGuess,suppliers,2).map(x=>x.s.name).join(", ")}</div>}</div>}
                    {row("Amount",sub.amountGuess!=null?"AED "+Number(sub.amountGuess).toLocaleString():"")}
                    {row("Project",proj?proj.name:sub.site)}
                    {row("Submitted by",sub.submittedBy)}
                    {row("Submitted on",sub.submittedDate)}
                    {sub.note&&<div style={{fontSize:12,color:"#3b545a",marginTop:10,fontStyle:"italic",background:"#f4f8f9",padding:"8px 12px",borderRadius:8}}>{sub.note}</div>}
                  </div>
                  <div style={S.card}>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:10}}>Decision</div>
                    <div style={{display:"flex",gap:18,marginBottom:10,flexWrap:"wrap"}}><label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer"}}><input type="checkbox" checked={pdPrio} onChange={e=>setPdPrio(e.target.checked)}/> Mark as priority</label><label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer"}}><input type="checkbox" checked={pdUrgent} onChange={e=>setPdUrgent(e.target.checked)}/> Mark as urgent</label></div>
                    <button className="hover-up" style={{...S.btn,width:"100%",opacity:busy?0.7:1}} disabled={!!pdBusy} onClick={()=>pdApproveSub(sub)}>{busy?"⏳ Working...":"✓ Approve and release to DC"}</button>
                    <div style={{borderTop:"1px solid #edf2f3",margin:"14px 0 12px"}}/>
                    <textarea style={{...S.input,minHeight:70,marginBottom:8}} placeholder="Reason for rejection (required)" value={pdNotes[sub.id]||""} onChange={e=>setPdNotes(p=>({...p,[sub.id]:e.target.value}))}/>
                    <button className="hover-up" style={{...S.btnDanger,width:"100%",padding:"10px",fontSize:13,borderRadius:10}} disabled={!!pdBusy} onClick={()=>pdRejectSub(sub)}>Reject - send back to engineer</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* SUBMIT INVOICE (engineer) */}
        {view==="submitInvoice"&&(()=>{
          const engProjs=projects.filter(p=>p.active&&currentUser.projects&&currentUser.projects.includes(p.id));
          return (
            <div style={{maxWidth:500,margin:"0 auto"}}>
              <div style={S.card}>
                <div style={{fontSize:16,fontWeight:700,marginBottom:14}}>Submit Invoice</div>
                <div style={{marginBottom:12}}>
                  <span style={S.label}>Project *</span>
                  {engProjs.length===0
                    ?<div style={{background:"#fde8e6",border:"1px solid #f3c6c1",borderRadius:10,padding:"10px 14px",fontSize:13,color:"#b42318"}}>No projects assigned. Contact admin.</div>
                    :<div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                      {engProjs.map(p=><div key={p.id} onClick={()=>setSubmitForm(x=>({...x,projectId:p.id,site:p.name}))} style={{padding:"8px 16px",borderRadius:20,border:"2px solid "+(submitForm.projectId===p.id?p.color:"#d5e1e4"),background:submitForm.projectId===p.id?p.color+"18":"#f4f8f9",cursor:"pointer",fontSize:13,fontWeight:600,color:submitForm.projectId===p.id?p.color:"#4f676d"}}>{p.name}</div>)}
                    </div>
                  }
                </div>
                <div style={{marginBottom:12}}><span style={S.label}>Invoice File *</span><input style={S.input} type="file" accept="image/*,application/pdf" onChange={handleFileChange}/>{submitForm.file&&<div style={{fontSize:11,color:"#085c66",marginTop:4}}>{submitForm.file.name}</div>}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Invoice No. *</span><input style={S.input} value={submitForm.invoiceNo} onChange={e=>setSubmitForm(p=>({...p,invoiceNo:e.target.value}))}/></div>
                  <div><span style={S.label}>LPO No. *</span><input style={S.input} value={submitForm.lpo} onChange={e=>setSubmitForm(p=>({...p,lpo:e.target.value}))}/></div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={S.label}>Supplier *</span><SupplierAutocomplete value={submitForm.supplierGuess} suppliers={suppliers} onChange={v=>setSubmitForm(p=>({...p,supplierGuess:v}))} canManage={false} onAddSupplier={addSupplier}/><div style={{fontSize:11,color:"#5a7278",marginTop:4}}>Pick from the list. If the supplier is new, type the full name - the Document Controller will verify it.</div></div>
                  <div><span style={S.label}>Amount (AED) *</span><input style={S.input} type="number" value={submitForm.amountGuess} onChange={e=>setSubmitForm(p=>({...p,amountGuess:e.target.value}))}/></div>
                </div>
                <div style={{marginBottom:12}}><span style={S.label}>Note</span><textarea style={{...S.input,minHeight:60}} value={submitForm.note} onChange={e=>setSubmitForm(p=>({...p,note:e.target.value}))}/></div>
                <button className="hover-up" style={{...S.btn,width:"100%",opacity:submitting?0.7:1}} onClick={submitRequest} disabled={submitting}>{submitting?"⏳ Uploading...":"Submit for PD approval"}</button>
              </div>
            </div>
          );
        })()}

        {/* MY SUBMISSIONS */}
        {view==="mySubmissions"&&(()=>{
          const mine=pendingSubs.filter(s=>s.submittedByUsername?s.submittedByUsername===currentUser.username:s.submittedBy===currentUser.name).sort((a,b)=>{
            // Sort by ID timestamp for same-day submissions
            const aTime = parseInt(a.id.replace('SUB-',''))||0;
            const bTime = parseInt(b.id.replace('SUB-',''))||0;
            return bTime-aTime;
          });
          const engProjs=projects.filter(p=>currentUser.projects&&currentUser.projects.includes(p.id));
          return (
            <div>
              <div style={{fontSize:18,fontWeight:700,marginBottom:14}}>My Projects & Submissions</div>
              {engProjs.length>0&&<div style={{...S.card,marginBottom:14}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:10,color:"#0b7a87"}}>Filter by Project</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  <div onClick={()=>setEngProjFilter("all")} style={{padding:"6px 14px",borderRadius:20,border:"2px solid "+(engProjFilter==="all"?"#0b7a87":"#d5e1e4"),background:engProjFilter==="all"?"rgba(11,122,135,0.08)":"#f4f8f9",cursor:"pointer",fontSize:12,fontWeight:600,color:engProjFilter==="all"?"#0b7a87":"#4f676d"}}>All</div>
                  {engProjs.map(p=><div key={p.id} onClick={()=>setEngProjFilter(p.id)} style={{padding:"6px 14px",borderRadius:20,border:"2px solid "+(engProjFilter===p.id?p.color:"#d5e1e4"),background:engProjFilter===p.id?p.color+"18":"#f4f8f9",cursor:"pointer",fontSize:12,fontWeight:600,color:engProjFilter===p.id?p.color:"#4f676d"}}>{p.name}</div>)}
                </div>
              </div>}
              <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>My Submissions</div>
              {mine.filter(s=>engProjFilter==="all"||s.projectId===engProjFilter).length>0&&<div style={{...S.card,padding:0,overflow:"hidden",marginBottom:14}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Invoice No","LPO","Supplier","Amount","Project","Document","Status"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{mine.filter(s=>engProjFilter==="all"||s.projectId===engProjFilter).map((s,i)=>{
                    const proj=projects.find(p=>p.id===s.projectId);
                    return <tr key={s.id} className="inv-row" style={{borderTop:"1px solid #edf2f3",background:i%2?"#fafcfc":"#fff"}}>
                      <td style={{...S.td,fontWeight:700,color:"#0b7a87"}}>{s.invoiceNo||s.displayId||s.id}</td>
                      <td style={S.td}>{s.lpo||"—"}</td>
                      <td style={S.td}>{s.supplierGuess||"—"}</td>
                      <td style={{...S.td,fontWeight:600}}>{s.amountGuess!=null?"AED "+Number(s.amountGuess).toLocaleString():"—"}</td>
                      <td style={S.td}><ProjBadge proj={proj}/></td>
                      <td style={S.td}><DocPreviewLink fileKey={s.fileKey} name={s.fileName} type={s.fileType} token={localStorage.getItem("token")}/></td>
                      <td style={S.td}>{engStatusBadge(s)}</td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>}
              {mine.filter(s=>engProjFilter==="all"||s.projectId===engProjFilter).length===0
                ?<div style={{...S.card,textAlign:"center",padding:32,color:"#4f676d"}}>No submissions.</div>
                :mine.filter(s=>engProjFilter==="all"||s.projectId===engProjFilter).map(s=>{
                  const proj=projects.find(p=>p.id===s.projectId);
                  return <div key={s.id} style={S.card}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:12}}>
                      <div>
                        <div style={{fontWeight:700,color:"#0b7a87"}}>{s.invoiceNo||s.displayId||s.id}{s.lpo?<span style={{fontWeight:500,color:"#4f676d",fontSize:12}}> · LPO {s.lpo}</span>:null}</div>
                        <div style={{fontSize:12,color:"#4f676d",marginTop:2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}><ProjBadge proj={proj}/> · {s.submittedDate} · <DocPreviewLink fileKey={s.fileKey} name={s.fileName} type={s.fileType} token={localStorage.getItem("token")}/></div>
                        {s.note&&<div style={{fontSize:12,color:"#3b545a",marginTop:4,fontStyle:"italic"}}>{s.note}</div>}
                      </div>
                      <div style={{flexShrink:0}}>
                        {engStatusBadge(s)}
                      </div>
                    </div>
                    {s.status==="rejected"&&s.rejectReason&&<div style={{background:"#fde8e6",border:"1px solid #f3c6c1",borderRadius:8,padding:"8px 12px",marginTop:8,fontSize:12,color:"#b42318"}}>{s.rejectReason}</div>}
                  </div>;
                })
              }
            </div>
          );
        })()}

        {/* REPORTS */}
        {view==="reports"&&(
          <div>
            <div style={{marginBottom:16}}>
              <div style={{...S.h,fontSize:22}}>Reports</div>
              <div style={{fontSize:13,color:"#4f676d",marginTop:2}}>A snapshot of where everything stands</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginBottom:16}}>
              {[["Total",invoices.length,"#0b7a87"],["Active",invoices.filter(i=>i.stage!=="completed").length,"#e8a020"],["Filed",archivedInvs.length,"#20c070"],["Urgent",urgentCount,"#ff4444"],["In PVs",invoices.filter(i=>isLockedInPV(i,vouchers)).length,"#30d98a"]].map(x=>(
                <div key={x[0]} style={{...S.card,marginBottom:0,textAlign:"center",padding:14,borderRadius:12}}><div style={{...S.h,fontSize:24,color:x[2]}}>{x[1]}</div><div style={{fontSize:11,color:"#4f676d",marginTop:4}}>{x[0]}</div></div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={S.card}>
                <div style={{...S.h,fontSize:15,marginBottom:12}}>By Stage</div>
                {sortedStages.map(s=>{ const cnt=stageCounts[s.id]||0; const pct=freeInvoices.length?Math.round(cnt/freeInvoices.length*100):0; return <div key={s.id} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:12,color:s.color,fontWeight:600}}>{s.label}</span><span style={{fontSize:12,fontWeight:700,color:s.color}}>{cnt}</span></div>
                  <div style={{background:"#edf2f3",borderRadius:4,height:6}}><div style={{background:s.color,borderRadius:4,height:6,width:pct+"%",transition:"width 0.5s"}}/></div>
                </div>; })}
              </div>
              <div style={S.card}>
                <div style={{...S.h,fontSize:15,marginBottom:12}}>By Project</div>
                {projects.map(p=>{ const cnt=invoices.filter(i=>i.projectId===p.id).length; const filed=invoices.filter(i=>i.projectId===p.id&&i.stage==="completed").length; const pct=cnt?Math.round(filed/cnt*100):0; return <div key={p.id} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:12,color:p.color,fontWeight:600}}>{p.name}</span><span style={{fontSize:12,color:"#4f676d"}}>{filed}/{cnt} filed</span></div>
                  <div style={{background:"#edf2f3",borderRadius:4,height:6}}><div style={{background:p.color,borderRadius:4,height:6,width:pct+"%",transition:"width 0.5s"}}/></div>
                </div>; })}
              </div>
            </div>
          </div>
        )}

        {/* ARCHIVE */}
        {view==="archive"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:16}}>
              <div>
                <div style={{...S.h,fontSize:22}}>Filed</div>
                <div style={{fontSize:13,color:"#4f676d",marginTop:2}}>{archQ.trim()?archShown.length+" of "+archivedInvs.length:archivedInvs.length} invoice{archivedInvs.length===1?"":"s"} filed</div>
              </div>
              <button className="hover-up" style={S.btnSec} onClick={()=>setView("dashboard")}>← Back</button>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input style={{...S.input,flex:1,maxWidth:420}} placeholder="Search invoice #, supplier, LPO, PV, file no, project, amount..." value={archQ} onChange={e=>setArchQ(e.target.value)}/>
              {archQ&&<button className="hover-up" style={S.btnSec} onClick={()=>setArchQ("")}>Clear</button>}
            </div>
            <div style={{...S.card,padding:0,overflow:"hidden"}}>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
                  <thead><tr>{["Invoice #","Supplier","Project","Amount","PV Ref","File No","Filed",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {archShown.length===0&&<tr><td colSpan={8} style={{padding:30,textAlign:"center",color:"#4f676d"}}>{archQ.trim()?"No filed invoices match your search.":"No filed invoices."}</td></tr>}
                    {archShown.map((inv,i)=>{ const proj=projects.find(p=>p.id===inv.projectId); return (
                      <tr key={inv.id} className="inv-row" style={{borderTop:"1px solid #edf2f3",background:i%2?"#fafcfc":"#fff"}}>
                        <td style={{...S.td,fontWeight:700,color:"#20c070"}}>{inv.id}</td>
                        <td style={S.td}>{inv.supplier}</td>
                        <td style={S.td}><ProjBadge proj={proj}/></td>
                        <td style={{...S.td,fontWeight:600}}>AED {inv.amount.toLocaleString()}</td>
                        <td style={{...S.td,fontSize:12,color:"#1a9a60"}}>{inv.pvRef||"—"}</td>
                        <td style={{...S.td,fontWeight:600,color:"#20c070"}}>{inv.fileNumber||"—"}</td>
                        <td style={{...S.td,fontSize:12,color:"#4f676d"}}>{(inv.history.find(h=>h.stage==="completed")||{}).date||"—"}</td>
                        <td style={S.td}><button className="hover-up" style={S.btnSm} onClick={()=>openDetail(inv)}>View</button></td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
        </div>
        </div>
    </div>
  );
}
